// src/App.jsx - COMPLETE REVISED VERSION WITH PERFORMANCE & ACCESSIBILITY FIXES


import { supabase } from './lib/supabase'; 

import { useState, useEffect, useCallback, useMemo, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useNavigate, useLocation, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

// Optimized individual icon imports instead of bulk import
import CalendarDaysIcon from '@heroicons/react/24/outline/CalendarDaysIcon';
import UsersIcon from '@heroicons/react/24/outline/UsersIcon';
import StarIcon from '@heroicons/react/24/outline/StarIcon';
import CameraIcon from '@heroicons/react/24/outline/CameraIcon';
import ChatBubbleLeftRightIcon from '@heroicons/react/24/outline/ChatBubbleLeftRightIcon';
import TrophyIcon from '@heroicons/react/24/outline/TrophyIcon';
import ClockIcon from '@heroicons/react/24/outline/ClockIcon';
import ArrowRightIcon from '@heroicons/react/24/outline/ArrowRightIcon';
import CheckCircleIcon from '@heroicons/react/24/outline/CheckCircleIcon';
import BoltIcon from '@heroicons/react/24/outline/BoltIcon';
import ShareIcon from '@heroicons/react/24/outline/ShareIcon';
import BookmarkIcon from '@heroicons/react/24/outline/BookmarkIcon';
import HeartIcon from '@heroicons/react/24/outline/HeartIcon';
import EyeIcon from '@heroicons/react/24/outline/EyeIcon';
import MapPinIcon from '@heroicons/react/24/outline/MapPinIcon';
import FireIcon from '@heroicons/react/24/outline/FireIcon';
import XMarkIcon from '@heroicons/react/24/outline/XMarkIcon';
import SparklesIcon from '@heroicons/react/24/outline/SparklesIcon';

// 11-25-25 
import NotificationPrompt from './components/NotificationPrompt';
import pushNotificationService from './services/pushNotificationService';
import DailyPromptModal from './components/DailyPromptModal';
//

import ProtectedAdminRoute from './components/ProtectedAdminRoute';

// Import image utilities for Google Cloud Storage

import { getImageUrl, getEventImageUrl } from './utils/images';

// 1-29-26
import PayPerEventSuccess from './components/PayPerEventSuccess';



// 1-11-26
function preloadCriticalImage(imagePath, context = 'hero') {
  if (typeof window === 'undefined') return;
  
  const link = document.createElement('link');
  link.rel = 'preload';
  link.as = 'image';
  link.href = getImageUrl(imagePath, { context, responsive: true });
  
  const srcset = [
    { context: 'medium', width: '600w' },
    { context: 'large', width: '1200w' },
    { context: 'hero', width: '1920w' }
  ].map(({ context, width }) => 
    `${getImageUrl(imagePath, { context, responsive: true })} ${width}`
  ).join(', ');
  
  link.imageSrcset = srcset;
  link.imageSizes = '100vw';
  link.fetchPriority = 'high';
  document.head.appendChild(link);
}

import OptimizedImage from './components/OptimizedImage';

// end of 1-11-26


import { HeartIcon as HeartSolid, BookmarkIcon as BookmarkSolid, StarIcon as StarSolid } from '@heroicons/react/24/solid';
import { Tab } from '@headlessui/react';

// Core Components (keep immediate loading)
import Header from './components/Header';
import Footer from './components/Footer';
import EmailSignup from './components/EmailSignup';
//import SEO, { seoConfigs, defaultStructuredData, injectStructuredData } from './components/SEO';
import SEO, { seoConfigs, defaultStructuredData, injectStructuredData, whyConnectCoStructuredData, whyConnectCoFAQSchema } from './components/SEO';
import ScrollToTop from './components/ScrollToTop';
import ErrorBoundary from './components/ErrorBoundary';
import { Navigate } from 'react-router-dom';

// Legal Pages (immediate loading - small components)
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfService from './components/TermsOfService';
import CookiesPolicy from './components/CookiesPolicy';
import ContactForm from './components/ContactForm';

import { monthlyFikaContentPieces } from './data/monthlyFikaContent';

import NotFound from './components/NotFound';

import CuratedFikaAdmin from './components/CuratedFikaAdmin';
import DeepFikaAdmin from './components/DeepFikaAdmin';
import DeepFikaRespond from './components/DeepFikaRespond';
import DeepFikaFeedback from './components/DeepFikaFeedback';

import CuratedFikaCancellation from './components/CuratedFikaCancellation';

import MySessions from './components/MySessions';

import { coffeeGuideSEOConfigs, getCoffeeGuideSEO } from './components/SEO';
import CuratedFikaEntrepreneursAdmin from './components/CuratedFikaEntrepreneursAdmin';
import CuratedFikaEntrepreneursConfirm from './components/CuratedFikaEntrepreneursConfirm';


// Lazy load heavy components for code splitting
const TokyoExpatCalculator = lazy(() => import('./components/TokyoExpatCalculator'));


const BlogPage = lazy(() => import('./components/BlogPage'));
const BlogPost = lazy(() => import('./components/BlogPost'));
const BlogArticleRouter = lazy(() => import('./components/blog/BlogArticleRouter'));
const EventsPage = lazy(() => import('./components/events/EventsPage'));
const EventDetailsPage = lazy(() => import('./components/events/EventDetailsPage'));
const WellnessIntegration = lazy(() => import('./components/wellness/WellnessIntegration'));
const ConversationStartersPage = lazy(() => import('./components/ConversationStartersPage'));
const FounderConversationsPage = lazy(() => import('./components/FounderConversationsPage'));
const ExperienceMarketplacePage = lazy(() => import('./components/ExperienceMarketplacePage'));
const HostApplicationForm = lazy(() => import('./components/HostApplicationForm'));
const NewsletterAdmin = lazy(() => import('./components/NewsletterAdmin'));
const MonthlyFikaPage = lazy(() => import('./components/MonthlyFikaPage'));
const SignatureFikaPage = lazy(() => import('./components/SignatureFikaPage'));
const WhatIsFikaPage = lazy(() => import('./components/WhatIsFikaPage'));
const WhatIsSpeedFikaPage = lazy(() => import('./components/WhatIsSpeedFikaPage'));
const FikaAt33Page = lazy(() => import('./components/FikaAt33Page'));

const MerchandisePage = lazy(() => import('./components/MerchandisePage'));
const QRCheckIn = lazy(() => import('./components/QRCheckIn'));

const EventCancellationPage = lazy(() => import('./components/EventCancellationPage'));

const TokyoExpatGuide = lazy(() => import('./components/TokyoExpatGuide'));
const TokyoSurvivalGuide = lazy(() => import('./components/TokyoSurvivalGuide'));

const GoalAccountabilityForm = lazy(() => import('./components/GoalAccountabilityForm'));
const GoalAccountabilityAdmin = lazy(() => import('./components/GoalAccountabilityAdmin'));
const GoalAccountabilityFAQ = lazy(() => import('./components/GoalAccountabilityFAQ'));


const MemberDashboard = lazy(() => import('./components/member/MemberDashboard'));
const MemberLogin = lazy(() => import('./components/member/MemberLogin'));
const AboutPage = lazy(() => import('./components/AboutPage'));
const CommunityGuidelines = lazy(() => import('./components/CommunityGuidelines'));

const SubscriptionPage = lazy(() => import('./components/SubscriptionPage'));
const SubscriptionSuccess = lazy(() => import('./components/SubscriptionSuccess').then(module => ({ default: module.SubscriptionSuccess })));
const SubscriptionCancel = lazy(() => import('./components/SubscriptionCancel').then(module => ({ default: module.SubscriptionCancel })));

const SubscriptionPageAd = lazy(() => import('./components/SubscriptionPageAd'));

const MemberPassportPage = lazy(() => import('./components/MemberPassportPage'));
const UnsubscribePage = lazy(() => import('./components/UnsubscribePage'));

const CuratedFikaSignup = lazy(() => import('./components/CuratedFikaSignup'));
const CuratedFikaConfirm = lazy(() => import('./components/CuratedFikaConfirm'));

const CuratedFikaPage = lazy(() => import('./components/CuratedFikaPage'));
const CuratedFikaFeedback = lazy(() => import('./components/CuratedFikaFeedback'));

const EmailBlockingManagement = lazy(() => import('./components/EmailBlockingManagement'));

const HappinessLandingPage = lazy(() => import('./components/HappinessLandingPage'));

const NomadConcierge = lazy(() => import('./components/NomadConcierge'));

const NomadNotesPage = lazy(() => import('./components/NomadNotesPage'));

const CuratedFikaMemberPortal = lazy(() => import('./components/CuratedFikaMemberPortal'));

const SubmitDiscovery = lazy(() => import('./components/SubmitDiscovery'));

const TokyoCafesPage = lazy(() => import('./components/TokyoCafesPage'));

const PositioningPage = lazy(() => import('./components/PositioningPage'));

const PartnerPage = lazy(() => import('./components/PartnerPage'));

// Founders & Startups (1-on-1) page
const CuratedFikaEntrepreneursPage = lazy(() =>
  import('./components/CuratedFikaEntrepreneursPage') 
);

const CuratedFikaEntrepreneursSignup = lazy(() => import('./components/CuratedFikaEntrepreneursSignup'));
const CuratedFikaEntrepreneursFeedback = lazy(() => import('./components/CuratedFikaEntrepreneursFeedback'));


const FoundersFikaPage = lazy(() => import('./components/FoundersFikaPage'));


const ResponseModerationDashboard = lazy(() => import('./components/admin/ResponseModerationDashboard'));

const CommunityPhotoGallery = lazy(() => import('./components/CommunityPhotoGallery'));
const PhotosPage = lazy(() => import('./components/PhotosPage'));

const EventHostGuide = lazy(() => import('./components/EventHostGuide'));

const TeamFikaPage = lazy(() => import('./components/TeamFikaPage'));

const WhyWereChargingPage = lazy(() => import('./components/WhyWereChargingPage'));

const JoinInstagramPage = lazy(() => import('./components/JoinInstagramPage'));

// 3-7-26
const FikaPracticeOnboarding = lazy(() => import('./pages/fika-practice/FikaPracticeOnboarding'));
const FikaPracticeToday = lazy(() => import('./pages/fika-practice/FikaPracticeToday'));
const FikaPracticeProgress = lazy(() => import('./pages/fika-practice/FikaPracticeProgress'));
const FikaPracticeCourses = lazy(() => import('./pages/fika-practice/FikaPracticeCourses'));

// 3-10-26
const MonthlyFikaPageAd = lazy(() => import('./components/MonthlyFikaPageAd'));

// 4-11-26
const KindMomentsPage = lazy(() => import('./components/KindMomentsPage'));

// 5-1-26
const FikaBoard     = lazy(() => import('./community/FikaBoard'));

// 5-2-26 — Google Ads landing page
const MeetPeopleTokyoPage = lazy(() => import('./components/MeetPeopleTokyoPage'));
const FikaBoardPost = lazy(() => import('./community/FikaBoardPost'));

// 5-27-26 — Bangkok event landing page (static import — module already loaded for SEO config)
import BangkokPage, { bangkokSeoConfig } from './components/BangkokPage';
const BangkokSuccess = lazy(() => import('./components/BangkokSuccess'));

// 5-29-26 — Deep Fika (1-on-1 matched fika) (static — module already loaded for SEO configs)
import DeepFikaPage, { deepFikaSeoConfig } from './components/DeepFikaPage';
import DeepFikaSignup, { deepFikaSignupSeoConfig } from './components/DeepFikaSignup';

import { getCompatibleEvents } from './utils/eventsCompatibility';

// Real polling service
import pollService from './services/pollService';

// Analytics imports
import { initGA, trackEvent, trackNavigation } from './utils/analytics';
import { useAnalytics } from './hooks/useAnalytics';

import { 
  communityGuidelinesSEO,
  communityGuidelinesStructuredData,
  communityGuidelinesFAQSchema,
  communityGuidelinesPolicySchema,
  communityGuidelinesHowToSchema,
  communityGuidelinesOrganizationSchema,
  communityGuidelinesBreadcrumbSchema
} from './components/SEO';



// Loading component for Suspense fallbacks
const LoadingSpinner = () => (
  <div className="flex items-center justify-center min-h-screen bg-gray-50">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
      <p className="text-gray-600">Loading...</p>
    </div>
  </div>
);

// Wellness SEO
const wellnessSEO = {
  title: "Community Wellness Hub - Mental Health Support Through Authentic Friendships | Connect & Co",
  description: "Supporting your mental health journey through authentic friendships and genuine community. Authentic friendships create happiness and wellbeing. Get pre-event mood check-ins, social anxiety resources, mindful friendship-building tips, and peer support for building real connections in our international community. Wellness through genuine friendship.",
  keywords: "mental health friendships, authentic friendships wellbeing, genuine friendships mental health, social anxiety support, mindful friendship building, community wellness, authentic connection mental health, friendship mental health, pre-event anxiety support, international community mental health, wellness through friendships, peer support network, authentic friendships happiness, genuine connections wellbeing, friendship mental health benefits, social wellbeing, authentic community support, mental health through connection, friendship wellness, wellbeing through authentic friendships",
  url: "https://connectco.app/wellness",
  canonical: "https://connectco.app/wellness",
  openGraph: {
    title: "Community Wellness Hub - Mental Health Support Through Authentic Friendships",
    description: "Supporting your mental health journey through authentic friendships. Genuine connections create happiness and wellbeing. Get social anxiety resources, mindful friendship-building tips, and authentic peer support.",
    url: "https://connectco.app/wellness",
    image: "https://connectco.app/wellness-og.jpg",
    type: "website"
  },
  twitter: {
    card: "summary_large_image",
    title: "Community Wellness Hub - Mental Health Through Authentic Friendships",
    description: "Authentic friendships create happiness. Social anxiety resources, mindful friendship-building tips, and genuine peer support for real connections.",
    image: "https://connectco.app/wellness-og.jpg"
  }
};

// Conversation SEO
const conversationStartersSEO = {
  title: "Authentic Conversation Starters Where authentic friendships happen naturally | Meaningful Friendship Questions | Connect & Co",
  description: "Thoughtful conversation starters designed to build authentic friendships through meaningful dialogue. Skip small talk and forge genuine connections with cultural exchange questions, deep friendship-building prompts, and authentic conversation guides for building real friendships in international community and Swedish Fika gatherings. Transform conversations into lasting friendships.",
  keywords: "authentic conversation starters, building friendships questions, friendship building questions, meaningful friendship questions, genuine connection prompts, deep friendship questions, authentic dialogue starters, real friendship conversations, fika friendship questions, cultural exchange questions, meaningful friendships, authentic conversations, genuine friendship prompts, international friendship questions, friendship depth questions, authentic human connection, skip small talk questions, deep connection conversations, friendship conversation guide, how to build friendships, friendship starting questions, authentic relationship questions, genuine friendship building",
  url: "https://connectco.app/conversation-starters",
  canonical: "https://connectco.app/conversation-starters",
  openGraph: {
    title: "Authentic Conversation Starters for Building Real Friendships",
    description: "Thoughtful conversation starters for building authentic friendships. Skip small talk and forge genuine connections through meaningful dialogue and cultural exchange questions.",
    url: "https://connectco.app/conversation-starters",
    image: "https://connectco.app/conversation-starters-og.jpg",
    type: "website"
  },
  twitter: {
    card: "summary_large_image",
    title: "Authentic Conversation Starters for Building Real Friendships",
    description: "Skip small talk. Build authentic friendships through thoughtful conversation starters designed for genuine connection.",
    image: "https://connectco.app/conversation-starters-og.jpg"
  }
};

// NEW: Signature Fika SEO Configuration

const signatureWeeklyFikaSEO = {
  title: "Weekly Signature Fika - Build Authentic Friendships Every Wednesday | Premium Community Experience | Connect & Co",
  description: "Building authentic friendships together through our weekly Swedish Fika ritual. Join 20-30 exceptional people every Wednesday for genuine connection and happiness. Your predictable place for authentic friendship building in premium Tokyo venues. Deepen real friendships through consistent community touchpoints.",
  keywords: "weekly fika, signature fika, weekly authentic friendships, recurring friendship building, premium community events, swedish fika tradition, tokyo weekly friendship meetup, authentic networking, weekly friendship ritual, community infrastructure, wednesday fika, consistent friendship building, genuine weekly connections, premium venue friendships, weekly social gathering, authentic friendship routine, happiness through weekly ritual, predictable friendship building, recurring authentic connections, weekly community touchpoints",
  url: "https://connectco.app/signature-fika",
  canonical: "https://connectco.app/signature-fika",
  openGraph: {
    title: "Weekly Signature Fika - Build Authentic Friendships Every Wednesday",
    description: "Join our weekly Swedish Fika tradition every Wednesday. Build genuine friendships through premium venues, authentic conversations, and exceptional people. Your weekly ritual for happiness and authentic connection.",
    url: "https://connectco.app/signature-fika",
    image: "https://connectco.app/signature-fika-og.jpg",
    type: "website"
  },
  twitter: {
    card: "summary_large_image",
    title: "Weekly Signature Fika - Build Authentic Friendships Every Wednesday",
    description: "Weekly Swedish Fika ritual. Build genuine friendships through premium venues and authentic conversations every Wednesday.",
    image: "https://connectco.app/signature-fika-og.jpg"
  }
};

const signatureFikaSEO = {
  title: "Weekly Signature Fika - Build Authentic Friendships Every Wednesday | Premium Friendship Building Ritual | Connect & Co",
  description: "Building authentic friendships together through our weekly Swedish Fika ritual. Join 20-30 exceptional people every Wednesday for genuine connection and happiness. Your predictable place for authentic friendship building in premium Tokyo venues. Deepen real friendships through consistent weekly community touchpoints that create lasting bonds.",
  keywords: "weekly fika, signature fika, weekly authentic friendships, recurring friendship building, premium friendship events, swedish fika tradition, tokyo weekly friendship ritual, authentic friendships wednesday, genuine weekly connections, premium venue friendships, weekly friendship gathering, authentic friendship routine, happiness through weekly ritual, predictable friendship building, recurring authentic connections, weekly community friendships, wednesday fika tokyo, consistent friendship building, weekly social infrastructure, authentic friendship touchpoints, premium friendship experience, regular friendship meetings, weekly authentic networking",
  url: "https://connectco.app/signature-fika",
  canonical: "https://connectco.app/signature-fika",
  openGraph: {
    title: "Weekly Signature Fika - Build Authentic Friendships Every Wednesday",
    description: "Join our weekly Swedish Fika tradition every Wednesday. Build genuine friendships through premium venues, authentic conversations, and exceptional people. Your weekly ritual for happiness and authentic connection.",
    url: "https://connectco.app/signature-fika",
    image: "https://connectco.app/signature-fika-og.jpg",
    type: "website"
  },
  twitter: {
    card: "summary_large_image",
    title: "Weekly Signature Fika - Build Authentic Friendships Every Wednesday",
    description: "Weekly Swedish Fika ritual. Build genuine friendships through premium venues and authentic conversations every Wednesday.",
    image: "https://connectco.app/signature-fika-og.jpg"
  }
};

// Events
const eventsSEO = {
  title: "Build Authentic Friendships Through Swedish Fika | Where Strangers Become Friends in Tokyo, Osaka & Beyond | Connect & Co",
  description: "Building authentic friendships together starts with Swedish Fika. Join locals, international professionals, digital nomads, and culture enthusiasts finding genuine friendship and happiness through Monthly Fika (40+ people), Weekly Signature Fika, intimate Curated Fika (3-4 people), and authentic friendship community experiences across Tokyo, Osaka, Lisbon, Porto, and expanding to major cities worldwide.",
  keywords: "authentic friendships, building friendships, genuine friendship, happiness through connection, authentic connection, Where Strangers Become Friends, find your people, find your tribe tokyo, friendship building, meaningful friendships, friendship-first community, real friendships tokyo, fika coffee, monthly fika, weekly fika, curated fika, community events tokyo, friendship events, authentic friendship groups, swedish fika tokyo, international friendships, authentic networking, genuine connections, friendship community, member exclusive events, intimate friendship gatherings, cultural exchange friendships, tokyo friendship events, osaka community friendships, digital nomad friendships tokyo, culture enthusiasts community, locals welcome friendship, international professionals friendship, community building hub, social infrastructure tokyo, meaningful connections, authentic friendship events, friendship touchpoints, happiness community, expat friendships worldwide, friendship alternatives to networking, how to make authentic friends tokyo, how to find like-minded friends, making real friends after 30, adult friendship groups tokyo, genuine friendship building, friendship priority access, global friendship community tokyo, authentic friendship experiences",
  url: "https://connectco.app/events",
  canonical: "https://connectco.app/events",
  robots: "index, follow",
  openGraph: {
    title: "Build Authentic Friendships Through Swedish Fika | Where Strangers Become Friends",
    description: "Building authentic friendships together. Join locals, international professionals, digital nomads, and culture enthusiasts finding genuine friendship and happiness through Swedish Fika across Tokyo, Osaka, Lisbon, Porto, and worldwide.",
    url: "https://connectco.app/events",
    image: "https://connectco.app/events-og.jpg",
    type: "website",
    site_name: "Connect & Co",
    locale: "en_US"
  },
  twitter: {
    card: "summary_large_image",
    site: "@connectandco",
    title: "Build Authentic Friendships Through Swedish Fika | Where Strangers Become Friends",
    description: "Building authentic friendships together through Swedish Fika. Genuine friendship and happiness await across Tokyo, Osaka, and worldwide.",
    image: "https://connectco.app/events-og.jpg"
  },
  additionalMeta: [
    { name: "author", content: "Connect & Co" },
    { name: "publisher", content: "Connect & Co" },
    { name: "application-name", content: "Connect & Co Events" },
    { name: "apple-mobile-web-app-title", content: "Connect & Co Events" },
    { name: "theme-color", content: "#8b5cf6" },
    { name: "format-detection", content: "telephone=no" },
    { property: "article:author", content: "Connect & Co" },
    { property: "article:publisher", content: "https://connectco.app" },
    { property: "og:image:width", content: "1200" },
    { property: "og:image:height", content: "630" },
    { property: "og:image:alt", content: "Connect & Co - Building authentic friendships together through Swedish Fika. Genuine connections where locals, digital nomads, international professionals, and culture enthusiasts find real friendship and happiness" }
  ]
};



const origeventsSEO = {
  title: "Where Strangers Become Friends Through Swedish Fika | Community Events in Tokyo, Osaka & Beyond | Connect & Co",
  description: "Join intimate Swedish Fika gatherings where locals, international professionals, digital nomads, and culture enthusiasts find their people. Monthly Fika (40+ people), Weekly Signature Fika, Curated Fika (3-4 people), and diverse experiences across Tokyo, Osaka, Lisbon, Porto, and expanding to major cities worldwide.",
  keywords: "Where Strangers Become Friends, find your people, find your tribe tokyo, fika coffee, monthly fika, weekly fika, curated fika, community events tokyo, priority event access, swedish fika tokyo, international networking, expat events japan, authentic community gatherings, member exclusive events, intimate social gatherings, cultural exchange events, tokyo networking, osaka community events, digital nomad community tokyo, culture enthusiasts events, locals welcome community, international professionals networking, community building hub, social infrastructure tokyo, meaningful connections, authentic friendship events, community touchpoints, expat community worldwide, professional networking alternatives, how to find like-minded people tokyo, making friends after 30, adult friendship groups tokyo, waitlist priority, global community tokyo, premium event access, exclusive networking events",
  url: "https://connectco.app/events",
  canonical: "https://connectco.app/events",
  robots: "index, follow",
  openGraph: {
    title: "Exclusive Community Events - Priority Access for Members",
    description: "Join intimate Swedisk fika coffee gatherings across Tokyo and Osaka with priority member access. Swedish Fika, cultural experiences, and authentic community for internationals.",
    url: "https://connectco.app/events",
    image: "https://connectco.app/events-og.jpg",
    type: "website",
    site_name: "Connect & Co",
    locale: "en_US"
  },
  twitter: {
    card: "summary_large_image",
    site: "@connectandco",
    title: "Exclusive Community Events - Priority Access for Members",
    description: "Join intimate Swedish Fika, cultural experiences, and authentic community gatherings across Tokyo and Osaka.",
    image: "https://connectco.app/events-og.jpg"
  },
  additionalMeta: [
    { name: "author", content: "Connect & Co" },
    { name: "publisher", content: "Connect & Co" },
    { name: "application-name", content: "Connect & Co Events" },
    { name: "apple-mobile-web-app-title", content: "Connect & Co Events" },
    { name: "theme-color", content: "#8b5cf6" },
    { name: "format-detection", content: "telephone=no" },
    { property: "article:author", content: "Connect & Co" },
    { property: "article:publisher", content: "https://connectco.app" },
    { property: "og:image:width", content: "1200" },
    { property: "og:image:height", content: "630" },
    { property: "og:image:alt", content: "Connect & Co exclusive community events - Swedish Fika, cultural experiences, and authentic human connection" }
  ]
};

// Conversation SEO


const wellnessStructuredData = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "name": "Community Wellness Hub",
  "description": "Mental health and wellness support for international community building and networking",
  "url": "https://connectco.app/wellness",
  "mainEntity": {
    "@type": "ItemList",
    "name": "Wellness Features",
    "itemListElement": [
      {
        "@type": "Service",
        "position": 1,
        "name": "Pre-Event Mood Check-in",
        "description": "Quick emotional wellness check before events with personalized tips"
      },
      {
        "@type": "Service", 
        "position": 2,
        "name": "Social Anxiety Resources",
        "description": "Tools and techniques for managing networking anxiety including breathing exercises"
      },
      {
        "@type": "Service",
        "position": 3, 
        "name": "Mindful Networking Tips",
        "description": "Quality over quantity connection guidance for authentic networking"
      },
      {
        "@type": "Service",
        "position": 4,
        "name": "Peer Support Network", 
        "description": "Connect with wellness-minded community members for support"
      }
    ]
  },
  "provider": {
    "@type": "Organization",
    "name": "Connect & Co",
    "url": "https://connectco.app"
  },
  "audience": {
    "@type": "Audience",
    "audienceType": "International professionals and community builders"
  }
};

// 3. Add health-related FAQ schema
const wellnessFAQs = [
  {
    question: "How can Connect & Co help with social anxiety at networking events?",
    answer: "Our wellness hub provides pre-event mood check-ins, guided breathing exercises, mindful networking tips, and peer support to help manage social anxiety and build confidence in networking situations."
  },
  {
    question: "What wellness features are available for community members?",
    answer: "Members get access to pre-event mood tracking, social anxiety resources including 5-minute breathing exercises, mindful networking tips, wellness challenges, and a peer support network for authentic connections."
  },
  {
    question: "Is the wellness support suitable for international professionals?",
    answer: "Yes, our wellness resources are designed specifically for international community members who may face additional challenges with cultural differences, language barriers, and building new social networks."
  },
  {
    question: "How does the peer support network work?",
    answer: "Community members can request support through our anonymous peer network, including event buddy matching, pre-event anxiety chat, and newcomer guidance from experienced members."
  }
];

// NEW: SEO Route Component - ONLY ADDITION
const SEORoute = ({ children, seoConfig, dynamicSEO }) => {
  const location = useLocation();
  
  useEffect(() => {
    // Apply SEO configuration for this route
    const finalSEOConfig = dynamicSEO || seoConfig;
    
    if (finalSEOConfig) {
      // Ensure canonical URL is set for current path
      const canonicalUrl = finalSEOConfig.canonical || `https://connectco.app${location.pathname}`;
      
      // Apply SEO with proper canonical URL
      const enhancedConfig = {
        ...finalSEOConfig,
        canonical: canonicalUrl,
        url: canonicalUrl
      };
      
      // This will be handled by the SEO component
      console.log('Setting SEO for route:', location.pathname, enhancedConfig);
    }
  }, [location.pathname, seoConfig, dynamicSEO]);
  
  return (
    <>
      {(dynamicSEO || seoConfig) && <SEO {...(dynamicSEO || seoConfig)} />}
      {children}
    </>
  );
};

// Analytics wrapper component
const AnalyticsWrapper = ({ children }) => {
  useAnalytics(); // This will track page views automatically
  return children;
};

// Custom Hooks - COMPLETELY PRESERVED
const useIntersectionObserver = (options = {}) => {
  const [ref, setRef] = useState(null);
  const [isIntersecting, setIsIntersecting] = useState(false);

  useEffect(() => {
    if (!ref) return;
    
    const observer = new IntersectionObserver(([entry]) => {
      setIsIntersecting(entry.isIntersecting);
    }, options);
    
    observer.observe(ref);
    return () => observer.disconnect();
  }, [ref, options]);

  return [setRef, isIntersecting];
};

// 
// Enhanced mobile-friendly intersection observer
const useIntersectionObserverMobile = (options = {}) => {
  const [ref, setRef] = useState(null);
  const [isIntersecting, setIsIntersecting] = useState(false);
  const [hasIntersected, setHasIntersected] = useState(false);

  useEffect(() => {
    if (!ref) return;
    
    // Mobile detection
    const isMobile = window.innerWidth <= 768;
    
    // Mobile-optimized options
    const mobileOptions = {
      threshold: isMobile ? [0, 0.01, 0.05] : [0.1], // Much lower threshold for mobile
      rootMargin: isMobile ? '100px 0px' : '50px 0px', // Earlier trigger for mobile
      ...options
    };
    
    console.log('📱 Mobile intersection observer:', { isMobile, options: mobileOptions });
    
    const observer = new IntersectionObserver(([entry]) => {
      const isVisible = entry.isIntersecting;
      console.log('📱 Intersection change:', { isVisible, mobile: isMobile });
      
      setIsIntersecting(isVisible);
      
      // Once visible, stay visible (prevents mobile flickering)
      if (isVisible && !hasIntersected) {
        setHasIntersected(true);
      }
    }, mobileOptions);
    
    observer.observe(ref);
    
    // Mobile fallback: Auto-show after delay if observer fails
    const fallbackTimer = setTimeout(() => {
      if (!hasIntersected && !isIntersecting) {
        console.log('📱 Mobile fallback: Force showing events');
        setIsIntersecting(true);
        setHasIntersected(true);
      }
    }, 1000); // Show after 1 second regardless
    
    return () => {
      observer.disconnect();
      clearTimeout(fallbackTimer);
    };
  }, [ref, options, hasIntersected, isIntersecting]);

  // Return true if ever intersected OR currently intersecting
  return [setRef, isIntersecting || hasIntersected];
};

// Fixed useLocalStorage hook - COMPLETELY PRESERVED
const useLocalStorage = (key, initialValue) => {
  const [storedValue, setStoredValue] = useState(() => {
    try {
      const item = window.localStorage.getItem(key);
      
      if (!item || item === 'undefined' || item === 'null') {
        return initialValue;
      }
      
      const parsed = JSON.parse(item);
      return parsed !== null && parsed !== undefined ? parsed : initialValue;
      
    } catch (error) {
      console.warn(`Error reading localStorage key "${key}":`, error);
      try {
        window.localStorage.removeItem(key);
      } catch (clearError) {
        console.warn(`Could not clear corrupted localStorage key "${key}":`, clearError);
      }
      return initialValue;
    }
  });

  const setValue = useCallback((value) => {
    try {
      setStoredValue(value);
      const valueToStore = typeof value === 'function' ? value(storedValue) : value;
      window.localStorage.setItem(key, JSON.stringify(valueToStore));
    } catch (error) {
      console.error(`Error setting localStorage key "${key}":`, error);
    }
  }, [key, storedValue]);

  return [storedValue, setValue];
};

// Testimonial Carousel Component for Mobile
const TestimonialCarousel = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  
  const testimonials = [
    {
      name: "Rachel",
      role: "Founding Member • 🇯🇵",
      quote: "Thank you for what you're building with ConnectCo. I really appreciate your way of treating people, and the courage it takes to start something from zero with an open mind. That kind of fearlessness around change has genuinely influenced how I think about life. In the end, we all need a life with depth and spirit, not just movement.",
      bgColor: "from-amber-50 to-orange-50",
      borderColor: "border-amber-200",
      emoji: "✨",
      emojiColor: "bg-amber-100",
      textColor: "text-amber-600",
      badge: "Recent testimonial"
    },
    {
      name: "Pedro Noel",
      role: "Member • 🇺🇸 → 🇯🇵",
      quote: "Moving to Japan hasn't been easy. Connecting with the Fika community has been a game changer. You have to put yourself out there and be your authentic self for those connections to become real.",
      bgColor: "from-blue-50 to-indigo-50",
      borderColor: "border-transparent",
      emoji: "👨‍💼",
      emojiColor: "bg-blue-100",
      textColor: "text-blue-600",
      link: "/blog/discovering-community-one-fika-at-a-time",
      linkText: "Read Pedro's story"
    },
    {
      name: "Terry",
      role: "Founding Member • 🇺🇸🇯🇵",
      quote: "At first, I felt self-conscious about regularly showing up to Fika. But now it's become a treasured weekly routine. I look forward to meeting new people, but also to seeing familiar faces.",
      bgColor: "from-green-50 to-teal-50",
      borderColor: "border-transparent",
      emoji: "🌱",
      emojiColor: "bg-green-100",
      textColor: "text-green-600",
      link: "/blog/vulnerability-by-way-of-self-introduction",
      linkText: "Read Terry's story"
    },
    {
      name: "Founding Member • 🇯🇵",
      role: "December 2025",
      quote: "Your Fika gathering is one of the best things I discovered this year. Thank you for your entrepreneurial creativity and commitment!",
      bgColor: "from-amber-50 to-orange-50",
      borderColor: "border-amber-200",
      emoji: "✨",
      emojiColor: "bg-amber-100",
      textColor: "text-amber-600",
      badge: "Recent testimonial"
    },
    {
      name: "Sarah L.",
      role: "Member • 🇺🇸 → 🇯🇵",
      quote: "Three months ago I was isolated in Tokyo. ConnectCo changed everything. From feeling lost to making genuine friends — this community gave me a sense of home.",
      bgColor: "from-purple-50 to-blue-50",
      borderColor: "border-transparent",
      emoji: "👩‍💼",
      emojiColor: "bg-purple-100",
      textColor: "text-purple-600",
      badge: "Member since May 2025"
    }
  ];

  const nextTestimonial = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const current = testimonials[currentIndex];

  return (
    <div className="md:hidden relative">
      <div className="relative">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentIndex}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            transition={{ duration: 0.3 }}
            className={`bg-gradient-to-br ${current.bgColor} rounded-xl p-6 border-2 ${current.borderColor}`}
          >
            <div className="flex items-center mb-4">
              <div className={`w-12 h-12 ${current.emojiColor} rounded-full flex items-center justify-center text-2xl mr-3 flex-shrink-0`}>
                {current.emoji}
              </div>
              <div>
                <h4 className="font-semibold text-gray-800">{current.name}</h4>
                <p className="text-sm text-gray-600">{current.role}</p>
              </div>
            </div>
            <blockquote className="text-gray-700 leading-relaxed mb-4 text-sm">
              {current.quote}
            </blockquote>
            {current.badge && (
              <div className={`flex items-center text-xs ${current.textColor}`}>
                <CheckCircleIcon className="w-4 h-4 mr-1 flex-shrink-0" />
                <span>{current.badge}</span>
              </div>
            )}
            {current.link && (
              <div className={`text-xs ${current.textColor}`}>
                <a 
                  href={current.link}
                  className="inline-flex items-center hover:underline"
                >
                  {current.linkText}
                  <ArrowRightIcon className="w-3 h-3 ml-1" />
                </a>
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        {/* Navigation Arrows */}
        <button
          onClick={prevTestimonial}
          className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 bg-white rounded-full p-2 shadow-lg hover:bg-gray-50 transition-all"
          aria-label="Previous testimonial"
        >
          <svg className="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        
        <button
          onClick={nextTestimonial}
          className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 bg-white rounded-full p-2 shadow-lg hover:bg-gray-50 transition-all"
          aria-label="Next testimonial"
        >
          <svg className="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      {/* Dots */}
      <div className="flex justify-center gap-2 mt-6">
        {testimonials.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentIndex(index)}
            className={`w-2 h-2 rounded-full transition-all ${
              index === currentIndex ? 'bg-purple-600 w-6' : 'bg-gray-300'
            }`}
            aria-label={`Go to testimonial ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

// HomePage Component - COMPLETELY PRESERVED - NO CHANGES except accessibility attributes
const HomePage = () => {
  const navigate = useNavigate();

  // 1-11-26
   useEffect(() => {
    if (typeof window !== 'undefined') {
      preloadCriticalImage('/images/events/nov-monthly-fika-1.jpg', 'hero');
      preloadCriticalImage('/images/events/curated-fika-1.jpg', 'large');
      preloadCriticalImage('/images/events/signature-fika-11.jpg', 'large');
    }
  }, []);
  // end of 1-11-26

  const [showFullTestimonial, setShowFullTestimonial] = useState(false);

  // State management (ALL ORIGINAL FUNCTIONALITY PRESERVED)
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [selectedBlogCategory, setSelectedBlogCategory] = useState(0);
  const [memberCount, setMemberCount] = useState(2000);
  const [bookmarkedPosts, setBookmarkedPosts] = useLocalStorage('bookmarkedPosts', []);
  const [likedPosts, setLikedPosts] = useLocalStorage('likedPosts', []);
  const [showExitIntent, setShowExitIntent] = useState(false);
  const [isTestimonialPaused, setIsTestimonialPaused] = useState(false);

  // CRITICAL FIX: Enhanced polling state with revision counter (PRESERVED)
  const [currentPoll, setCurrentPoll] = useState(null);
  const [userVote, setUserVote] = useState(null);
  const [pollLoading, setPollLoading] = useState(false);
  const [pollError, setPollError] = useState(null);
  const [pollRevision, setPollRevision] = useState(0); // 🔥 CRITICAL: Force re-renders

  // Get analytics functions (ONLY ADDITION)
  const { trackEvent: analyticsTrackEvent } = useAnalytics();

  // Intersection Observer hooks (PRESERVED)
  const [membersRef, membersInView] = useIntersectionObserver({ threshold: 0.1 });
  const [eventsRef, eventsInView] = useIntersectionObserver({ threshold: 0.05 });
  const [interactiveRef, interactiveInView] = useIntersectionObserver({ threshold: 0.1 });

//  const [eventsRef, eventsInView] = useIntersectionObserverMobile({ threshold: 0.05 });

  // Enhanced events data (PRESERVED)
  const events = useMemo(() => getCompatibleEvents(), []);

  // 🆕 ADD: Filter for upcoming events only
const upcomingEvents = useMemo(() => {
  const now = new Date();
  
  return events.filter(event => {
    if (!event.next) return false;
    
    try {
      let eventDate;
      
      if (event.next instanceof Date) {
        eventDate = event.next;
      } else if (typeof event.next === 'string') {
        const dateStr = event.next.trim();
        eventDate = new Date(dateStr);
        
        if (isNaN(eventDate.getTime())) {
          const currentYear = new Date().getFullYear();
          eventDate = new Date(`${dateStr}, ${currentYear}`);
          
          if (isNaN(eventDate.getTime())) {
            eventDate = new Date(`${dateStr}, ${currentYear + 1}`);
          }
        }
      }
      
      if (!eventDate || isNaN(eventDate.getTime())) {
        console.warn(`Invalid date format for event ${event.id}: ${event.next}`);
        return false;
      }
      
      const eventDateOnly = new Date(eventDate.getFullYear(), eventDate.getMonth(), eventDate.getDate());
      const nowDateOnly = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      
      return eventDateOnly >= nowDateOnly;
      
    } catch (error) {
      console.warn(`Error parsing date for event ${event.id}:`, error);
      return false;
    }
  }).slice(0, 4);
}, [events]);

  // ALL ORIGINAL DATA (PRESERVED)
  const testimonials = useMemo(() => [
    {
      name: "Sarah M.",
      role: "UX Designer",
      company: "Google",
      nationality: "🇺🇸",
      quote: "Connect & Co changed my experience completely. The priority access saved me from so many waitlists, and I've built my entire social circle through these events!",
      image: "👩‍💻",
      memberSince: "March 2025",
      connectionsMode: 23,
      verified: true,
      badges: ["Early Adopter", "Super Connector"]
    },
    {
      name: "Jason G.", 
      role: "Owner/Founder",
      company: "Quality Surfboards LLC,",
      nationality: "🇯🇵",
      quote: "The international perspective here is invaluable for global business.",
      image: "👨‍💼",
      memberSince: "January 2025",
      connectionsMode: 47,
      verified: true,
      badges: ["Entrepreneur", "Mentor"]
    },
    {
      name: "Emma R.",
      role: "Digital Nomad",
      company: "Freelance",
      nationality: "🇪🇸", 
      quote: "The member directory helped me find roommates, language exchange partners, and even freelance clients. It's like having a local network instantly anywhere I go.",
      image: "👩‍💻",
      memberSince: "June 2025",
      connectionsMode: 31,
      verified: true,
      badges: ["Nomad", "Community Builder"]
    }
  ], []);

  const memberBenefits = useMemo(() => [
    {
      icon: ClockIcon,
      title: "24hr Early Access",
      description: "RSVP to popular events ",
      color: "text-blue-600", // FIXED: Improved contrast
      featured: true,
      savings: "Skip 72+ person waitlists"
    },
    {
      icon: SparklesIcon,
      title: "Curated Fika Access",
      description: "AI-matched intimate gatherings with 3-4 interesting people for meaningful conversations",
      color: "text-amber-600", // FIXED: Improved contrast
      featured: true,
      savings: "Meet interesting people"
    },
    {
      icon: StarIcon,
      title: "Priority Waitlist",
      description: "First to know when spots open in full events like Fika",
      color: "text-purple-600", // FIXED: Improved contrast
      featured: true,
      savings: "Save 3-5 hours per week"
    },
    {
      icon: UsersIcon,
      title: "Cross-Cultural Connections",
      description: `Where world travelers meet local culture enthusiasts to create meaningful connections and shared discoveries`,
      color: "text-green-600", // FIXED: Improved contrast
      featured: false,
      savings: "Authentic experiences"
    },
    {
      icon: CameraIcon,
      title: "Exclusive Photos",
      description: "Access high-res event photo galleries and memories",
      color: "text-pink-600", // FIXED: Improved contrast
      featured: false,
      savings: "Premium quality"
    },
    {
      icon: BoltIcon,
      title: "Members-Only Experiences",
      description: "Diverse gatherings just for website members (25-30 people)",
      color: "text-yellow-600", // FIXED: Improved contrast
      featured: true,
      savings: "Exclusive access"
    },
    {
      icon: TrophyIcon,
      title: "Achievement Badges",
      description: "Earn recognition for community participation and connections",
      color: "text-indigo-600", // FIXED: Improved contrast
      featured: false,
      savings: "Gamified experience"
    }
  ], [memberCount]);

  // CRITICAL FIX: Enhanced poll options with revision dependency (PRESERVED)
  const pollOptions = useMemo(() => {
    console.log('🔄 Recalculating poll options, revision:', pollRevision);
    console.log('🔄 Current poll data:', currentPoll);
    
    if (!currentPoll?.options) {
      console.log('🔄 Using fallback options');
      return [
        { id: 1, text: "Explore the Quiet Charm of Nezu & Yanaka (Yanesen area)", votes: 0, emoji: "⛩️" },
        { id: 2, text: "Free walking tour museum", votes: 0, emoji: "🏛️" },
        { id: 3, text: "Nordic Morning & Urban Oasis", votes: 0, emoji: "📸" },
        { id: 4, text: "Growth Circle (Beyond the Book Club)", votes: 0, emoji: "🚀" }
      ];
    }
    
    const options = currentPoll.options.map(option => ({
      id: option.id,
      text: option.text,
      votes: option.votes || 0,
      emoji: option.emoji || "📝"
    }));
    
    console.log('🔄 Calculated poll options:', options.map(o => ({
      text: o.text.substring(0, 20),
      votes: o.votes,
      id: o.id
    })));
    
    return options;
  }, [currentPoll, pollRevision]); // 🔥 CRITICAL: Added pollRevision dependency

  const totalVotes = pollOptions.reduce((sum, option) => sum + option.votes, 0);

  // Auto-rotate testimonials with pause on hover (PRESERVED)
  useEffect(() => {
    if (isTestimonialPaused) return;
    
    const interval = setInterval(() => {
      setCurrentTestimonial(prev => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [testimonials.length, isTestimonialPaused]);

  // Exit intent detection (PRESERVED)
  useEffect(() => {
    const handleMouseLeave = (e) => {
      if (e.clientY <= 0 && !showExitIntent) {
        setShowExitIntent(true);
      }
    };

    document.addEventListener('mouseleave', handleMouseLeave);
    return () => document.removeEventListener('mouseleave', handleMouseLeave);
  }, [showExitIntent]);

  // CRITICAL FIX: Enhanced polling initialization (PRESERVED)
  useEffect(() => {
    const initializePoll = async () => {
      console.log('🗳️ Initializing poll system...');
      setPollError(null);
      
      try {
        const result = await pollService.initialize('57e399a7-480f-4875-8311-8f59fe18cd20');
        
        if (result?.pollData) {
          console.log('✅ Poll initialized successfully:', result.pollData);
          console.log('✅ Initial vote counts:', result.pollData.options?.map(o => ({
            text: o.text.substring(0, 20),
            votes: o.votes
          })));
          
          setCurrentPoll(result.pollData);
          setUserVote(result.userVote);
          setPollRevision(1); // 🔥 CRITICAL: Initialize revision counter
        } else {
          console.warn('⚠️ Failed to initialize polling');
          setPollError('Failed to load poll data');
        }
      } catch (error) {
        console.error('❌ Error initializing poll:', error);
        setPollError('Connection error');
      }
    };

    initializePoll();
  }, []);

  // Enhanced event navigation with comprehensive analytics
  const handleEventNavigation = useCallback((event, clickLocation = 'homepage') => {
    console.log('📊 [DEBUG] Event card clicked:', { 
      eventId: event.id, 
      eventSlug: event.slug,
      clickLocation 
    });
    
    // Enhanced analytics tracking with rich metadata
    analyticsTrackEvent('event_card_click', {
      // Event details
      event_id: event.id,
      event_slug: event.slug,
      event_title: event.title,
      event_type: event.eventType || 'unknown',
      event_featured: event.featured || false,
      event_waitlist_count: event.waitlist || 0,
      event_capacity: event.capacity || 0,
      event_location: event.location || '',
      event_date: event.next || '',
      event_time: event.time || '',
      
      // Click context
      click_location: clickLocation, // 'homepage', 'events-page', 'search-results'
      click_section: 'event-cards',
      page_location: window.location.pathname,
      
      // User context
      viewport_width: window.innerWidth,
      viewport_height: window.innerHeight,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      user_agent: navigator.userAgent,
      referrer: document.referrer || 'direct',
      
      // Campaign attribution
      campaign_source: new URLSearchParams(window.location.search).get('utm_source') || 'direct',
      campaign_medium: new URLSearchParams(window.location.search).get('utm_medium') || 'organic',
      campaign_campaign: new URLSearchParams(window.location.search).get('utm_campaign') || 'none',
      
      // Engagement metrics
      event_category: 'engagement',
      action_type: 'card_click',
      value: 1 // For conversion tracking
    });

    // Navigate to event detail page
    navigate(`/events/${event.slug}`);
  }, [navigate, analyticsTrackEvent]);

  // Enhanced poll vote handler with analytics
  const handlePollVote = useCallback(async (optionId) => {
    if (pollLoading) {
      console.log('🗳️ Vote already in progress, ignoring click');
      return;
    }

    console.log('🗳️ Starting vote for option:', optionId);
    console.log('🗳️ Current user vote before:', userVote);
    console.log('🗳️ Current poll options before:', pollOptions.map(o => ({
      text: o.text.substring(0, 20),
      votes: o.votes
    })));

    console.log('📊 [DEBUG] Poll vote clicked:', optionId); // DEBUG LOG

    // Track poll interaction
    analyticsTrackEvent('poll_vote', {
      poll_id: currentPoll?.id,
      option_id: optionId,
      previous_vote: userVote,
      location: 'homepage_interactive',
      event_category: 'engagement'
    });

    // PRESERVED: All original poll voting logic
    setPollLoading(true);
    setPollError(null);

    try {
      if (!currentPoll) {
        throw new Error('No poll data available');
      }

      const result = await pollService.vote(currentPoll.id, optionId);
      console.log('🗳️ Vote result received:', result);

      if (result.success) {
        console.log('✅ Vote successful');
        
        // CRITICAL FIX 1: Update user vote state IMMEDIATELY
        const newUserVote = result.action === 'removed' ? null : result.optionId;
        console.log('🔄 Setting user vote from', userVote, 'to', newUserVote);
        setUserVote(newUserVote);
        
        // CRITICAL FIX 2: Force update poll data with fresh results
        if (result.pollData) {
          console.log('🔄 Updating poll data with fresh results');
          console.log('🔄 Fresh vote counts:', result.pollData.options?.map(o => ({
            text: o.text.substring(0, 20),
            votes: o.votes
          })));
          
          // 🔥 CRITICAL: Use functional update to ensure React detects the change
          setCurrentPoll(prevPoll => {
            const newPoll = { ...result.pollData };
            console.log('🔄 Poll state transition:', {
              before: prevPoll?.options?.map(o => ({ text: o.text.substring(0, 20), votes: o.votes })),
              after: newPoll.options?.map(o => ({ text: o.text.substring(0, 20), votes: o.votes }))
            });
            return newPoll;
          });
          
          // 🔥 CRITICAL: Force re-render by incrementing revision
          setPollRevision(prev => {
            const newRevision = prev + 1;
            console.log('🔄 Incrementing poll revision from', prev, 'to', newRevision);
            return newRevision;
          });
          
        } else {
          // Fallback: manually refresh poll data
          console.log('🔄 No fresh data in result, manually refreshing...');
          try {
            const freshData = await pollService.getPollData(currentPoll.id);
            if (freshData) {
              console.log('🔄 Manual refresh successful, vote counts:', freshData.options?.map(o => ({
                text: o.text.substring(0, 20),
                votes: o.votes
              })));
              
              setCurrentPoll(freshData);
              setPollRevision(prev => prev + 1); // 🔥 Force re-render
            }
          } catch (refreshError) {
            console.warn('⚠️ Could not refresh poll data:', refreshError);
          }
        }
        
        console.log(`✅ ${result.action === 'removed' ? 'Vote removed' : 'Vote updated'} successfully`);
        
      } else {
        console.error('❌ Vote failed:', result.error);
        setPollError(result.error || 'Vote failed');
      }
    } catch (error) {
      console.error('❌ Vote error:', error);
      setPollError(error.message || 'Network error');
    } finally {
      setPollLoading(false);
    }
  }, [currentPoll, pollLoading, userVote, pollOptions, pollRevision, analyticsTrackEvent]);

  // 🔥 CRITICAL: Debug effect to track state changes (PRESERVED)
  useEffect(() => {
    console.log('🔍 State change detected:');
    console.log('🔍 Poll revision:', pollRevision);
    console.log('🔍 User vote:', userVote);
    console.log('🔍 Poll options:', pollOptions.map(o => ({
      text: o.text.substring(0, 20),
      votes: o.votes,
      id: o.id
    })));
    console.log('🔍 Total votes:', totalVotes);
  }, [pollRevision, userVote, pollOptions, totalVotes]);

  // ALL ORIGINAL JSX CONTENT (COMPLETELY PRESERVED) with ACCESSIBILITY FIXES
  return (
    <>
      {/* Enhanced Hero Section with Email Signup */}     
     


<section id="hero" className="min-h-screen flex items-center justify-center text-center text-white relative overflow-hidden">
  <div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-purple-600 to-blue-800"></div>
  
  {/* Floating background elements */}
  <div className="absolute top-20 left-10 w-20 h-20 bg-white/10 rounded-full blur-xl animate-pulse"></div>
  <div className="absolute bottom-32 right-16 w-32 h-32 bg-purple-300/20 rounded-full blur-2xl animate-pulse delay-1000"></div>
  <div className="absolute top-1/2 left-1/4 w-16 h-16 bg-blue-300/20 rounded-full blur-xl animate-pulse delay-500"></div>
  
  <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 pt-20">
     
    <motion.h1
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8 }}
      className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-4 sm:mb-6 pt-8 sm:pt-12 md:pt-16"
    >
      <span>On a mission to make you </span>
      <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-pink-500">
        remarkable
      </span>
    </motion.h1>
              
    <motion.p
    initial={{ opacity: 0, y: 30 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.8, delay: 0.2 }}
    className="text-lg sm:text-xl md:text-2xl mb-8 sm:mb-10 opacity-90 px-2"
  >
    One real conversation at a time. In person. 
  </motion.p>


    {/* Primary CTA Button - MOBILE OPTIMIZED */}
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay: 0.3 }}
      className="mb-8 sm:mb-10"
    >
      <button
        onClick={() => navigate('/events')}
        className="inline-flex items-center justify-center px-6 sm:px-8 py-3 sm:py-4 bg-white text-purple-700 rounded-lg sm:rounded-xl font-bold text-base sm:text-lg hover:bg-gray-50 hover:scale-105 transition-all shadow-xl hover:shadow-2xl w-auto"
      >
        <span className="whitespace-nowrap">Find Your First Fika</span>
        <ArrowRightIcon className="w-4 h-4 sm:w-5 sm:h-5 ml-2 flex-shrink-0" />
      </button>

       {/* 🆕 NEW: Link to "Why We're Charging" */}
             {/* 
      <div className="mt-3 sm:mt-4">
        <a 
          href="/stories/why-were-charging"
          className="text-white/80 hover:text-white text-xs sm:text-sm underline hover:no-underline transition-all inline-flex items-center"
        >
          Why we're transitioning to membership
          <ArrowRightIcon className="w-3 h-3 ml-1" />
        </a>
      </div>
      */}
      
      <p className="text-xs sm:text-sm text-white/90 mt-3 italic px-4">
        Fika: The original school for remarkable people
      </p>
    </motion.div>


    

    {/* Checkmarks, Reduced cards (3 only), Curated Fika 2x size - MOBILE OPTIMIZED */}
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay: 0.4 }}
      className="max-w-5xl mx-auto mb-8 px-4"
    >

      {/* Grid: 1 column mobile, 2 columns tablet+ */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">

        {/* Monthly Fika - 2x SIZE - IMAGE-FIRST REDESIGN - HERO POSITION */}
        <Link 
          to="/events/authentic-swedish-fika-june-sunday-2026"
          className="lg:col-span-2 block cursor-pointer group"
          aria-label="Learn more about Monthly Fika - Low-pressure way to meet your first friends"
        >
          <div className="relative rounded-2xl overflow-hidden h-[500px] hover:scale-[1.02] transition-all duration-300">
            {/* FULL BACKGROUND IMAGE */}
       
            <img
              src={getImageUrl('/images/events/nov-monthly-fika-1.jpg')}
              alt="Monthly Fika community gathering"
              className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
        

            {/* OPTIMIZED RESPONSIVE IMAGE */}
                {/* 1-11-26
            <OptimizedImage
              src="/images/events/nov-monthly-fika-1.jpg"
              alt="Monthly Fika community gathering"
              context="hero"
              sizes="100vw"
              priority={true}
              fallbackEmoji="☕"
              className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
            />
               */}
            
            {/* Fallback emoji if image fails */}
            <div className="absolute inset-0 bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white text-6xl" style={{display: 'none'}}>
              ☕
            </div>
            
            {/* GRADIENT OVERLAY for text readability */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent"></div>
            
            {/* TEXT OVERLAY at bottom */}
            <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-8 text-white">
              <div className="flex items-start gap-3 mb-4">
                <CheckCircleIcon className="w-8 h-8 sm:w-9 sm:h-9 text-green-300 flex-shrink-0 mt-1" />
                <div className="flex-1">
                  <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-3">Monthly Fika</h3>
                  <p className="text-lg sm:text-xl lg:text-2xl mb-4 leading-relaxed">
                    Low-pressure way to meet your first friends
                  </p>
                  <div className="flex flex-wrap gap-2 text-sm sm:text-base">
                    <span className="px-3 py-1.5 bg-white/20 backdrop-blur-sm rounded-full">40+ people</span>
                    <span className="px-3 py-1.5 bg-white/20 backdrop-blur-sm rounded-full">Twice monthly</span>
                    <span className="px-3 py-1.5 bg-white/20 backdrop-blur-sm rounded-full">Sunday & Saturday mornings</span>
                    <span className="px-3 py-1.5 bg-white/20 backdrop-blur-sm rounded-full">Open to members</span>
                    <span className="px-3 py-1.5 bg-white/20 backdrop-blur-sm rounded-full">Your first community</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Link>

        {/* Curated Fika - IMAGE-FIRST REDESIGN - SECONDARY POSITION */}
        <Link 
          to="/curated-fika-signup"
          className="block cursor-pointer group"
          aria-label="Apply for Curated Fika - Premium intimate Swedish coffee experience"
        >
          <div className="relative rounded-xl overflow-hidden h-[350px] sm:h-[400px] hover:scale-[1.02] transition-all duration-300">
            {/* FULL BACKGROUND IMAGE */}
            
            <img
              src={getImageUrl('/images/events/curated-fika-1.jpg')}
              alt="Curated Fika intimate coffee gathering"
              className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
       

            {/* 1-11-26
            <OptimizedImage
              src="/images/events/curated-fika-1.jpg"
              alt="Curated Fika intimate coffee gathering"
              context="large"
              sizes="(min-width: 1024px) 50vw, 100vw"
              priority={true}
              fallbackEmoji="✨"
              className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
            />
                */}
            
            {/* Fallback emoji if image fails */}
            <div className="absolute inset-0 bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white text-5xl" style={{display: 'none'}}>
              ✨
            </div>
            
            {/* GRADIENT OVERLAY */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent"></div>
            
            {/* TEXT OVERLAY */}
            <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
              <div className="flex items-start gap-3">
                <CheckCircleIcon className="w-7 h-7 sm:w-8 sm:h-8 text-green-300 flex-shrink-0 mt-1" />
                <div className="flex-1">
                  <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold mb-2">Curated Fika</h3>
                  <p className="text-base sm:text-lg lg:text-xl leading-relaxed">
                    3–4 people. Maximum chemistry. Remarkably deep conversation
                  </p>
                </div>
              </div>
            </div>
          </div>
        </Link>

        {/* Deep Fika - 1-on-1 matched format */}
        <Link
          to="/deep-fika"
          className="block cursor-pointer group"
          aria-label="Apply for Deep Fika - One-on-one matched fika for active members"
        >
          <div className="relative rounded-xl overflow-hidden h-[350px] sm:h-[400px] hover:scale-[1.02] transition-all duration-300">
            <img
              src={getImageUrl('/images/events/deep-fika-1.jpg')}
              alt="Deep Fika one-on-one conversation at a Tokyo café"
              className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
            {/* Fallback gradient if image not yet available */}
            <div className="absolute inset-0 bg-gradient-to-br from-stone-900 via-emerald-950 to-stone-900 items-center justify-center text-white text-5xl" style={{display: 'none'}}>
              ☕
            </div>

            {/* New badge */}
            <div className="absolute top-4 left-4 z-10">
              <span className="bg-emerald-500 text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide">New</span>
            </div>

            {/* GRADIENT OVERLAY */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>

            {/* TEXT OVERLAY */}
            <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
              <div className="flex items-start gap-3">
                <CheckCircleIcon className="w-7 h-7 sm:w-8 sm:h-8 text-emerald-300 flex-shrink-0 mt-1" />
                <div className="flex-1">
                  <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold mb-2">Deep Fika</h3>
                  <p className="text-base sm:text-lg lg:text-xl leading-relaxed text-stone-200">
                    One person. Matched to you. Sixty minutes, no agenda.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </Link>
            
      </div>

       {/* FOUNDER STORY HERE - RIGHT AFTER HERO, BEFORE "MORE WAYS TO CONNECT" */}
         {/* 
      <section className="mt-16 pt-16 pb-16 bg-gradient-to-br from-purple-50 to-blue-50">
        <div className="max-w-4xl mx-auto px-6">
          
          <div className="bg-white rounded-2xl p-8 sm:p-10 shadow-lg">
            
            <div className="flex items-center mb-6">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center text-3xl mr-4">
                👨‍💻
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-800">Mark</h3>
                <p className="text-gray-600">Founder</p>
              </div>
            </div>

            <div className="prose prose-lg max-w-none">
              <p className="text-gray-700 leading-relaxed mb-4">
                In 2011, I discovered Swedish Fika during a trip to Stockholm. Watching strangers 
                become friends over coffee, I saw something modern cities had lost: 
                <strong>intentional, judgment-free connection</strong>.
              </p>
              
              <p className="text-gray-700 leading-relaxed mb-4">
                Years later in Tokyo, I watched the same loneliness epidemic—whether you're 
                Japanese navigating adult friendships, a digital nomad seeking community, 
                or a professional wanting deeper connections beyond networking.
              </p>

              <p className="text-gray-700 leading-relaxed">
                So I brought Swedish Fika culture to Tokyo. Not as another networking platform, 
                but as a space where authentic friendship happens naturally—one coffee at a time.
              </p>
            </div>

          </div>
        </div>
      </section>
       */}



      {/* Link to "More ways to connect" section */}
      <div className="text-center mt-4 sm:mt-6">
        <button
          onClick={() => {
            const moreWaysSection = document.getElementById('more-ways-to-connect');
            moreWaysSection?.scrollIntoView({ behavior: 'smooth' });
          }}
          className="text-white/80 hover:text-white text-xs sm:text-sm underline hover:no-underline transition-all"
        >
          More ways to connect
        </button>
      </div>

        {/* ORIG WORKING TESTIMONIALS */}
      <section className="py-12 sm:py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          
          <div className="text-center mb-8 sm:mb-10 lg:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 mb-3 sm:mb-4">
              What Our Members Say
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto px-4">
              Real stories from people who found their community through Fika
            </p>
          </div>

          {/* 4-column grid: 1 col mobile, 2 cols tablet, 4 cols desktop */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            
            {/* Testimonial 1: Rachel- MOST RECENT */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-6 sm:p-8 border-2 border-amber-200"
            >
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center text-2xl mr-3 flex-shrink-0">
                  ✨
                </div>
                <div>
                  <h4 className="font-semibold text-gray-800">Rachel</h4>
                  <p className="text-sm text-gray-600">Founding Member • 🇯🇵 (January 2026) </p>
                </div>
              </div>
              {/* That kind of fearlessness around change has genuinely influenced how I think about life. In the end, we all need a life with depth and spirit, not just movement. */}
              <blockquote className="text-gray-700 leading-relaxed mb-4 text-sm italic">
                "Thank you for what you’re building with ConnectCo. I really appreciate your way of treating people, and the courage it takes to start something from zero with an open mind."
              </blockquote>
              <div className="flex items-center text-xs text-amber-600">
                <CheckCircleIcon className="w-4 h-4 mr-1 flex-shrink-0" />
                <span>Recent testimonial</span>
              </div>
            </motion.div>
            

            {/* Testimonial 2: Pedro */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 sm:p-8"
            >
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center text-2xl mr-3 flex-shrink-0">
                  👨‍💼
                </div>
                <div>
                  <h4 className="font-semibold text-gray-800">Pedro Noel</h4>
                  <p className="text-sm text-gray-600">Member • 🇺🇸 → 🇯🇵</p>
                </div>
              </div>
              <blockquote className="text-gray-700 leading-relaxed mb-4 text-sm">
                "Moving to Japan hasn't been easy. Connecting with the Fika community has been a game changer. You have to put yourself out there and be your authentic self for those connections to become real."
              </blockquote>
              <div className="text-xs text-blue-600">
                <a 
                  href="/blog/discovering-community-one-fika-at-a-time"
                  className="inline-flex items-center hover:underline"
                >
                  Read Pedro's story
                  <ArrowRightIcon className="w-3 h-3 ml-1" />
                </a>
              </div>
            </motion.div>

            {/* Testimonial 3: Terry - VULNERABILITY & RITUAL */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="bg-gradient-to-br from-green-50 to-teal-50 rounded-xl p-6 sm:p-8"
            >
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center text-2xl mr-3 flex-shrink-0">
                  🌱
                </div>
                <div>
                  <h4 className="font-semibold text-gray-800">Terry</h4>
                  <p className="text-sm text-gray-600">Founding Member • 🇺🇸🇯🇵</p>
                </div>
              </div>
              <blockquote className="text-gray-700 leading-relaxed mb-4 text-sm">
                "At first, I felt self-conscious about regularly showing up to Fika. But now it's become a treasured weekly routine. I look forward to meeting new people, but also to seeing familiar faces."
              </blockquote>
              <div className="text-xs text-green-600">
                <a 
                  href="/blog/vulnerability-by-way-of-self-introduction"
                  className="inline-flex items-center hover:underline"
                >
                  Read Terry's story
                  <ArrowRightIcon className="w-3 h-3 ml-1" />
                </a>
              </div>
            </motion.div>

            {/* Testimonial 4: Anonymous - MOST RECENT */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-6 sm:p-8 border-2 border-amber-200"
            >
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center text-2xl mr-3 flex-shrink-0">
                  ✨
                </div>
                <div>
                  <h4 className="font-semibold text-gray-800">Founding Member • 🇯🇵 </h4>
                  <p className="text-sm text-gray-600">December 2025</p>
                </div>
              </div>
              <blockquote className="text-gray-700 leading-relaxed mb-4 text-sm italic">
                "Your Fika gathering is one of the best things I discovered this year. Thank you for your entrepreneurial creativity and commitment!"
              </blockquote>
              <div className="flex items-center text-xs text-amber-600">
                <CheckCircleIcon className="w-4 h-4 mr-1 flex-shrink-0" />
                <span>Recent testimonial</span>
              </div>
            </motion.div>

             {/* Testimonial 5: Sarah */}
              {/* 
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl p-6 sm:p-8"
            >
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center text-2xl mr-3 flex-shrink-0">
                  👩‍💼
                </div>
                <div>
                  <h4 className="font-semibold text-gray-800">Sarah L.</h4>
                  <p className="text-sm text-gray-600">Member • 🇺🇸 → 🇯🇵</p>
                </div>
              </div>
              <blockquote className="text-gray-700 leading-relaxed mb-4 text-sm">
                "Three months ago I was isolated in Tokyo. ConnectCo changed everything. From feeling lost to making genuine friends — this community gave me a sense of home."
              </blockquote>
              <div className="flex items-center text-xs text-purple-600">
                <CheckCircleIcon className="w-4 h-4 mr-1 flex-shrink-0" />
                <span>Member since May 2025</span>
              </div>
            </motion.div>
            */}

          </div>

          {/* Link to more stories */}
          <div className="text-center mt-8 sm:mt-10">
            <a 
              href="/blog"
              className="text-purple-600 hover:text-purple-700 font-medium inline-flex items-center text-sm sm:text-base"
            >
              Read more member stories
              <ArrowRightIcon className="w-4 h-4 ml-2" />
            </a>
          </div>

        </div>
      </section>

  

      {/* Press Badge - Metropolis Japan */}
        {/* 
      <div className="flex justify-center mt-6 mb-8 px-4">
        <a
          href="https://metropolisjapan.com/fika-in-tokyo-swedish-coffee-ritual/"
          target="_blank"
          rel="noopener noreferrer"
          className="group flex flex-col items-center gap-1 bg-white/95 hover:bg-white border border-white/40 w-full max-w-sm px-4 py-3 sm:px-6 sm:py-4 rounded-2xl text-gray-800 transition-all shadow-xl hover:shadow-2xl hover:scale-105"
        >
          <div className="flex items-center gap-2 text-xs text-gray-500 font-medium uppercase tracking-widest">
            <span className="text-yellow-500">★★★★★</span>
            <span>Metropolis Japan · Feb 2026</span>
          </div>
          <p className="text-xs sm:text-sm font-semibold text-gray-900 text-center leading-snug">
            "How a Swedish Coffee Ritual Is Helping Professionals Feel Less Alone"
          </p>
          <span className="text-xs text-purple-600 font-semibold group-hover:underline">
            Read the full article →
          </span>
        </a>
      </div>
       */}


    </motion.div>

    <motion.div
  initial={{ opacity: 0, y: 20 }}
  animate={{ opacity: 1, y: 0 }}
  transition={{ duration: 0.8, delay: 0.25 }}
  className="flex justify-center mb-6 px-4"
>
   <a
    href="https://metropolisjapan.com/fika-in-tokyo-swedish-coffee-ritual/"
    target="_blank"
    rel="noopener noreferrer"
    className="group flex flex-col items-center gap-1 bg-white/95 hover:bg-white border border-white/40 w-full max-w-sm px-4 py-3 sm:px-6 sm:py-4 rounded-2xl text-gray-800 transition-all shadow-xl hover:shadow-2xl hover:scale-105"
  >
    <div className="flex items-center gap-2 text-xs text-gray-500 font-medium uppercase tracking-widest">
      <span className="text-yellow-500">★★★★★</span>
      <span>Metropolis Japan · Feb 2026</span>
    </div>
    <p className="text-xs sm:text-sm font-semibold text-gray-900 text-center leading-snug">
      "How a Swedish Coffee Ritual Is Helping Professionals Feel Less Alone"
    </p>
    <span className="text-xs text-purple-600 font-semibold group-hover:underline">
      Read the full article →
    </span>
  </a>
</motion.div>

   
{/* 
<motion.div
  initial={{ opacity: 0, y: 20 }}
  animate={{ opacity: 1, y: 0 }}
  transition={{ duration: 0.8, delay: 0.25 }}
  className="mb-6"
>
  <p className="text-white/70 text-xs uppercase tracking-widest font-semibold mb-2">
    ★ Metropolis Japan · Feb 2026
  </p>
  <p className="text-white text-lg sm:text-xl font-light italic">
    "You come to make friends. That's it, simple as that."
  </p>
</motion.div>
*/}

    {/* Email signup - MOBILE OPTIMIZED */}
    {/*  Or join as a founding member */}
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay: 0.6 }}
      className="mb-8 sm:mb-10 lg:mb-12 px-4"
    >
      <EmailSignup
        variant="hero"
        title=""
        subtitle="Or start your journey to becoming remarkable" 
        placeholder="Enter your email address"
        buttonText="Start Becoming Remarkable"
        source="hero-happiness"
        metadata={{
          signup_page: '/',
          signup_section: 'hero-secondary',
          page_type: 'homepage',
          campaign_source: new URLSearchParams(window.location.search).get('utm_source') || 'direct',
          campaign_medium: new URLSearchParams(window.location.search).get('utm_medium') || 'homepage',
          campaign_campaign: new URLSearchParams(window.location.search).get('utm_campaign') || 'hero-signup',
          viewport_width: window.innerWidth,
          viewport_height: window.innerHeight,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          referrer: document.referrer || 'direct'
        }}
        className="max-w-lg mx-auto"
      />
    </motion.div>

    {/* Harvard study quote - MOBILE OPTIMIZED */}
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay: 0.7 }}
      className="flex justify-center mb-6 sm:mb-8"
    >
      <div className="max-w-3xl mx-auto text-center px-4">
        <p className="text-sm sm:text-base lg:text-lg leading-relaxed opacity-90">
          Harvard's 85-year study found that remarkable people share one thing: 
          <strong> quality relationships</strong>. Not wealth. Not status. Not raw talent. 
          The people who move through life with depth, curiosity, and resilience — 
          they all learned to connect. That's what we're building here.
        </p>
        <p className="text-xs text-white/60 italic mt-2">
          Harvard Study of Adult Development (1938–2023)
        </p>
      </div>
    </motion.div>

    {/* Bottom badges - MOBILE OPTIMIZED */}
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay: 1.2 }}
      className="mb-8 px-4"
    >
      <div className="flex flex-col sm:flex-row justify-center items-center space-y-2 sm:space-y-0 sm:space-x-4 md:space-x-6 lg:space-x-8 text-xs sm:text-sm opacity-70">
        <div className="flex items-center">
          <span className="mr-2">🚫</span>
          <span>Not networking</span>
        </div>
        <div className="flex items-center">
          <span className="mr-2">✨</span>
          <span>Making you remarkable</span>
        </div>
        <div className="flex items-center">
          <span className="mr-2">☕</span>
          <span>Real conversation over real coffee</span>
</div>
      </div>
    </motion.div>
   

  </div>
</section>

{/* Press Quote - Metropolis Japan */}
      <section className="py-8 md:py-10 bg-gray-900">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <p className="text-yellow-400 text-xs uppercase tracking-widest font-semibold mb-4">
            ★ In The Press
          </p>
          <blockquote className="text-white text-base sm:text-lg md:text-xl font-light italic leading-relaxed mb-4">
            "How a Swedish Coffee Ritual Is Helping Professionals Feel Less Alone"
          </blockquote>
            <a
            href="https://metropolisjapan.com/fika-in-tokyo-swedish-coffee-ritual/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-yellow-400 hover:text-yellow-300 text-xs sm:text-sm font-semibold transition-colors"
          >
            Metropolis Japan · February 2026 →
          </a>
        </div>
      </section>


{/* NEW: Who is on ConnectCo Section - CITY AGNOSTIC */}
{/* NEW: Who is on ConnectCo Section - MOBILE-FIRST OPTIMIZED */}
<section className="py-12 sm:py-16 md:py-20 bg-gradient-to-br from-purple-50 via-blue-50 to-pink-50">
  <div className="max-w-5xl mx-auto px-4 sm:px-6">
    
    {/* Header - Mobile optimized text sizes */}
    <div className="text-center mb-8 sm:mb-10 md:mb-12">
      <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 mb-3 sm:mb-4">
        Who Becomes Remarkable at ConnectCo?
      </h2>
      <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto px-2">
        People who understand that remarkable isn't born — it's built, one honest conversation at a time
      </p>
    </div>

    {/* White card - Progressive padding */}
    <div className="bg-white rounded-xl sm:rounded-2xl p-6 sm:p-8 md:p-10 lg:p-12 shadow-lg">
      
      {/* Main description - Mobile-optimized prose */}
      <div className="prose prose-sm sm:prose-base max-w-none mb-6 sm:mb-8">
        <p className="text-gray-700 leading-relaxed mb-4 sm:mb-6">
          Open-minded, culturally curious people who believe the best version of themselves 
          shows up in real conversation — not on a résumé. Most have lived in multiple countries, 
          speak multiple languages, or are building lives that blend cultures. You'll find them 
          exploring hidden neighborhood gems, debating the perfect coffee roast, or sharing the kind 
          of honest stories that only come out when you slow down.
        </p>
        
         {/*
        <p className="text-gray-700 leading-relaxed mb-4 sm:mb-6">
          Some just arrived in a new city seeking authentic connection. Others have lived there for years 
          but crave deeper friendships. They value presence over productivity, conversations over 
          small talk, and showing up consistently over performing perfectly.
        </p>
         */}

        <p className="text-gray-700 leading-relaxed">
          From founders building in stealth mode to designers, educators, engineers, filmmakers, 
          and consultants — ConnectCo brings together people who understand that 
          <strong> remarkable people make each other more remarkable</strong>. That's not a slogan. 
          It's what happens when you put curious, honest people in a room and give them permission 
          to go deeper than the weather.
        </p>
      </div>

      {/* Key attributes grid - Mobile: 1 col, Tablet: 2 cols, Desktop: 3 cols */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 pt-6 border-t border-gray-200">
        
        <div className="flex items-start">
          <div className="text-xl sm:text-2xl mr-2 sm:mr-3 flex-shrink-0">🌍</div>
          <div className="min-w-0">
            <h4 className="font-semibold text-gray-800 text-sm mb-1">Globally Minded</h4>
            <p className="text-xs text-gray-600">Lived in 2+ countries or multilingual</p>
          </div>
        </div>

        <div className="flex items-start">
          <div className="text-xl sm:text-2xl mr-2 sm:mr-3 flex-shrink-0">💭</div>
          <div className="min-w-0">
            <h4 className="font-semibold text-gray-800 text-sm mb-1">Depth Seekers</h4>
            <p className="text-xs text-gray-600">Quality friendships over quantity</p>
          </div>
        </div>

        <div className="flex items-start">
          <div className="text-xl sm:text-2xl mr-2 sm:mr-3 flex-shrink-0">🎨</div>
          <div className="min-w-0">
            <h4 className="font-semibold text-gray-800 text-sm mb-1">Makers & Creators</h4>
            <p className="text-xs text-gray-600">Building, designing, or teaching</p>
          </div>
        </div>

        <div className="flex items-start">
          <div className="text-xl sm:text-2xl mr-2 sm:mr-3 flex-shrink-0">☕</div>
          <div className="min-w-0">
            <h4 className="font-semibold text-gray-800 text-sm mb-1">Slow Living</h4>
            <p className="text-xs text-gray-600">Values presence over hustle culture</p>
          </div>
        </div>

        <div className="flex items-start">
          <div className="text-xl sm:text-2xl mr-2 sm:mr-3 flex-shrink-0">🤝</div>
          <div className="min-w-0">
            <h4 className="font-semibold text-gray-800 text-sm mb-1">Anti-Networking</h4>
            <p className="text-xs text-gray-600">Friendships, not transactions</p>
          </div>
        </div>

        <div className="flex items-start">
          <div className="text-xl sm:text-2xl mr-2 sm:mr-3 flex-shrink-0">🗺️</div>
          <div className="min-w-0">
            <h4 className="font-semibold text-gray-800 text-sm mb-1">City Explorers</h4>
            <p className="text-xs text-gray-600">New arrivals or long-term residents</p>
          </div>
        </div>

      </div>

      {/* Active cities badge - Mobile optimized */}
      <div className="mt-6 pt-6 border-t border-gray-200">
        <p className="text-xs sm:text-sm text-gray-500 text-center mb-3">
          Currently building communities in:
        </p>
        <div className="flex flex-wrap justify-center gap-2">
          <span className="px-2.5 sm:px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
            🇯🇵 Tokyo
          </span>
          <span className="px-2.5 sm:px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
            🇯🇵 Osaka
          </span>
          <span className="px-2.5 sm:px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
            🇵🇹 Lisbon
          </span>
          <span className="px-2.5 sm:px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
            🇵🇹 Porto
          </span>
          <span className="px-2.5 sm:px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-medium">
            + More cities soon
          </span>
        </div>
      </div>

      {/* CTA - Mobile optimized */}
      <div className="mt-6 sm:mt-8 text-center">
        <p className="text-sm sm:text-base text-gray-600 mb-3 sm:mb-4 px-4">
          Ready to become a little more remarkable?
        </p>
        <a 
          href="/events"
          className="inline-flex items-center justify-center bg-gradient-to-r from-purple-600 to-blue-600 text-white px-5 sm:px-6 py-2.5 sm:py-3 rounded-lg text-sm sm:text-base font-medium hover:from-purple-700 hover:to-blue-700 hover:scale-105 transition-all touch-manipulation"
        >
          Find Your First Fika
          <ArrowRightIcon className="w-4 h-4 ml-2" />
        </a>
      </div>

    </div>

  </div>
</section>




{/* NEW SECTION: More Ways to Connect - MOBILE-FIRST RESPONSIVE */}
<section id="more-ways-to-connect" className="py-12 sm:py-16 md:py-20 bg-white">
  <div className="max-w-6xl mx-auto px-4 sm:px-6">
    
    <div className="text-center mb-8 sm:mb-10 lg:mb-12">
      <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 mb-3 sm:mb-4">
        More Ways to Connect
      </h2>
      <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto px-4">
        Beyond our core Fika experiences, discover additional ways to build meaningful connections
      </p>
      <p className="text-sm text-gray-500 mt-4 italic max-w-2xl mx-auto">
        Inspired by Swedish Fika tradition I discovered in Stockholm, brought to Tokyo with love
      </p>
    </div>

    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
      
      {/* Speed Fika Connection - MOBILE OPTIMIZED */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5, delay: 0.1 }}
        className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl overflow-hidden hover:shadow-lg transition-all hover:scale-[1.02]"
      >
        {/* Image - RESPONSIVE HEIGHT */}
        <div className="h-40 sm:h-44 md:h-48 bg-gradient-to-br from-blue-500 to-purple-500 relative overflow-hidden">
          <img
            src={getImageUrl('/images/events/speed-fika-hero.jpg')}
            alt="Speed Fika Connection"
            className="w-full h-full object-cover object-center opacity-90"
            style={{ minHeight: '100%', minWidth: '100%' }}
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
          <div className="absolute inset-0 flex items-center justify-center text-white text-3xl sm:text-4xl" style={{display: 'none'}}>
            ⚡
          </div>
        </div>
        
        {/* Content - MOBILE OPTIMIZED */}
        <div className="p-4 sm:p-5 md:p-6">
          <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-2 sm:mb-3">Speed Fika Connection</h3>
          <p className="text-sm sm:text-base text-gray-600 mb-3 sm:mb-4 leading-relaxed">
            Structured questions that skip small talk and spark real conversations. Meet 8-12 people in one evening.
          </p>
          <button
            onClick={() => navigate('/what-is-speed-fika')}
            className="text-purple-600 font-medium hover:text-purple-700 transition-colors inline-flex items-center text-sm sm:text-base"
          >
            Learn more
            <ArrowRightIcon className="w-3 h-3 sm:w-4 sm:h-4 ml-1" />
          </button>
        </div>
      </motion.div>

      {/* Goal Accountability - MOBILE OPTIMIZED */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5, delay: 0.2 }}
        className="bg-gradient-to-br from-green-50 to-teal-50 rounded-xl overflow-hidden hover:shadow-lg transition-all hover:scale-[1.02]"
      >
        {/* Image - RESPONSIVE HEIGHT */}
        <div className="h-40 sm:h-44 md:h-48 bg-gradient-to-br from-green-500 to-teal-500 relative overflow-hidden">
          <img
            src={getImageUrl('/images/events/goal-accountability-hero.jpg')}
            alt="Goal Accountability Partners"
            className="w-full h-full object-cover object-center opacity-90"
            style={{ minHeight: '100%', minWidth: '100%' }}
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
          <div className="absolute inset-0 flex items-center justify-center text-white text-3xl sm:text-4xl" style={{display: 'none'}}>
            🎯
          </div>
        </div>
        
        {/* Content - MOBILE OPTIMIZED */}
        <div className="p-4 sm:p-5 md:p-6">
          <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-2 sm:mb-3">Goal Accountability</h3>
          <p className="text-sm sm:text-base text-gray-600 mb-3 sm:mb-4 leading-relaxed">
            Join 6-8 like-minded people pursuing the same goal with weekly check-ins and genuine support.
          </p>
          <button
            onClick={() => navigate('/goal-accountability-partners')}
            className="text-green-600 font-medium hover:text-green-700 transition-colors inline-flex items-center text-sm sm:text-base"
          >
            Learn more
            <ArrowRightIcon className="w-3 h-3 sm:w-4 sm:h-4 ml-1" />
          </button>
        </div>
      </motion.div>

      {/* Diverse Experiences - MOBILE OPTIMIZED */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5, delay: 0.3 }}
        className="bg-gradient-to-br from-orange-50 to-pink-50 rounded-xl overflow-hidden hover:shadow-lg transition-all hover:scale-[1.02] sm:col-span-2 md:col-span-1"
      >
        {/* Image - RESPONSIVE HEIGHT */}
        <div className="h-40 sm:h-44 md:h-48 bg-gradient-to-br from-orange-500 to-pink-500 relative overflow-hidden">
          <img
            src={getImageUrl('/images/events/diverse-experiences-hero.jpg')}
            alt="Diverse Experiences"
            className="w-full h-full object-cover object-center opacity-90"
            style={{ minHeight: '100%', minWidth: '100%' }}
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
          <div className="absolute inset-0 flex items-center justify-center text-white text-3xl sm:text-4xl" style={{display: 'none'}}>
            🌟
          </div>
        </div>
        
        {/* Content - MOBILE OPTIMIZED */}
        <div className="p-4 sm:p-5 md:p-6">
          <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-2 sm:mb-3">Diverse Experiences</h3>
          <p className="text-sm sm:text-base text-gray-600 mb-3 sm:mb-4 leading-relaxed">
            Curated dinners, brunches, walks, and cultural explorations that turn shared moments into lasting connections.
          </p>
          <button
            onClick={() => navigate('/events')}
            className="text-orange-600 font-medium hover:text-orange-700 transition-colors inline-flex items-center text-sm sm:text-base"
          >
            View all experiences
            <ArrowRightIcon className="w-3 h-3 sm:w-4 sm:h-4 ml-1" />
          </button>
        </div>
      </motion.div>

    </div>
  </div>
</section>

      {/* Event Photos */}
      {/* Community Photo Gallery Section */}
      <section id="community-photos" className="py-16 md:py-20 bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          
          <div className="text-center mb-8 sm:mb-10 lg:mb-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-3 sm:mb-4">
                Moments That Matter
              </h2>
              <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto px-4">
                Real connections, authentic smiles, lasting friendships captured through our community journey
              </p>
            </motion.div>
          </div>

          {/* Photo Grid Component */}
          <Suspense fallback={<LoadingSpinner />}>
            <CommunityPhotoGallery 
              featuredOnly={true}
              maxPhotos={8}
              showFilters={false}
              showSearch={false}
            />
          </Suspense>
          
          <div className="text-center mt-8 sm:mt-10">
            <button
              onClick={() => navigate('/photos')}
              className="inline-flex items-center bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 sm:px-8 py-3 rounded-lg font-medium hover:from-purple-700 hover:to-pink-700 hover:scale-105 transition-all text-sm sm:text-base"
            >
              <span className="hidden sm:inline">View Full Gallery</span>
              <span className="sm:hidden">View All Photos</span>
              <ArrowRightIcon className="w-4 h-4 ml-2" />
            </button>
          </div>
        </div>
      </section>


      {/* Curated Fika Spotlight Section */}
      <section id="curated-fika-spotlight" className="py-16 md:py-20 bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 relative overflow-hidden">
  <div className="absolute top-10 right-10 w-32 h-32 bg-purple-200/30 rounded-full blur-3xl"></div>
  <div className="absolute bottom-20 left-10 w-40 h-40 bg-amber-200/30 rounded-full blur-3xl"></div>
  
  <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6">
    <div className="text-center mb-8">
     
      <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4">
        Introducing Curated Fika
      </h2>
      
      <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto">
        AI-assisted matching creates perfect chemistry with just 3-4 people
      </p>
    </div>

    <div className="grid lg:grid-cols-2 gap-8 md:gap-12 items-center mb-12">
      {/* Left Column - Feature Box */}
      <div className="bg-white rounded-2xl p-8 shadow-lg">
        <div className="text-center mb-6">
          <div className="text-6xl mb-4">✨</div>
          <div className="text-2xl font-bold text-gray-800 mb-2">3-4 People Maximum</div>
          <div className="text-gray-600">90 minutes of meaningful conversation</div>
        </div>
        
        <div className="space-y-4">
          <div className="flex items-start">
            <div className="w-2 h-2 bg-purple-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
            <p className="text-gray-700 text-sm">Data-driven compatibility analysis</p>
          </div>
          <div className="flex items-start">
            <div className="w-2 h-2 bg-purple-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
            <p className="text-gray-700 text-sm">Premium venues</p>
          </div>
          <div className="flex items-start">
            <div className="w-2 h-2 bg-purple-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
            <p className="text-gray-700 text-sm">Maximum chemistry, zero crowd</p>
          </div>
        </div>
      </div>

      {/* Right Column - Description & CTA */}
      <div className="space-y-6">
        <div>
          <h3 className="text-2xl font-bold text-gray-800 mb-3">
            Beyond Small Talk
          </h3>
          <p className="text-gray-600 leading-relaxed mb-4">
            While Monthly Fika welcomes 40+ people and Signature Fika gathers 10-15 weekly, Curated Fika is for those seeking the deepest form of connection.
          </p>
        </div>

        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
          <div className="flex items-start mb-2">
            <div className="text-2xl mr-3">👤</div>
            <div>
              <p className="text-gray-700 text-sm italic">
                "Within 30 minutes we moved past small talk into real conversation. I left with two people I genuinely want to stay connected with."
              </p>
              <p className="text-xs text-gray-500 mt-2">Maria S., Beta Participant</p>
            </div>
          </div>
        </div>

        <div className="space-y-3">
          
           <a href="/curated-fika-signup"
            className="block w-full bg-gradient-to-r from-purple-600 to-amber-600 text-white text-center py-4 px-6 rounded-lg font-semibold hover:from-purple-700 hover:to-amber-700 transition-all shadow-lg"
          >
            Apply for Curated Fika 
          </a>
        </div>
      </div>
    </div>
  </div>
</section>


      {/* Deep Fika Spotlight Section */}
      <section id="deep-fika-spotlight" className="py-16 md:py-20 relative overflow-hidden">
        {/* Background image */}
        <div className="absolute inset-0">
          <img
            src={getImageUrl('/images/events/deep-fika-1.jpg')}
            alt="Deep Fika atmosphere"
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.parentElement.style.background = 'linear-gradient(to bottom right, #1c1917, #064e3b)';
            }}
          />
        </div>
        {/* Dark overlay for text readability */}
        <div className="absolute inset-0 bg-gradient-to-br from-stone-900/92 via-stone-900/85 to-emerald-950/92"></div>

        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <span className="inline-block bg-emerald-500/20 text-emerald-300 text-xs font-semibold px-4 py-1.5 rounded-full mb-5 uppercase tracking-widest">
              Introducing Deep Fika
            </span>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4">
              One conversation.<br />One person. Everything intentional.
            </h2>
            <p className="text-lg sm:text-xl text-stone-300 max-w-2xl mx-auto leading-relaxed">
              The most intimate format in the ConnectCo community. You apply, we match you with one person
              whose interests and way of being in a conversation genuinely aligns with yours.
            </p>
          </div>

          <div className="grid grid-cols-3 gap-4 sm:gap-6 mb-12 max-w-2xl mx-auto">
            {[
              { stat: '1', label: 'person, matched to you', icon: '👤' },
              { stat: '60', label: 'minutes, no agenda', icon: '☕' },
              { stat: '¥0', label: 'extra cost for members', icon: '✓' },
            ].map(function(item) {
              return (
                <div key={item.stat} className="bg-white/5 border border-white/10 rounded-2xl p-4 sm:p-6 text-center">
                  <div className="text-2xl sm:text-3xl mb-2">{item.icon}</div>
                  <div className="text-3xl sm:text-4xl font-bold text-emerald-300 mb-1">{item.stat}</div>
                  <div className="text-stone-400 text-xs sm:text-sm leading-tight">{item.label}</div>
                </div>
              );
            })}
          </div>

          <div className="grid lg:grid-cols-2 gap-8 items-start">

            <div className="space-y-5">
              <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
                <h3 className="text-white font-semibold text-lg mb-4">How the match works</h3>
                <div className="space-y-4">
                  {[
                    { step: '01', text: 'Fill out a short application about who you are and how you like to talk.' },
                    { step: '02', text: 'We read every application and find one person whose interests actually align with yours. Human review, not just an algorithm.' },
                    { step: '03', text: 'You each receive an introduction. From there, you pick a café, pick a time, and show up.' },
                  ].map(function(item) {
                    return (
                      <div key={item.step} className="flex items-start gap-4">
                        <div className="w-8 h-8 rounded-full bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center flex-shrink-0">
                          <span className="text-emerald-300 text-xs font-bold">{item.step}</span>
                        </div>
                        <p className="text-stone-300 text-sm leading-relaxed pt-1">{item.text}</p>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="space-y-3">
                {[
                  'Friendship-only — no networking, no dating, full stop',
                  'Mutual acceptance — both say yes before any contact is shared',
                  'Your café, your time, your pace',
                  'Included in all ConnectCo memberships at no extra cost',
                ].map(function(point) {
                  return (
                    <div key={point} className="flex items-start gap-3">
                      <CheckCircleIcon className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                      <span className="text-stone-300 text-sm">{point}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="bg-white/5 border border-emerald-500/30 rounded-2xl p-8 text-center">
              <div className="text-6xl mb-5">☕</div>
              <h3 className="text-2xl font-bold text-white mb-3">
                Ready to meet one person who gets it?
              </h3>
              <p className="text-stone-400 text-sm leading-relaxed mb-3">
                Deep Fika is included in all ConnectCo memberships. If you are an active member,
                your application takes about ten minutes.
              </p>
              <p className="text-stone-500 text-xs mb-8 italic">
                Not a member yet? Join ConnectCo and Deep Fika is included from day one.
              </p>
              <button
                onClick={() => navigate('/deep-fika')}
                className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 text-white py-4 px-6 rounded-xl font-semibold hover:from-emerald-700 hover:to-teal-700 transition-all shadow-lg mb-3"
              >
                Learn more about Deep Fika
              </button>
              <button
                onClick={() => navigate('/subscription')}
                className="w-full border border-stone-600 text-stone-300 py-3 px-6 rounded-xl text-sm font-medium hover:border-stone-400 hover:text-white transition-all"
              >
                Join ConnectCo
              </button>
              <p className="text-stone-600 text-xs mt-4">Matching takes 1 to 2 weeks · Active members only</p>
            </div>

          </div>
        </div>
      </section>


            {/* Accountability Member Access Section - Add after community CTA section */}
            <section id="member-access" className="py-16 bg-white border-t border-gray-100">
              <div className="max-w-4xl mx-auto px-6 text-center">
                <div className="mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">Already a Goal Accountability Member?</h2>
                  <p className="text-gray-600">Access your accountability dashboard and track your progress</p>
                </div>

                <div className="grid md:grid-cols-2 gap-8 max-w-2xl mx-auto">
                  {/* Existing Members */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl p-6 border border-purple-100"
                  >
                    <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                      <span className="text-2xl">🎯</span>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Member Dashboard</h3>
                    <p className="text-gray-600 text-sm mb-4">
                      Submit weekly check-ins, track progress, and connect with your accountability group
                    </p>
                    <div className="space-y-2">
                      <button
                        onClick={() => navigate('/member-login')}
                        className="w-full bg-purple-600 text-white py-2 px-4 rounded-lg hover:bg-purple-700 transition-colors font-medium"
                      >
                        Access Dashboard
                      </button>
                      <p className="text-xs text-gray-500">
                        Secure email link authentication
                      </p>
                    </div>
                  </motion.div>

                  {/* New  Accountability Members Applicants */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.1 }}
                    className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-6 border border-amber-100"
                  >
                    <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                      <span className="text-2xl">👥</span>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Join a Group</h3>
                    <p className="text-gray-600 text-sm mb-4">
                      Apply to join 6-8 like-minded people pursuing the same goal as you
                    </p>
                    <div className="space-y-2">
                      <button
                        onClick={() => navigate('/goal-accountability-partners')}
                        className="w-full bg-amber-600 text-white py-2 px-4 rounded-lg hover:bg-amber-700 transition-colors font-medium"
                      >
                        Apply Now
                      </button>
                      <p className="text-xs text-gray-500">
                        Free to join • No commitment required
                      </p>
                    </div>
                  </motion.div>
                </div>

                {/* Help Text */}
                <div className="mt-8 text-center">
                  <p className="text-sm text-gray-500 mb-2">
                    Need help accessing your account?
                  </p>
                  <div className="flex justify-center space-x-4 text-xs">
                    <a href="mailto:hello@connectco.app" className="text-purple-600 hover:underline">
                      Email Support
                    </a>
                    <span className="text-gray-300">•</span>
                    <a href="/goal-accountability-partners" className="text-purple-600 hover:underline">
                      About the Program
                    </a>
                  </div>
                </div>
              </div>
            </section>

            {/* Enhanced Popular Events Section */}
            <section id="events" ref={eventsRef} className="py-20 bg-white">
                    <div className="max-w-7xl mx-auto px-6">
                      <div className="text-center mb-16">
                        <h2 className="text-5xl font-bold mb-4 text-gray-800">Our Most Popular Experiences</h2>
                        <p className="text-xl text-gray-600">Get priority FREE access as a member and skip the waitlists</p>
                      </div>
            
                      <div className="grid md:grid-cols-2 gap-8">
                      {upcomingEvents.map((event, index) => {
                          const IconComponent = event.icon;
                          return (
                            <motion.div
                              key={event.id}
                              initial={{ opacity: 0, y: 20 }}
                              animate={eventsInView ? { opacity: 1, y: 0 } : {}}
                              transition={{ duration: 0.5, delay: index * 0.1 }}
                              className={`relative bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl hover:scale-102 hover:-translate-y-1 transition-all duration-300 ${
                                event.featured ? 'ring-2 ring-orange-200' : ''
                              }`}
                            >
                              {event.featured && (
                                <div className="absolute top-4 right-4 z-10 bg-orange-500 text-white px-3 py-1 rounded-full text-xs font-medium flex items-center">
                                  <FireIcon className="w-3 h-3 mr-1" />
                                  Featured
                                </div>
                              )}
                              
                              <div className={`bg-gradient-to-br ${event.color} p-6 text-white`}>
                                <div className="flex items-center justify-between mb-4">
                                  <IconComponent className="w-8 h-8" />
                                  <div className="bg-orange-500 px-3 py-1 rounded-full text-sm font-medium hover:scale-105 transition-transform">
                                    {event.waitlist} on waitlist
                                  </div>
                                </div>
                                <h3 className="text-xl font-semibold mb-2">{event.title}</h3>
                                <p className="opacity-90 mb-4">{event.description}</p>
                                
                                {/* Event tags */}
                                <div className="flex flex-wrap gap-2">
                                  {event.tags.map((tag, tagIndex) => (
                                    <span key={tagIndex} className="px-2 py-1 bg-white/20 rounded-full text-xs">
                                      {tag}
                                    </span>
                                  ))}
                                </div>
                              </div>
                              
                              <div className="p-6">
                                <div className="space-y-3 mb-6">
                                  <div className="flex justify-between items-center">
                                    <span className="text-gray-600 flex items-center">
                                      <CalendarDaysIcon className="w-4 h-4 mr-2" />
                                      Next Event:
                                    </span>
                                    <span className="font-semibold text-purple-600">{event.next}</span>
                                  </div>
                                  
                                  <div className="flex justify-between items-center">
                                    <span className="text-gray-600 flex items-center">
                                      <ClockIcon className="w-4 h-4 mr-2" />
                                      Time:
                                    </span>
                                    <span className="font-medium">{event.time}</span>
                                  </div>
                                  
                                  <div className="flex justify-between items-center">
                                    <span className="text-gray-600 flex items-center">
                                      <MapPinIcon className="w-4 h-4 mr-2" />
                                      Location:
                                    </span>
                                    <span className="font-medium text-right text-sm">{event.location}</span>
                                  </div>
                                  
                                  <div className="flex justify-between items-center">
                                    <span className="text-gray-600 flex items-center">
                                      <UsersIcon className="w-4 h-4 mr-2" />
                                      Capacity:
                                    </span>
                                    <span className="font-medium">{event.capacity} people</span>
                                  </div>
                                </div>
                                
                                {/* Single CTA button that navigates to event detail page */}
                                <button 
                                  onClick={() => handleEventNavigation(event)}
                                  className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white py-3 px-4 rounded-lg font-semibold hover:from-purple-700 hover:to-blue-700 hover:scale-105 transition-all"
                                  aria-label={`Get member priority access to ${event.title}`}
                                >
                                  Member Priority
                                </button>
                              </div>
                            </motion.div>
                          );
                        })}
                      </div>
                    </div>
            </section>

            {/* CRITICAL FIX: Enhanced Interactive Community Section with Working Polling */}
            <section id="interactive" ref={interactiveRef} className="py-20 bg-gradient-to-br from-gray-50 to-purple-50">
                        <div className="max-w-7xl mx-auto px-6">
                          <div className="text-center mb-16">
                            <h2 className="text-5xl font-bold mb-4 text-gray-800">Interactive Community</h2>
                            <p className="text-xl text-gray-600">Engage, connect, and shape our events together</p>
                          </div>
                
                          <div className="grid lg:grid-cols-2 gap-12 mb-16">
                            {/* CRITICAL FIX: Enhanced Community Poll with Real Database Integration */}
                            <motion.div
                              initial={{ opacity: 0, x: -20 }}
                              animate={interactiveInView ? { opacity: 1, x: 0 } : {}}
                              transition={{ duration: 0.5 }}
                              className="bg-white rounded-2xl shadow-lg p-8"
                            >
                              <h3 className="text-2xl font-semibold mb-6 text-gray-800">
                                {currentPoll?.title || "Help Us Plan: Next Special Event?"}
                              </h3>
                              
                              {/* Enhanced status indicators */}
                              <div className="mb-4 space-y-2">
                                {currentPoll ? (
                                  <div className="text-sm text-green-700 flex items-center">
                                    <CheckCircleIcon className="w-4 h-4 mr-1" />
                                    Live poll ({totalVotes} total votes)
                                  </div>
                                ) : (
                                  <div className="text-sm text-orange-700 flex items-center">
                                    ⚠️ Loading poll data...
                                  </div>
                                )}
                                
                                {pollLoading && (
                                  <div className="text-sm text-blue-700 flex items-center">
                                    🔄 Processing vote...
                                  </div>
                                )}
                                
                                {pollError && (
                                  <div className="text-sm text-red-700 flex items-center">
                                    ❌ {pollError}
                                    <button 
                                      onClick={() => window.location.reload()} 
                                      className="ml-2 text-blue-600 hover:underline"
                                      aria-label="Retry loading poll"
                                    >
                                      Retry
                                    </button>
                                  </div>
                                )}
                              </div>
                              
                              <div className="space-y-4">
                                {pollOptions.map(option => {
                                  const percentage = totalVotes > 0 ? (option.votes / totalVotes) * 100 : 0;
                                  const isSelected = userVote === option.id;
                                  
                                  return (
                                    <div key={`${option.id}-${pollRevision}`} className="relative">
                                      <button
                                        onClick={() => handlePollVote(option.id)}
                                        disabled={pollLoading}
                                        className={`w-full text-left p-4 rounded-lg border transition-all hover:border-purple-500 hover:scale-[1.02] ${
                                          isSelected 
                                            ? 'border-purple-500 bg-purple-50 ring-2 ring-purple-200' 
                                            : 'border-gray-200'
                                        } ${
                                          pollLoading 
                                            ? 'opacity-50 cursor-not-allowed' 
                                            : 'hover:shadow-md cursor-pointer'
                                        }`}
                                        aria-label={`Vote for ${option.text}${isSelected ? ' - Currently selected' : ''}`}
                                        aria-pressed={isSelected}
                                      >
                                        <div className="flex justify-between items-center mb-2">
                                          <div className="flex items-center">
                                            <span className="text-lg mr-3">{option.emoji}</span>
                                            <span className="font-medium">{option.text}</span>
                                            {isSelected && (
                                              <CheckCircleIcon className="w-4 h-4 ml-2 text-purple-600" />
                                            )}
                                          </div>
                                          <span className="text-sm text-gray-500 font-medium">
                                            {option.votes} vote{option.votes !== 1 ? 's' : ''}
                                          </span>
                                        </div>
                                        
                                        {/* Progress bar */}
                                        <div className="relative h-2 bg-gray-200 rounded-full overflow-hidden">
                                          <motion.div 
                                            key={`progress-${option.id}-${pollRevision}`} // 🔥 Force re-render
                                            className={`h-full rounded-full ${
                                              isSelected 
                                                ? 'bg-gradient-to-r from-purple-600 to-blue-600' 
                                                : 'bg-gradient-to-r from-purple-400 to-blue-400'
                                            }`}
                                            initial={{ width: 0 }}
                                            animate={{ width: `${percentage}%` }}
                                            transition={{ duration: 0.8, delay: 0.1 }}
                                          />
                                        </div>
                                        
                                        <div className="text-xs text-purple-600 mt-1 font-medium">
                                          {percentage.toFixed(1)}%
                                        </div>
                                      </button>
                                    </div>
                                  );
                                })}
                              </div>
                              
                              <div className="mt-6 text-center">
                                <p className="text-sm text-gray-500">
                                  {totalVotes} total vote{totalVotes !== 1 ? 's' : ''} • Poll closes Jan 31
                                </p>
                                
                                {userVote && (
                                  <motion.div
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg"
                                  >
                                    <CheckCircleIcon className="w-5 h-5 text-green-500 inline-block mr-2" />
                                    <span className="text-green-700 text-sm">
                                      Thanks for voting! You can change your vote anytime.
                                    </span>
                                  </motion.div>
                                )}
                              </div>
                            </motion.div>
                
                            {/* Enhanced Member Spotlight */}
                            <motion.div
                              initial={{ opacity: 0, x: 20 }}
                              animate={interactiveInView ? { opacity: 1, x: 0 } : {}}
                              transition={{ duration: 0.5, delay: 0.2 }}
                              className="bg-gradient-to-br from-purple-600 to-blue-600 text-white rounded-2xl p-8"
                            >
                              <h3 className="text-2xl font-semibold mb-6">Member Spotlight</h3>
                              
                              <div className="flex items-center mb-6">
                                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center text-2xl mr-4">
                                  👩‍💼
                                </div>
                                <div>
                                  <h4 className="text-xl font-semibold">Sarah L. </h4>
                                  <p className="text-purple-200">Professional • 🇺🇸 USA (NYC) → Japan</p>
                                </div>
                              </div>
                              
                              {/* Responsive testimonial text */}
                              <blockquote className="text-lg mb-6 italic leading-relaxed">
                                {showFullTestimonial ? (
                                  // Full testimonial with proper line breaks
                                  <span className="whitespace-pre-line">
                                    "Three months ago, I arrived in Japan for work and a fresh start with my partner. When that relationship ended, I found myself a bit lost — still learning the language, knowing next to no one, and feeling surprisingly isolated in a city as energetic and dense as Tokyo.
                
                That all changed when I attended my first ConnectCo event.
                
                From the moment I arrived at the event, I felt so welcomed and comfortable. It was refreshing to be in a space where the goal wasn't networking or bar crawls, but genuine connection with other people. 
                
                There's something special about sitting down at a table full of strangers and keeping an open mind and heart as you get to know them. Through this group, I've not only met people from all walks of life, I've also made some good friends that I almost certainly wouldn't have had the opportunity to meet otherwise.
                
                I've enjoyed these events so much that I'm now working to help the Founder, Mark grow this awesome community. I'm grateful to be able to give back to a group that has helped make this place feel a bit more like home."
                                  </span>
                                ) : (
                                  // Condensed version for initial view
                                  "When my relationship ended, I found myself isolated in Tokyo, still learning the language and knowing almost no one. That all changed when I attended my first Connect & Co. Meetup. From the moment I arrived, I felt welcomed and comfortable — it wasn't about networking, but genuine connection."
                                )}
                              </blockquote>
                              
                              <div className="grid grid-cols-2 gap-4 text-sm mb-6">
                                <div>
                                  <span className="text-purple-200">Member Since:</span>
                                  <br />May 2025
                                </div>
                                <div>
                                  <span className="text-purple-200">Role:</span>
                                  <br />Event Host
                                </div>
                                <div>
                                  <span className="text-purple-200">Events Attended:</span>
                                  <br />5+ events
                                </div>
                                <div>
                                  <span className="text-purple-200">Achievement:</span>
                                  <br />Community Builder
                                </div>
                              </div>
                
                              {/* Enhanced button with smooth transitions */}
                              <motion.button 
                                onClick={() => setShowFullTestimonial(!showFullTestimonial)}
                                className="bg-white text-purple-600 px-6 py-2 rounded-lg font-medium hover:bg-gray-100 hover:scale-105 transition-all"
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                aria-label={showFullTestimonial ? 'Show condensed version of testimonial' : 'Read full testimonial from Sarah'}
                                aria-expanded={showFullTestimonial}
                              >
                                {showFullTestimonial ? 'Show Less' : 'Read Full Story'}
                              </motion.button>
                            </motion.div>
                
                          </div>
                
                          {/* NEW HEALTH & WELLNESS */}
                          <div className="mt-16">
                            <Suspense fallback={<LoadingSpinner />}>
                              <WellnessIntegration />
                            </Suspense>
                          </div>
                
                          {/* NEW: Blog Stories from Our Community Section - Mobile-First Responsive */}
                          <section className="py-12 sm:py-16 lg:py-20 bg-white">
                            <div className="max-w-7xl mx-auto px-4 sm:px-6">
                              <div className="text-center mb-8 sm:mb-12 lg:mb-16">
                                <h3 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-3 sm:mb-4 text-gray-800 leading-tight">
                                  Stories from Our Community
                                </h3>
                                <p className="text-base sm:text-lg lg:text-xl text-gray-600 px-2 sm:px-0">
                                   Real stories about food, travel, growth, and finding your tribe
                                </p>
                              </div>
                
                              {/* Mobile: Stack vertically, Tablet: 2 cols, Desktop: 3 cols */}
                              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8 mb-8 sm:mb-10 lg:mb-12">
                                {/* Part 1: Origin Story - Mobile-First */}
                                <motion.article
                                  initial={{ opacity: 0, y: 20 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  transition={{ duration: 0.5, delay: 0.1 }}
                                  className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl sm:rounded-2xl p-4 sm:p-6 lg:p-8 cursor-pointer hover:shadow-lg sm:hover:shadow-xl active:scale-95 sm:hover:scale-105 sm:hover:-translate-y-2 transition-all duration-300 group touch-manipulation flex flex-col h-full"
                                  onClick={() => navigate('/blog/fika-coffee-meeting-story')}
                                >
                                  <div className="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 bg-gradient-to-br from-blue-500 to-purple-500 rounded-lg sm:rounded-xl flex items-center justify-center text-white text-xl sm:text-2xl lg:text-3xl mb-4 sm:mb-5 lg:mb-6 group-hover:scale-110 transition-transform">
                                    ☕
                                  </div>
                                  <div className="flex flex-col sm:flex-row sm:items-center mb-3 sm:mb-4 gap-2">
                                    <span className="inline-block px-2 sm:px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs sm:text-sm font-medium">
                                      Origin Story
                                    </span>
                                    <span className="inline-block px-2 sm:px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-xs sm:text-sm font-medium">
                                      Part 1
                                    </span>
                                  </div>
                                  <h4 className="text-lg sm:text-xl lg:text-xl font-bold text-gray-800 mb-2 sm:mb-3 group-hover:text-blue-600 transition-colors leading-tight">
                                    How One Coffee Conversation Sparked A Growing Community
                                  </h4>
                                  <p className="text-sm sm:text-base text-gray-600 mb-3 sm:mb-4 leading-relaxed line-clamp-3 sm:line-clamp-none flex-grow">
                                    How one intentional conversation over lattes ignited a community where authentic connections happen naturally.
                                  </p>
                                  
                                  {/* Mobile: Simplified meta info */}
                                  <div className="flex items-center justify-between text-xs sm:text-sm text-gray-500 mb-3 sm:mb-4">
                                    <div className="flex items-center">
                                      <ClockIcon className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                                      <span>7 min read</span>
                                    </div>
                                    <div className="hidden sm:flex items-center">
                                      <span className="mr-1">📍</span>
                                      <span>Tokyo</span>
                                    </div>
                                  </div>
                                  
                                  {/* Mobile: Simplified CTA - Aligned to bottom */}
                                  <div className="flex items-center text-blue-600 group-hover:text-blue-700 font-medium text-sm sm:text-base mt-auto">
                                    <span className="hidden sm:inline">Read the full story</span>
                                    <span className="sm:hidden">Read more</span>
                                    <ArrowRightIcon className="w-3 h-3 sm:w-4 sm:h-4 ml-1 sm:ml-2 group-hover:translate-x-1 transition-transform" />
                                  </div>
                                </motion.article>
                
                                {/* Part 2: Swedish Fika Connection - Mobile-First */}
                                <motion.article
                                  initial={{ opacity: 0, y: 20 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  transition={{ duration: 0.5, delay: 0.2 }}
                                  className="bg-gradient-to-br from-orange-50 to-pink-50 rounded-xl sm:rounded-2xl p-4 sm:p-6 lg:p-8 cursor-pointer hover:shadow-lg sm:hover:shadow-xl active:scale-95 sm:hover:scale-105 sm:hover:-translate-y-2 transition-all duration-300 group touch-manipulation flex flex-col h-full"
                                  onClick={() => navigate('/blog/swedish-fika-connection-story')}
                                >
                                  <div className="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 bg-gradient-to-br from-orange-500 to-pink-500 rounded-lg sm:rounded-xl flex items-center justify-center text-white text-xl sm:text-2xl lg:text-3xl mb-4 sm:mb-5 lg:mb-6 group-hover:scale-110 transition-transform">
                                    🇸🇪
                                  </div>
                                  <div className="flex flex-col sm:flex-row sm:items-center mb-3 sm:mb-4 gap-2">
                                    <span className="inline-block px-2 sm:px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-xs sm:text-sm font-medium">
                                      Cultural Insights
                                    </span>
                                    <span className="inline-block px-2 sm:px-3 py-1 bg-pink-100 text-pink-700 rounded-full text-xs sm:text-sm font-medium">
                                      Part 2
                                    </span>
                                  </div>
                                  <h4 className="text-lg sm:text-xl lg:text-xl font-bold text-gray-800 mb-2 sm:mb-3 group-hover:text-orange-600 transition-colors leading-tight">
                                    What a Swedish Fika Coffee Taught me About Real Connection
                                  </h4>
                                  <p className="text-sm sm:text-base text-gray-600 mb-3 sm:mb-4 leading-relaxed line-clamp-3 sm:line-clamp-none flex-grow">
                                    How a simple Swedish practice transformed the way I relate to people—starting with myself.
                                  </p>
                                  <div className="flex items-center justify-between text-xs sm:text-sm text-gray-500 mb-3 sm:mb-4">
                                    <div className="flex items-center">
                                      <ClockIcon className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                                      <span>5 min read</span>
                                    </div>
                                    <div className="hidden sm:flex items-center">
                                      <span className="mr-1">📍</span>
                                      <span>Stockholm → Tokyo</span>
                                    </div>
                                  </div>
                                  <div className="flex items-center text-orange-600 group-hover:text-orange-700 font-medium text-sm sm:text-base mt-auto">
                                    <span className="hidden sm:inline">Read the full story</span>
                                    <span className="sm:hidden">Read more</span>
                                    <ArrowRightIcon className="w-3 h-3 sm:w-4 sm:h-4 ml-1 sm:ml-2 group-hover:translate-x-1 transition-transform" />
                                  </div>
                                </motion.article>
                
                                {/* Yanaka Tokyo Article - Mobile-First, Full Width on Tablet */}
                                <motion.article
                                  initial={{ opacity: 0, y: 20 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  transition={{ duration: 0.5, delay: 0.3 }}
                                  className="bg-gradient-to-br from-amber-50 to-red-50 rounded-xl sm:rounded-2xl p-4 sm:p-6 lg:p-8 cursor-pointer hover:shadow-lg sm:hover:shadow-xl active:scale-95 sm:hover:scale-105 sm:hover:-translate-y-2 transition-all duration-300 group touch-manipulation sm:col-span-2 lg:col-span-1 flex flex-col h-full"
                                  onClick={() => navigate('/blog/yanaka-tokyo-authentic-neighborhood')}
                                >
                                  <div className="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 bg-gradient-to-br from-amber-500 to-red-500 rounded-lg sm:rounded-xl flex items-center justify-center text-white text-xl sm:text-2xl lg:text-3xl mb-4 sm:mb-5 lg:mb-6 group-hover:scale-110 transition-transform">
                                    🏮
                                  </div>
                                  <div className="flex flex-col sm:flex-row sm:items-center mb-3 sm:mb-4 gap-2">
                                    <span className="inline-block px-2 sm:px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-xs sm:text-sm font-medium">
                                      City Tips
                                    </span>
                                  </div>
                                  <h4 className="text-lg sm:text-xl lg:text-xl font-bold text-gray-800 mb-2 sm:mb-3 group-hover:text-amber-600 transition-colors leading-tight">
                                    A Perfect Day in Old Tokyo: Yanaka District
                                  </h4>
                                  <p className="text-sm sm:text-base text-gray-600 mb-3 sm:mb-4 leading-relaxed line-clamp-3 sm:line-clamp-none flex-grow">
                                    How I discovered Tokyo's most authentic neighborhood, and why it changed everything I thought I knew about the city.
                                  </p>
                                  <div className="flex items-center justify-between text-xs sm:text-sm text-gray-500 mb-3 sm:mb-4">
                                    <div className="flex items-center">
                                      <ClockIcon className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                                      <span>4 min read</span>
                                    </div>
                                    <div className="hidden sm:flex items-center">
                                      <span className="mr-1">📍</span>
                                      <span>Tokyo, Japan</span>
                                    </div>
                                  </div>
                                  <div className="flex items-center text-amber-600 group-hover:text-amber-700 font-medium text-sm sm:text-base mt-auto">
                                    <span className="hidden sm:inline">Read the full story</span>
                                    <span className="sm:hidden">Read more</span>
                                    <ArrowRightIcon className="w-3 h-3 sm:w-4 sm:h-4 ml-1 sm:ml-2 group-hover:translate-x-1 transition-transform" />
                                  </div>
                                </motion.article>
                              </div>
                
                              {/* View All Blog Link - Mobile-First */}
                              <div className="text-center">
                                <button 
                                  onClick={() => navigate('/blog')}
                                  className="inline-flex items-center bg-gradient-to-r from-purple-600 to-blue-600 text-white px-6 sm:px-8 py-3 rounded-lg font-medium hover:from-purple-700 hover:to-blue-700 active:scale-95 sm:hover:scale-105 transition-all touch-manipulation text-sm sm:text-base"
                                  aria-label="Explore all community stories and blog posts"
                                >
                                  <span className="hidden sm:inline">Explore All Stories</span>
                                  <span className="sm:hidden">More Stories</span>
                                  <ArrowRightIcon className="w-4 h-4 ml-2" />
                                </button>
                              </div>
                            </div>
                          </section>
                
                        </div>
            </section>

    </>
  );
};

function App() {

   // State to track GA initialization
   const [gaReady, setGAReady] = useState(false);

   
   // 11-25-25
// 🆕 ADD: Register service worker on app load
useEffect(() => {
  const registerServiceWorker = async () => {
    if ('serviceWorker' in navigator) {
      try {
        const registration = await navigator.serviceWorker.register('/sw.js', {
          scope: '/'
        });
        console.log('✅ Service Worker registered successfully:', registration);
      } catch (error) {
        console.error('❌ Service Worker registration failed:', error);
      }
    } else {
      console.log('⚠️ Service Workers not supported in this browser');
    }
  };
  registerServiceWorker();
}, []);

useEffect(() => {
  if (typeof window !== 'undefined') {
    window.pushNotificationService = pushNotificationService;
  }
}, []);

const [promptModalOpen, setPromptModalOpen] = useState(false);
const [currentPromptId, setCurrentPromptId] = useState(null);

// Check URL for prompt parameter on load
useEffect(() => {
  const urlParams = new URLSearchParams(window.location.search);
  const promptId = urlParams.get('prompt');
  
  if (promptId) {
    setCurrentPromptId(promptId);
    setPromptModalOpen(true);
    
    // Clean URL without reload
    window.history.replaceState({}, '', window.location.pathname);
  }
}, []);

// 🆕 NEW: Check for today's prompt when user visits site
useEffect(() => {
  checkForTodaysPrompt();
}, []);




const checkForTodaysPrompt = async () => {
  console.log('🚀 checkForTodaysPrompt STARTED');
  
  try {
    const today = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Tokyo' });
    console.log('📅 Today\'s date (JST):', today);

    // ✅ FIX: Order by scheduled_for, not created_at


    const { data: allPrompts, error } = await supabase
      .from('daily_prompts')
      .select('id, title, question, scheduled_for, status')
      .in('status', ['scheduled', 'pending', 'sent'])  // ✅ Accept multiple statuses
      .order('scheduled_for', { ascending: false });

    console.log('📊 Query result:', { allPrompts, error });

    if (error) {
      console.error('❌ Error fetching prompts:', error);
      return;
    }

    console.log('🔍 All prompts fetched:', allPrompts?.length || 0);
    
    // Debug each prompt date
    allPrompts?.forEach((p, i) => {
      const promptDate = new Date(p.scheduled_for).toLocaleDateString('en-CA', { timeZone: 'Asia/Tokyo' });
      console.log(`Prompt ${i}:`, {
        scheduled_for: p.scheduled_for,
        formatted: promptDate,
        matches: promptDate === today,
        title: p.title
      });
    });

    const prompt = allPrompts?.find(p => {
      const promptDate = new Date(p.scheduled_for).toLocaleDateString('en-CA', { timeZone: 'Asia/Tokyo' });
      return promptDate === today;
    });

    console.log('🎯 Found prompt for today:', prompt);

    if (!prompt) {
      console.log('❌ No prompt scheduled for today');
      return;
    }

    console.log('✅ Found today\'s prompt:', prompt);

    const userEmail = localStorage.getItem('connectco_checkout_email') 
      || localStorage.getItem('userEmail');
    
    console.log('👤 User email:', userEmail);

    if (userEmail) {
      const { data: existingResponse } = await supabase
        .from('prompt_responses')
        .select('id')
        .eq('prompt_id', prompt.id)
        .eq('email', userEmail)
        .maybeSingle();

      console.log('📝 Existing response:', existingResponse);

      if (existingResponse) {
        console.log('✅ User already responded to today\'s prompt');
        return;
      }
    }

    const dismissedDate = localStorage.getItem('prompt_dismissed_date');
    console.log('🚫 Dismissed date:', dismissedDate, 'Today:', today);
    
    if (dismissedDate === today) {
      console.log('⏭️ User dismissed today\'s prompt');
      return;
    }

    console.log('🔍 promptModalOpen state:', promptModalOpen);
    
    if (promptModalOpen) {
      console.log('ℹ️ Modal already open from notification');
      return;
    }

    console.log('🎯 All checks passed! Will show modal in 3 seconds');
    console.log('Setting currentPromptId to:', prompt.id);
    
    setTimeout(() => {
      console.log('⏰ Timer fired! Setting modal state...');
      setCurrentPromptId(prompt.id);
      setPromptModalOpen(true);
      console.log('After setState called');
    }, 3000);

  } catch (error) {
    console.error('❌ FATAL Error in checkForTodaysPrompt:', error);
    console.error('Stack trace:', error.stack);
  }
};


// END of 11-25-25
 


   // Initialize Google Analytics FIRST, before rendering components
   useEffect(() => {
     const initializeAnalytics = async () => {
       console.log('🔍 [DEBUG] Initializing Google Analytics...');
       
       try {
         const gaInitialized = await initGA();
         
         if (gaInitialized) {
           console.log('✅ Google Analytics initialized successfully');
           setGAReady(true);
         } else {
           console.log('ℹ️ Google Analytics not initialized (development mode or missing config)');
           setGAReady(false);
         }
       } catch (error) {
         console.error('❌ Failed to initialize Google Analytics:', error);
         setGAReady(false);
       }
     };
 
     initializeAnalytics();
   }, []);

  // ALL ORIGINAL BLOG DATA (COMPLETELY PRESERVED)
  const allBlogPosts = useMemo(() => [
    {
      id: 'steep-cafe-bar-guide-tokyo',
      slug: 'steep-cafe-bar-guide-tokyo',
      title: "The Problem With 4.3 Stars",
      category: "City Tips",
      readTime: "3 min",
      image: "steep-cafe-guide.jpg",
      excerpt: "Every cafe guide scores the coffee. Almost none of them score whether the room will let you stay. Why I built Steep, an independent cafe and bar guide covering twenty cities, around a metric called staying power.",
      author: "Mark Jerrick",
      authorAvatar: "mark-francisco.jpg",
      date: "July 31, 2026",
      tags: ["tokyo-cafes", "specialty-coffee", "cafe-guide", "coffee-app", "laptop-friendly-cafes", "city-guides", "staying-power", "solo-friendly", "coffee-culture", "fika-community"],
      featured: true,
      readingTime: 3,
      location: "Tokyo, Japan"
    },
     {
      id: 'you-dont-have-to-be-extraordinary-to-be-remarkable',
      slug: 'you-dont-have-to-be-extraordinary-to-be-remarkable',
      title: "You Don't Have to Be Extraordinary to Be Remarkable",
      category: "Personal Growth",
      readTime: "3 min",
      image: "you-dont-have-to-be-extraordinary-to-be-remarkable.jpg",
      excerpt: "Remarkable isn't born. It's built one honest conversation at a time. The science of showing up, slowing down, and becoming the kind of person others remember.",
      author: "Mark Jerrick",
      authorAvatar: "mark-francisco.jpg",
      date: "May 24, 2026",
      tags: ["how-to-be-remarkable", "personal-growth", "human-connection", "remarkable-people", "authentic-relationships", "community", "fika", "conversation-skills", "loneliness", "showing-up"],
      featured: false,
      readingTime: 3,
      location: "Tokyo, Japan"
    },
    {
      id: 'conversations-long-term-friends-stop-having',
      slug: 'conversations-long-term-friends-stop-having',
      title: "The Conversations Old Friends Stop Having",
      category: "Community",
      readTime: "3 min",
      image: "conversations-long-term-friends.jpg",
      excerpt: "Familiarity is comforting. Over time, it can also quietly narrow what two people ask each other. Here are the conversation topics long-term friends stop having, and how to bring them back.",
      author: "Mark Jerrick",
      authorAvatar: "mark-francisco.jpg",
      date: "May 9, 2026",
      tags: ["conversation-topics", "long-term-friendship", "deep-conversations", "reconnecting-with-friends", "meaningful-conversations", "conversation-starters", "friendship-advice", "adult-friendship"],
      featured: false,
      readingTime: 3,
      location: "Tokyo, Japan"
    },
    {
      id: 'everyone-you-love-was-once-a-stranger', // 🆕 NEW: Everyone You Love Was Once a Stranger - NEWEST POST
      slug: 'everyone-you-love-was-once-a-stranger',
      title: "Everyone You Love Was Once a Stranger. So Why Aren't You Talking to Them?",
      category: "Community",
      readTime: "3 min",
      image: "everyone-you-love-was-once-a-stranger.jpg",
      excerpt: "The science of small conversations, and why you're better at them than you think. How one low-stakes question at a communal table changed my life forever.",
      author: "Mark Jerrick",
      authorAvatar: "mark-francisco.jpg",
      date: "April 18, 2026",
      tags: ["talking-to-strangers", "making-friends", "social-connection", "adult-friendship", "conversation-tips", "social-anxiety", "spotlight-effect", "community-building", "authentic-connection", "fika", "tokyo-community", "friendship"],
      featured: false,
      readingTime: 3,
      location: "Global"
    },
     {
        id: 'hidden-cost-free-communities', 
        slug: 'hidden-cost-free-communities',
        title: "The Hidden Cost of Free Communities",
        category: "Community Building",
        readTime: "2 min",
        image: "hidden-cost-free-communities.jpg",
        excerpt: "When you build for free, you become invisible. What 12 months of building a community in Tokyo taught me about pricing, commitment, and showing up.",
        author: "Mark Jerrick",
        authorAvatar: "mark-francisco.jpg",
        date: "April 8, 2026",
        tags: ["community-building", "free-to-paid", "startup-lessons", "tokyo-community", "fika", "membership-pricing", "building-in-public", "expat-tokyo", "authentic-connection", "connectco", "entrepreneurship", "community-mistakes"],
        featured: false,
        readingTime: 3,
        location: "Tokyo, Japan"
      },
     {
        id: 'why-people-line-up-perfectly-japan', // 🆕 NEW: Japan Queuing Article - NEWEST POST
        slug: 'why-people-line-up-perfectly-japan',
        title: "Why People Line Up Perfectly in Japan (And What It Says About All of Us)",
        category: "Travel & Culture",
        readTime: "3 min",
        image: "japan-queuing-culture-trust.jpg",
        excerpt: "The answer isn't rules or conformity. After years watching queues form and dissolve across Japan, it's something far more interesting — and what it reveals about all of us.",
        author: "Mark Jerrick",
        authorAvatar: "mark-francisco.jpg",
        date: "March 17, 2026",
        tags: ["japan-queuing-culture", "japanese-social-behavior", "collective-trust-japan", "japan-etiquette", "tokyo-observations", "japanese-values", "why-japan-works", "japan-culture-shock", "authentic-japan", "living-in-tokyo", "japan-travel-insights", "shinjuku-station", "japanese-politeness"],
        featured: false,
        readingTime: 3,
        location: "Tokyo, Japan"
      },

          
      {
        id: 'sawara-chiba-day-trip-tokyo', // 🆕 NEW: Sawara Day Trip Article - NEWEST POST
        slug: 'sawara-chiba-day-trip-tokyo',
        title: "Two Hours from Tokyo, 200 Years Back in Time: A Perfect Day-Trip in Sawara",
        category: "Travel & Culture",
        readTime: "4 min",
        image: "sawara-chiba-canal-town.jpg",
        excerpt: "Sawara, Chiba's hidden Edo-period canal town, is just 120 minutes from Tokyo and almost entirely off the tourist trail. A half-day itinerary for travelers who want the real Japan.",
        author: "Mark Jerrick",
        authorAvatar: "mark-francisco.jpg",
        date: "March 22, 2026",
        tags: ["sawara-chiba", "day-trips-from-tokyo", "non-touristy-japan", "hidden-gems-tokyo", "edo-period-japan", "little-edo", "slow-travel-japan", "japan-off-the-beaten-path", "katori-city", "authentic-japan", "canal-town-japan", "japan-day-trip", "chiba-travel", "underrated-japan"],
        featured: false,
        readingTime: 4,
        location: "Katori, Chiba, Japan"
      },
   
     
       {
        id: 'why-you-instantly-click-with-people', // 🆕 NEW: Click Chemistry Article - NEWEST POST
        slug: 'why-you-instantly-click-with-people',
        title: "Why You Instantly Click With Some People (And Feel Nothing With Others)",
        category: "Community",
        readTime: "4 min",
        image: "why-you-instantly-click-with-people.jpg",
        excerpt: "It's not luck. It's not fate. It may be your brain running a very fast, very quiet calculation — and you never even notice it's happening.",
        author: "Mark Jerrick",
        authorAvatar: "mark-francisco.jpg",
        date: "February 22, 2026",
        tags: ["human-connection", "neuroscience-of-connection", "why-we-click", "chemistry-explained", "authentic-relationships", "social-bonding", "mirror-neurons", "nervous-system", "instant-connection", "friendship-science"],
        featured: false,
        readingTime: 4,
        location: "Tokyo, Japan"
      },
        {
        id: 'finding-great-restaurant-vacation', // 🆕 NEW: Finding Great Restaurants Article - NEWEST POST
        slug: 'finding-great-restaurant-vacation',
        title: "Skip the TripAdvisor Trap: How I Actually Find Great Restaurants on Vacation",
        category: "Food & Travel",
        readTime: "3 min",
        image: "finding-great-restaurant-vacation.jpg",
        excerpt: "After a lifetime of eating my way across the globe, here's the counterintuitive system that consistently leads me to the best food in any city — no algorithms required.",
        author: "Mark Jerrick",
        authorAvatar: "mark-francisco.jpg",
        date: "February 13, 2026",
        tags: ["finding-restaurants-abroad", "travel-food-tips", "authentic-dining", "avoid-tourist-traps", "culinary-travel", "local-food-guide", "eating-abroad", "food-travel", "restaurant-tips", "travel-advice", "solo-dining", "street-food", "local-cuisine"],
        featured: false,
        readingTime: 3,
        location: "Everywhere"
      },
    {
    id: 'friendship-fade-keeping-friends-close', // 🆕 NEW: Friendship Fade Article - NEWEST POST
    slug: 'friendship-fade-keeping-friends-close',
    title: "The Friendship Fade: Why Good Friends Drift Apart (And the Small Habits That Keep Them Close)",
    category: "Community",
    readTime: "3 min",
    image: "friendship-fade-keeping-friends-close.jpg",
    excerpt: "Most friendships don't end in dramatic fallouts—they fade through small acts of de-prioritization. Here's what research reveals about maintaining close friendships and the simple habits that actually work.",
    content: "Full blog post content would go here...",
    author: "Mark Jerrick",
    authorAvatar: "mark-francisco.jpg",
    date: "January 27, 2026",
    tags: ["friendship-maintenance", "keeping-friends-close", "adult-friendship", "friendship-fade", "maintaining-friendships", "friendship-habits", "long-distance-friendship", "social-connection", "relationship-maintenance", "meaningful-connection"],
    views: 0,
    likes: 0,
    featured: false,
    readingTime: 3,
    location: "Tokyo, Japan"
  },
     
    {
    id: 'minimal-michelin-ice-cream-taichung-taiwan', 
    slug: 'minimal-michelin-ice-cream-taichung-taiwan',
    title: "Inside the World's Only Michelin-Starred Ice Cream Shop: Minimal in Taichung",
    category: "Food & Travel",
    readTime: "3 min",
    image: "minimal-michelin-ice-cream-taichung-taiwan.jpg",
    excerpt: "An ice cream aficionado's first-hand experience at the world's first and only Michelin-starred ice cream shop in Taiwan. What makes it worth the pilgrimage.",
    content: "Full blog post content would go here...",
    author: "Mark Jerrick",
    authorAvatar: "mark-francisco.jpg",
    date: "January 16, 2026",
    tags: ["minimal-ice-cream", "taichung-taiwan", "michelin-star-dessert", "gourmet-ice-cream", "taiwan-food", "culinary-travel", "food-aficionado", "taiwanese-cuisine", "michelin-guide", "world-best-ice-cream", "taichung-travel", "dessert-destination", "food-pilgrimage"],
    views: 0,
    likes: 0,
    featured: false, 
    readingTime: 3,
    location: "Taichung, Taiwan"
  },

     {
    id: 'happiness-roi', // 🆕 NEW: Happiness ROI Article - NEWEST POST
    slug: 'happiness-roi',
    title: "The Happiness ROI: Why Small Daily Investments Pay Massive Dividends",
    category: "Personal Growth",
    readTime: "3 min",
    image: "happiness-roi.jpg",
    excerpt: "In today's world of stress, anxiety, and burnout, investing in happiness isn't optional—it's essential. Learn the simple daily habits that unlock your full potential.",
    content: "Full blog post content would go here...",
    author: "Mark Jerrick",
    authorAvatar: "mark-francisco.jpg",
    date: "December 30, 2025",
    tags: ["happiness", "daily-habits", "burnout-prevention", "stress-management", "loneliness", "mental-health", "personal-growth", "self-improvement", "work-life-balance", "authentic-living", "compound-habits", "wellbeing"],
    views: 0,
    likes: 0,
    featured: false, // 🔥 FEATURED: Core message about happiness investment
    readingTime: 3,
    location: "Tokyo, Japan"
  },
  
      {
      id: 'new-years-eve-celebrations-around-world', // 🆕 NEW: NYE Celebrations Article - NEWEST POST
      slug: 'new-years-eve-celebrations-around-world',
      title: "10 Unforgettable New Year's Eve Celebrations Around the World: A Traveler's Guide to Ringing in the New Year",
      category: "Travel & Culture",
      readTime: "3 min",
      image: "new-years-eve-celebrations-around-world.jpg",
      excerpt: "From Tokyo's temple bells to Sydney's harbor fireworks—discover the world's most spectacular New Year's Eve traditions. Your complete guide to authentic global celebrations.",
      content: "Full blog post content would go here...",
      author: "Mark Jerrick",
      authorAvatar: "mark-francisco.jpg",
      date: "December 31, 2025",
      tags: ["new-years-eve", "global-celebrations", "travel-guide", "cultural-traditions", "tokyo-new-year", "sydney-fireworks", "edinburgh-hogmanay", "times-square", "nye-around-world", "holiday-travel", "cultural-experiences", "travel-tips", "world-traditions", "nye-destinations", "authentic-travel"],
      views: 0,
      likes: 0,
      featured: false, // 🔥 FEATURED: Timely holiday content with strong SEO potential
      readingTime: 3,
      location: "Global"
    },
    
    {
    id: 'your-brain-on-fika-neuroscience', // 🆕 NEW: Brain on Fika - NEWEST POST
    slug: 'your-brain-on-fika-neuroscience',
    title: "Your Brain on Fika: The Neuroscience of Why Slow Coffee Actually Works",
    category: "Cultural Insights",
    readTime: "4 min",
    image: "your-brain-on-fika-neuroscience.jpg",
    excerpt: "Why does unhurried coffee feel different than networking? Neuroscience reveals what actually happens in your brain during fika—and why the Swedish practice of slow, intentional conversation rewires us for authentic connection.",
    content: "Full blog post content would go here...",
    author: "Mark Jerrick",
    authorAvatar: "mark-francisco.jpg",
    date: "December 24, 2025",
    tags: ["neuroscience-of-connection", "fika-tradition", "slow-coffee-culture", "brain-science", "oxytocin-bonding", "authentic-connection", "networking-alternatives", "social-neuroscience", "meaningful-conversation", "community-building", "hardwired-for-happiness", "polyvagal-theory"],
    views: 0,
    likes: 0,
    featured: false, // 🔥 FEATURED: Science-backed explanation of why fika works
    readingTime: 4,
    location: "Tokyo, Japan"
  },
      {
      id: 'tokyo-cafes-for-connection', 
      slug: 'tokyo-cafes-for-connection',
      title: "5 Tokyo Cafés Where Real Conversations Happen",
      category: "City Tips",
      readTime: "5 min",
      image: "tokyo-cafes-for-connection.jpg",
      excerpt: "Beyond the Instagram-worthy latte art: discovering Tokyo's cafés that understand coffee culture is about connection, not just caffeine.",
      content: "Full blog post content would go here...",
      author: "Mark Jerrick",
      authorAvatar: "mark-francisco.jpg",
      date: "December 9, 2025",
      tags: ["tokyo-cafes", "coffee-culture", "solo-travel", "community-spaces", "authentic-tokyo", "fika-inspired", "connection", "specialty-coffee", "tokyo-coffee-shops", "iki-espresso", "swell-coffee", "ater-tokyo", "streamers-coffee", "koffee-mameya-kakeru"],
      featured: false, 
      readingTime: 5,
      location: "Tokyo, Japan"
    },
      {
      id: 'vulnerability-by-way-of-self-introduction', // 🆕 NEW: Terry's Vulnerability Story - NEWEST POST
      slug: 'vulnerability-by-way-of-self-introduction',
      title: "Vulnerability By Way of Self-Introduction",
      category: "Member Stories",
      readTime: "5 min",
      image: "vulnerability-by-way-of-self-introduction.jpg",
      excerpt: "A powerful personal story of recovery, mental health, and finding community. How one member's journey through anxiety, divorce, and sobriety led to meaningful connection through Fika.",
      content: "Full blog post content would go here...",
      author: "Terry",
      authorAvatar: "terry-avatar.jpg",
      date: "November 30, 2025",
      tags: ["mental-health", "vulnerability", "recovery-journey", "anxiety-treatment", "sobriety", "personal-growth", "community-support", "fika-community", "therapy", "self-improvement", "depression-recovery", "authentic-connection", "weekly-rituals", "healing-journey"],
      views: 0,
      likes: 0,
      featured: false,
      readingTime: 5,
      location: "Tokyo, Japan"
    },
       {
      id: 'micro-joy-practice-noticing-small-things', // 🆕 NEW: Micro-Joy Practice Article - NEWEST POST
      slug: 'micro-joy-practice-noticing-small-things',
      title: "The Micro-Joy Practice: How Noticing Small Things Changed My Entire Day",
      category: "Personal Growth",
      readTime: "4 min",
      image: "micro-joy-practice-noticing-small-things.jpg",
      excerpt: "What a perfect avocado taught me about happiness - and why we're all looking for joy in the wrong places. A practical guide to finding daily happiness through micro-joys.",
      content: "Full blog post content would go here...",
      author: "Mark Jerrick",
      authorAvatar: "mark-francisco.jpg",
      date: "November 22, 2025",
      tags: ["micro-joys", "daily-happiness", "mindfulness-practice", "gratitude", "savoring-moments", "finding-happiness", "mental-wellbeing", "positive-psychology", "happiness-habits", "present-moment-awareness", "simple-living", "personal-growth"],
      views: 0,
      likes: 0,
      featured: false, // 🔥 FEATURED: Practical, universal appeal, highly actionable
      readingTime: 4,
      location: "Global"
    },
    
        {
      id: 'friendship-recession-making-friends-after-30', // 🆕 NEW: Friendship Recession Article - NEWEST POST
      slug: 'friendship-recession-making-friends-after-30',
      title: "The Friendship Recession: Why Making Friends at 30+ Feels Impossible (And What Actually Works)",
      category: "Community",
      readTime: "4 min",
      image: "friendship-recession-making-friends-after-30.jpg",
      excerpt: "Research shows Americans have lost 60% of their close friendships over 30 years. Here's why adult friendship is harder than ever—and the evidence-based strategies that actually work.",
      content: "Full blog post content would go here...",
      author: "Mark Jerrick",
      authorAvatar: "mark-francisco.jpg",
      date: "November 21, 2025",
      tags: ["friendship-recession", "making-friends-after-30", "adult-friendship", "loneliness-epidemic", "social-connection", "building-friendships", "friendship-statistics", "social-isolation", "community-building", "meaningful-connection", "tokyo-community", "research-backed"],
      views: 0,
      likes: 0,
      featured: false, 
      readingTime: 4,
      location: "Tokyo, Japan"
    },
    {
      id: 'tegestologist-cocktail-bars-japan', 
      slug: 'tegestologist-cocktail-bars-japan',
      title: "The Tegestologist: A Journey Through Bars and Coasters",
      category: "Member Stories",
      readTime: "7 min",
      image: "tegestologist-cocktail-bars-japan.jpg",
      excerpt: "Discovering Japanese cocktail bar culture through the lens of a coaster collector. How intimate bar experiences became a gateway to authentic cultural connection and friendship in Japan.",
      content: "Full blog post content would go here...",
      author: "Pedro Noel",
      authorAvatar: "pedro-avatar.jpg",
      date: "October 17, 2025",
      tags: ["tegestologist", "cocktail-bars-japan", "coaster-collecting", "japanese-bar-culture", "solo-travel-japan", "cultural-immersion", "old-fashioned", "japanese-hospitality", "hiroshima", "kyoto-bars", "tokyo-cocktails", "bar-top-note", "omotenashi", "sake-cocktails", "pedro-stories"],
      views: 0,
      likes: 0,
      featured: false,
      readingTime: 7,
      location: "Japan"
    },
    {
      id: 'san-sebastian-pintxos-crawl',
      slug: 'san-sebastian-pintxos-crawl',
      title: "When the World's Best Food Hides in Plain Sight: San Sebastián's Pintxos Crawl",
      category: "Food & Travel",
      readTime: "5 min",
      image: "san-sebastian-pintxos-crawl.jpg",
      excerpt: "Discovering San Sebastián's pintxos culture—where Michelin-starred excellence meets democratic three-bite perfection in the bars of Parte Vieja.",
      content: "Full blog post content would go here...",
      author: "Mark Jerrick",
      authorAvatar: "mark-francisco.jpg",
      date: "October 4, 2025",
      tags: ["san-sebastian", "pintxos", "basque-country", "food-travel", "spain", "solo-dining", "culinary-travel", "bar-culture", "anthony-bourdain", "food-culture", "txikiteo", "bar-nestor", "la-vina-cheesecake", "parte-vieja", "gastronomic-travel"],
      views: 0,
      likes: 0,
      featured: false,
      readingTime: 5,
      location: "San Sebastián, Spain"
    },
  
    {
      id: 'lighter-suitcase-open-heart', // 🆕 NEW: Myriam's travel story
      slug: 'lighter-suitcase-open-heart',
      title: "A Lighter Suitcase and an Open Heart",
      category: "Member Stories",
      readTime: "2 min",
      image: "lighter-suitcase-open-heart.jpg",
      excerpt: "A personal reflection on solo travel, minimalism, and authentic connection. How traveling light—physically and emotionally—creates space for meaningful relationships and cultural immersion.",
      content: "Full blog post content would go here...",
      author: "Myriam F",
      authorAvatar: "myriam-avatar.jpg",
      date: "October 3, 2025",
      tags: ["minimalist-travel", "solo-travel", "authentic-connection", "cultural-immersion", "travel-light", "personal-growth", "drawing", "identity", "ego-and-connection", "meaningful-relationships", "travel-philosophy", "nomad-life"],
      views: 0,
      likes: 0,
      featured: false,
      readingTime: 2,
      location: "Global"
    },

    {
      id: 'tokyo-friendship-building-guide', // 🆕 NEW: Tokyo Friendship Building Guide - NEWEST POST
      slug: 'tokyo-friendship-building-guide',
      title: "Top 11 Ways to Build Real Friendships in Tokyo Beyond Networking",
      category: "Origin Story",
      readTime: "7 min",
      image: "tokyo-friendship-community-building-guide.jpg",
      excerpt: "How to find your tribe in one of the world's most fascinating yet socially complex cities. A comprehensive guide to building authentic connections in Tokyo beyond traditional networking.",
      content: "Full blog post content would go here...",
      author: "Mark Jerrick",
      authorAvatar: "mark-francisco.jpg",
      date: "September 16, 2025",
      tags: ["tokyo-friendship", "making-friends-tokyo", "tokyo-community", "expat-friends-tokyo", "networking-tokyo", "tokyo-social-life", "authentic-connections", "tokyo-expat-guide", "building-relationships-japan", "tokyo-social-tips", "japanese-culture", "community-building", "fika-tradition", "slow-coffee-culture", "tokyo-networking-alternatives"],
      views: 0,
      likes: 0,
      featured: false, // 🔥 FEATURED: This is a comprehensive, actionable guide
      readingTime: 7,
      location: "Tokyo, Japan"
    },
    {
      id: 'discovering-community-one-fika-at-a-time', // 🆕 NEW: Pedro's Fika Discovery Story - NEWEST POST
      slug: 'discovering-community-one-fika-at-a-time',
      title: "Discovering Community, One Fika at a Time",
      category: "Member Stories",
      readTime: "4 min",
      image: "discovering-community-one-fika-at-a-time.jpg",
      excerpt: "A personal journey of finding authentic connection through Swedish Fika tradition after moving to Japan. How one expat discovered meaningful connections through Connect & Co's monthly gatherings.",
      content: "Full blog post content would go here...",
      author: "Pedro Noel",
      authorAvatar: "pedro-avatar.jpg",
      date: "September 3, 2025",
      tags: ["fika-discovery", "expat-community-japan", "tokyo-fika", "member-stories", "community-building", "social-connection", "friendship-building", "international-community", "authentic-networking", "swedish-fika-tradition", "little-darling-coffee", "monthly-fika"],
      views: 0,
      likes: 0,
      featured: false, // 🔥 FEATURED: This is a compelling personal story that should be highlighted
      readingTime: 4,
      location: "Japan → Tokyo Community"
    },
    {
      id: 'fika-newbie-to-community-regular', // 🆕 NEW: Fika Newbie to Regular Article - NEWEST POST
      slug: 'fika-newbie-to-community-regular',
      title: "From Fika Newbie to Community Regular: The Simple Next Steps Most People Miss",
      category: "Community",
      readTime: "3 min",
      image: "fika-newbie-to-community-regular.jpg",
      excerpt: "A 48-hour playbook for turning a great chat into real community. Learn the simple follow-up strategies that transform one-time fika encounters into lasting friendships.",
      content: "Full blog post content would go here...",
      author: "Mark Jerrick",
      authorAvatar: "mark-francisco.jpg",
      date: "August 11, 2025",
      tags: ["fika-follow-up", "community-building", "community-tips", "making-friends", "community-engagement", "social-connection", "friendship-building", "tokyo-community", "authentic-networking", "post-event-follow-up"],
      views: 0,
      likes: 0,
      featured: false, // 🔥 FEATURED: This is practical, actionable content that should be highlighted
      readingTime: 3,
      location: "Tokyo Community"
    },
    
    {
      id: 'solo-travel-dinner-lisbon', // 
      slug: 'solo-travel-dinner-lisbon',
      title: "The Dinner for One That Changed How I See the World (And Myself)",
      category: "Solo Traveling",
      readTime: "5 min",
      image: "solo-travel-dinner-lisbon.jpg",
      excerpt: "What eating alone in a Lisbon restaurant taught me about the difference between loneliness and solitude—and why solo travel might be the most honest mirror you'll ever encounter.",
      content: "Full blog post content would go here...",
      author: "Mark Jerrick",
      authorAvatar: "mark-francisco.jpg",
      date: "July 25, 2025",
      tags: ["solo-travel", "eating-alone", "lisbon", "loneliness-vs-solitude", "travel-insights", "self-discovery", "authentic-connection", "travel-philosophy", "solo-dining", "personal-growth"],
      views: 0,
      likes: 0,
      featured: false,
      readingTime: 5,
      location: "Lisbon, Portugal"
    },
    {
      id: 'global-greetings-guide', // 🆕 NEW: Global Greetings Article
      slug: 'global-greetings-guide',
      title: "Say Hello Like a Local: A Traveler's Guide to Global Greetings",
      category: "Travel & Culture",
      readTime: "4 min",
      image: "global-greetings-guide.jpg",
      excerpt: "Master the art of greeting locals around the world with this comprehensive guide to cultural customs and etiquette across diverse cultures and communities.",
      content: "Full blog post content would go here...",
      author: "Mark Jerrick",
      authorAvatar: "mark-francisco.jpg",
      date: "July 22, 2025",
      tags: ["global-greetings", "travel-etiquette", "cultural-customs", "international-travel", "greeting-customs", "travel-guide", "cultural-awareness", "travel-tips", "world-cultures", "respectful-travel"],
      views: 0,
      likes: 0,
      featured: false,
      readingTime: 4,
      location: "Global"
    },
    {
      id: 'energy-draining-patterns',
      slug: 'energy-draining-patterns',
      title: "6 Patterns That Can Drain Your Energy (And How to Navigate Them)",
      category: "Networking",
      readTime: "5 min",
      image: "energy-draining-patterns.jpg",
      excerpt: "Whether you're building community at home or abroad, some personality types can drain your energy and sabotage your happiness. Learn to recognize and navigate them.",
      content: "Full blog post content would go here...",
      author: "Mark Jerrick",
      authorAvatar: "mark-francisco.jpg",
      date: "July 19, 2025",
      tags: ["energy-vampires", "toxic-relationships", "networking", "community-building", "personal-boundaries", "relationship-advice", "social-skills", "authentic-connections", "international-community", "friendship-tips"],
      views: 2847,
      likes: 89,
      featured: false,
      readingTime: 5,
      location: "From someone who stays long enough to matter"
    },
     
    {
      id: 'koffee-mameya-review-tokyo',
      slug: 'koffee-mameya-review-tokyo',
      title: "Koffee Mameya Review: Tokyo's Most Exclusive Coffee Experience",
      category: "City Tips",
      readTime: "3 min",
      image: "koffee-mameya-review-tokyo.jpg",
      excerpt: "An in-depth review of Tokyo's premium coffee tasting experience at Koffee Mameya - from reservation-only tastings to coffee cocktails that redefine what a café can be",
      content: "Full blog post content would go here...",
      author: "Mark Jerrick",
      authorAvatar: "mark-francisco.jpg",
      date: "July 16, 2025",
      tags: ["koffee-mameya", "tokyo-coffee", "coffee-tasting", "tokyo-cafes", "specialty-coffee", "coffee-review", "premium-coffee-experience", "tokyo-food-guide", "coffee-cocktails"],
      views: 234,
      likes: 42,
      featured: false,
      readingTime: 5,
      location: "Tokyo, Japan"
    },
    {
      id: 'american-cultural-faux-pas',
      slug: 'american-cultural-faux-pas',
      title: "When Being Friendly Backfires: An American's Guide to Cultural Faux Pas (And How to Adapt)",
      category: "Travel & Culture",
      readTime: "4 min",
      image: "american-cultural-faux-pas.jpg",
      excerpt: "What years of travel taught me about American habits that can seem rude abroad—and how to adapt without losing your authenticity",
      content: "Full blog post content would go here...",
      author: "Mark Jerrick",
      authorAvatar: "mark-francisco.jpg",
      date: "July 4, 2025",
      tags: ["american-habits-abroad", "cultural-faux-pas", "travel-etiquette", "cultural-differences", "international-travel", "cross-cultural-communication", "travel-insights", "cultural-adaptation", "authentic-travel"],
      views: 1247,
      likes: 89,
      featured: false,
      readingTime: 4,
      location: "Global"
    },
    {
      id: 'yanaka-tokyo-authentic-neighborhood',
      slug: 'yanaka-tokyo-authentic-neighborhood',
      title: "A Perfect Day in Old Tokyo: Yanaka District",
      category: "City Tips",
      readTime: "4 min",
      image: "yanaka-tokyo-story.jpg",
      excerpt: "How I discovered Tokyo's most authentic neighborhood, and why it changed everything I thought I knew about the city",
      content: "Full blog post content would go here...",
      author: "Mark Jerrick",
      authorAvatar: "mark-francisco.jpg",
      date: "April 5, 2026",
      tags: ["yanaka-tokyo", "authentic-tokyo", "traditional-japan", "tokyo-neighborhood", "travel-insights", "cultural-immersion", "sento-bathhouse", "authentic-travel"],
      views: 4823,
      likes: 631,
      featured: false,
      readingTime: 4,
      location: "Tokyo, Japan"
    },
    {
      id: 'swedish-fika-connection-story',
      slug: 'swedish-fika-connection-story',
      title: "What a Swedish Fika Coffee Taught me About Real Connection (Part 2)—And How I Brought it to Tokyo",
      category: "Cultural Insights",
      readTime: "5 min",
      image: "swedish-fika-connection-story.jpg",
      excerpt: " How a simple Swedish practice transformed the way I relate to people—starting with myself.",
      content: "Full blog post content would go here...",
      author: "Mark Jerrick",
      authorAvatar: "mark-francisco.jpg",
      date: "June 28, 2025",
      tags: ["swedish-fika", "meaningful-connections", "stockholm", "friendship", "cultural-insights", "connection", "relationships", "community-building"],
      views: 3156,
      likes: 427,
      featured: false,
      readingTime: 15,
      location: "Stockholm → Tokyo"
    },
    {
      id: 'fika-coffee-meeting-story',
      slug: 'fika-coffee-meeting-story',
      title: "How One Coffee Conversation Sparked A Growing Community (Part 1)",
      category: "Origin Story",
      readTime: "7 min",
      image: "fika-coffee-meeting-story.jpg",
      excerpt: "How one intentional conversation over lattes ignited a community where authentic connections happen naturally, one Fika at a time.",
      content: "Full blog post content would go here...",
      author: "Mark Jerrick",
      authorAvatar: "mark-francisco.jpg",
      date: "June 26, 2025",
      tags: ["origin-story", "fika", "community-building", "startup-story", "tokyo", "friendship"],
      views: 2847,
      likes: 312,
      featured: false,
      readingTime: 10,
      location: "Tokyo"
    }
  ], []);


  

  // ALL ORIGINAL CALLBACK FUNCTIONS (COMPLETELY PRESERVED)
  const getRelatedPosts = useCallback((currentPost) => {
    return allBlogPosts
      .filter(post => 
        post.id !== currentPost.id && 
        (post.category === currentPost.category || 
         post.tags.some(tag => currentPost.tags.includes(tag)))
      )
      .slice(0, 3);
  }, [allBlogPosts]);

  const handleNavigation = useCallback((page) => {
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  }, []);

  const handleBlogNavigation = useCallback(() => {
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  }, []);

 

  // ALL ORIGINAL EFFECTS (COMPLETELY PRESERVED)
  // NOTE: Organization schema removed from here — it lives statically in index.html
  // and should not be overwritten by the weaker defaultStructuredData on mount.
  // useEffect(() => {
  //   injectStructuredData(defaultStructuredData);
  // }, []);

  // ALL ORIGINAL COMPONENTS (COMPLETELY PRESERVED)
  const origContactPage = () => (
    <div className="min-h-screen bg-gray-50 py-20 pt-32">
      <div className="max-w-4xl mx-auto px-6 text-center">
        <h1 className="text-4xl font-bold text-gray-900 mb-8">Contact Us</h1>
        <p className="text-xl text-gray-600 mb-8">
          Get in touch with the Connect & Co team
        </p>
        <div className="bg-white rounded-xl shadow-sm p-8">
          <p className="text-gray-700 mb-4">
            <strong>Email:</strong> hello@connectco.app
          </p>
          <p className="text-gray-700 mb-4">
            <strong>Privacy Inquiries:</strong> privacy@connectco.app
          </p>
          {/*
          <p className="text-gray-700 mb-4">
            <strong>Legal Inquiries:</strong> legal@connectco.app
          </p>
          */}
          <p className="text-gray-700">
            <strong>Response Time:</strong> We respond to all inquiries within 24 hours
          </p>
        </div>
      </div>
    </div>
  );

  // ENHANCED: Router structure with SEO route management and lazy loading
  return (
    <ErrorBoundary>
  
        <Router>
          <div className="min-h-screen bg-white">
            <Header onNavigate={handleNavigation} onBlogNavigate={handleBlogNavigation} />
            
            <ScrollToTop>
              <AnalyticsWrapper>

                <Routes>
                  <Route
                    path="/"
                    element={
                      <SEORoute seoConfig={seoConfigs.home}>
                        <HomePage />
                      </SEORoute>
                    }
                  />
                  <Route
                    path="/blog"
                    element={
                      <SEORoute seoConfig={seoConfigs.blog}>
                        <Suspense fallback={<LoadingSpinner />}>
                          <BlogPage onNavigate={handleNavigation} allBlogPosts={allBlogPosts} />
                        </Suspense>
                      </SEORoute>
                    }
                  />
                  <Route
                    path="/blog/:slug"
                    element={
                      <SEORoute>
                        <Suspense fallback={<LoadingSpinner />}>
                          <BlogArticleRouter />
                        </Suspense>
                      </SEORoute>
                    }
                  />




                <Route
                  path="/wellness"
                  element={
                    
                    <SEORoute seoConfig={wellnessSEO}>
                      <Suspense fallback={<LoadingSpinner />}>
                        <WellnessIntegration />
                      </Suspense>
                    </SEORoute>
                  }
                />

                <Route
                  path="/conversation-starters"
                  element={
                    <SEORoute seoConfig={conversationStartersSEO}>
                      <Suspense fallback={<LoadingSpinner />}>
                        <ConversationStartersPage />
                      </Suspense>
                    </SEORoute>
                  }
                />

              

                <Route
                  path="/founder-conversations"
                  element={
                    <SEORoute seoConfig={{
                      title: "Founder Conversation Starters - Curated Fika Entrepreneurs | Connect & Co",
                      description: "Thoughtful questions for deeper peer founder connections at Founders 1-on-1 sessions. Skip the small talk and get tactical with fellow founders.",
                      keywords: "founder conversations, startup questions, entrepreneur peer connections, founder networking, curated fika entrepreneurs, founders 1-on-1",
                      url: "https://connectco.app/founder-conversations",
                      canonical: "https://connectco.app/founder-conversations",
                      robots: "index, follow",
                      openGraph: {
                        title: "Founder Conversation Starters - Get Tactical with Peer Founders",
                        description: "Thoughtful questions for deeper founder peer connections. From vision & strategy to vulnerability & wellbeing.",
                        url: "https://connectco.app/founder-conversations",
                        image: "https://connectco.app/founder-conversations-og.jpg",
                        type: "website"
                      }
                    }}>
                      <Suspense fallback={<LoadingSpinner />}>
                        <FounderConversationsPage />
                      </Suspense>
                    </SEORoute>
                  }
                />



                <Route
                  path="/experiences"
                  element={
                    <SEORoute seoConfig={{
                      title: "Experience Marketplace - Share Your Culture | Connect & Co",
                      description: "Turn your cultural insights into meaningful experiences. Host authentic cultural exchanges, local tours, and skill-sharing sessions for our global community.",
                      keywords: "experience marketplace, cultural exchange, host experiences",
                      url: "https://connectco.app/experiences"
                    }}>
                      <Suspense fallback={<LoadingSpinner />}>
                        <ExperienceMarketplacePage />
                      </Suspense>
                    </SEORoute>
                  }
                />

<Route
  path="/community-guidelines"
  element={
    <SEORoute seoConfig={communityGuidelinesSEO}>
      <Suspense fallback={<LoadingSpinner />}>
        <CommunityGuidelines />
      </Suspense>
    </SEORoute>
  }
/>

<Route
  path="/event-host-guide"
  element={
    <SEORoute seoConfig={{
      title: "Event Host Guide - Facilitating Authentic Fika Experiences | Connect & Co",
      description: "A comprehensive guide for Connect & Co event hosts. Learn to facilitate authentic Swedish Fika experiences, protect community values, and create safe spaces for genuine connection.",
      keywords: "event host guide, fika facilitation, community guidelines hosting, event facilitation, swedish fika hosting, community safety, authentic networking, event host training, tokyo community events, fika host guide, community event management, facilitating connection",
      url: "https://connectco.app/event-host-guide",
      canonical: "https://connectco.app/event-host-guide",
      robots: "index, follow",
      openGraph: {
        title: "Event Host Guide - Facilitating Authentic Fika Experiences",
        description: "Comprehensive guide for facilitating Swedish Fika events that build genuine friendships and protect community values.",
        url: "https://connectco.app/event-host-guide",
        image: "https://connectco.app/event-host-guide-og.jpg",
        type: "website",
        site_name: "Connect & Co"
      },
      twitter: {
        card: "summary_large_image",
        title: "Event Host Guide - Facilitating Authentic Fika Experiences",
        description: "Learn to facilitate Swedish Fika events that build genuine friendships and protect community values.",
        image: "https://connectco.app/event-host-guide-og.jpg"
      },
      additionalMeta: [
        { name: "author", content: "Connect & Co" },
        { name: "publisher", content: "Connect & Co" },
        { property: "article:author", content: "Connect & Co" },
        { property: "og:image:width", content: "1200" },
        { property: "og:image:height", content: "630" }
      ]
    }}>
      <Suspense fallback={<LoadingSpinner />}>
        <EventHostGuide />
      </Suspense>
    </SEORoute>
  }
/>

<Route
  path="/teams"
  element={
    <SEORoute seoConfig={{
      title: "Team Fika - Corporate Team Building That Actually Works | Connect & Co",
      description: "Swedish Fika tradition meets modern team building. Psychology-based conversations that build genuine trust. No trust falls, no awkward icebreakers.",
      keywords: "team building tokyo, corporate fika, team connection, employee engagement, offsite activities, team bonding japan",
      url: "https://connectco.app/teams",
      canonical: "https://connectco.app/teams"
    }}>
      <Suspense fallback={<LoadingSpinner />}>
        <TeamFikaPage />
      </Suspense>
    </SEORoute>
  }
/>

<Route
  path="/join"
  element={
    <SEORoute seoConfig={{
      title: "Join Connect & Co - Find Your People in Tokyo | Swedish Fika Community",
      description: "Join 500+ people finding genuine friendships through Swedish Fika in Tokyo. Weekly events, AI-matched connections, authentic community. ¥2,100/month. Cancel anytime.",
      keywords: "join tokyo community, make friends tokyo, swedish fika tokyo, tokyo expat community, find friends tokyo, tokyo social events, authentic connection tokyo",
      url: "https://connectco.app/join",
      canonical: "https://connectco.app/join"
    }}>
      <Suspense fallback={<LoadingSpinner />}>
        <SubscriptionPageAd />
      </Suspense>
    </SEORoute>
  }
/>

<Route
  path="/subscription"
  element={
    <SEORoute seoConfig={{
      title: "Founding Memberships - Where Strangers Become Friends in Tokyo, Osaka and anywhere else | Connect & Co",
      description: "Join ConnectCo's founding membership and build authentic friendships through Swedish Fika experiences in Tokyo. Lock in your rate forever—limited to first 100 members.",
      keywords: "swedish fika tokyo, authentic friendships tokyo, build authentic friendships, maintain friendships, community membership tokyo, expat community japan, fika experiences, international community tokyo, social connection tokyo",
      url: "https://connectco.app/subscription",
      canonical: "https://connectco.app/subscription"
    }}>
      <Suspense fallback={<LoadingSpinner />}>
        <SubscriptionPage />
      </Suspense>
    </SEORoute>
  }
/>

<Route
  path="/subscription/success"
  element={
    <SEORoute seoConfig={{
      title: "Subscription Successful - Welcome to Premium | Connect & Co",
      description: "Welcome to Connect & Co Premium! You now have priority access to all events.",
      robots: "noindex, nofollow"
    }}>
      <Suspense fallback={<LoadingSpinner />}>
        <SubscriptionSuccess />
      </Suspense>
    </SEORoute>
  }
/>

<Route
  path="/subscription/cancel"
  element={
    <SEORoute seoConfig={{
      title: "Subscription Cancelled - Connect & Co", 
      description: "Your subscription was cancelled. You can try again anytime.",
      robots: "noindex, nofollow"
    }}>
      <Suspense fallback={<LoadingSpinner />}>
        <SubscriptionCancel />
      </Suspense>
    </SEORoute>
  }
/>

                  <Route
                    path="/privacy"
                    element={
                      <SEORoute seoConfig={seoConfigs.privacy}>
                        <PrivacyPolicy />
                      </SEORoute>
                    }
                  />
                  <Route
                    path="/terms"
                    element={
                      <SEORoute seoConfig={seoConfigs.terms}>
                        <TermsOfService />
                      </SEORoute>
                    }
                  />
                  <Route
                    path="/cookies"
                    element={
                      <SEORoute>
                        <CookiesPolicy />
                      </SEORoute>
                    }
                  />

                  {/* 
                  <Route
                    path="/contact"
                    element={
                      <SEORoute seoConfig={seoConfigs.contact}>
                        <ContactPage />
                      </SEORoute>
                    }
                  />
                  */}

                  <Route
                    path="/contact"
                    element={
                      <SEORoute seoConfig={seoConfigs.contact}>
                        <ContactForm />
                      </SEORoute>
                    }
                  />
          
                  {/* 🔗 FIXED: Enhanced events routing with SEORoute */}
                  <Route
                    path="/events"
                    element={
                      <SEORoute seoConfig={seoConfigs.events}>
                        <Suspense fallback={<LoadingSpinner />}>
                          <EventsPage />
                        </Suspense>
                      </SEORoute>
                    }
                  />

                <Route
                  path="/events/city/:city"
                  element={
                    <SEORoute seoConfig={seoConfigs.events}>
                      <Suspense fallback={<LoadingSpinner />}>
                        <EventsPage />
                      </Suspense>
                    </SEORoute>
                  }
                />

             

  {/* ✅ FIXED: Event Cancellation Routes - Corrected path formatting and ordering */}
<Route
  path="/cancel-reservation/:token"
  element={
    <SEORoute seoConfig={{
      title: "Cancel Event Registration - Connect & Co",
      description: "Cancel your event registration easily and securely with email verification",
      keywords: "cancel event registration, event cancellation, unregister event",
      url: "https://connectco.app/cancel-reservation",
      canonical: "https://connectco.app/cancel-reservation",
      robots: "noindex, nofollow", // Keep cancellation pages private
      openGraph: {
        title: "Cancel Event Registration - Connect & Co",
        description: "Cancel your event registration easily and securely",
        url: "https://connectco.app/cancel-reservation",
        type: "website"
      }
    }}>
      <Suspense fallback={<LoadingSpinner />}>
        <EventCancellationPage />
      </Suspense>
    </SEORoute>
  }
/>

<Route
  path="/cancel-reservation"
  element={
    <SEORoute seoConfig={{
      title: "Cancel Event Registration - Connect & Co",
      description: "Cancel your event registration easily and securely",
      keywords: "cancel event registration, event cancellation, unregister event",
      url: "https://connectco.app/cancel-reservation",
      canonical: "https://connectco.app/cancel-reservation",
      robots: "noindex, nofollow"
    }}>
      <Suspense fallback={<LoadingSpinner />}>
        <EventCancellationPage />
      </Suspense>
    </SEORoute>
  }
/>

                    <Route
                      path="/curated-fika/cancel"
                      element={
                        <SEORoute seoConfig={{
                          title: "Cancel Curated Fika Attendance - Connect & Co",
                          description: "Cancel your Curated Fika session attendance securely",
                          keywords: "cancel curated fika, cancel attendance, curated fika cancellation",
                          url: "https://connectco.app/curated-fika/cancel",
                          canonical: "https://connectco.app/curated-fika/cancel",
                          robots: "noindex, nofollow"
                        }}>
                          <Suspense fallback={<LoadingSpinner />}>
                            <CuratedFikaCancellation />
                          </Suspense>
                        </SEORoute>
                      }
                    />

    {/*
    <Route
      path="/admin/newsletter"
      element={
        <SEORoute seoConfig={{
          title: "Newsletter Admin | Connect & Co",
          description: "Manage newsletter campaigns and analytics",
          robots: "noindex, nofollow" // Keep admin pages private
        }}>
          <Suspense fallback={<LoadingSpinner />}>
            <NewsletterAdmin />
          </Suspense>
        </SEORoute>
      }
    />
    */}

     <Route
        path="/admin/newsletter"
        element={
          <ProtectedAdminRoute>
            <SEORoute seoConfig={{
              title: "Newsletter Admin | Connect & Co",
              description: "Manage newsletter campaigns and analytics",
              robots: "noindex, nofollow"
            }}>
              <Suspense fallback={<LoadingSpinner />}>
                <NewsletterAdmin />
              </Suspense>
            </SEORoute>
          </ProtectedAdminRoute>
        }
      />

      <Route
        path="/kind-moments"
        element={
          <SEORoute seoConfig={seoConfigs.kindMoments}>
            <Suspense fallback={<LoadingSpinner />}>
              <KindMomentsPage />
            </Suspense>
          </SEORoute>
        }
      />

      <Route
        path="/community"
        element={
          <SEORoute seoConfig={seoConfigs.community}>
            <Suspense fallback={<LoadingSpinner />}>
              <FikaBoard />
            </Suspense>
          </SEORoute>
        }
      />
      <Route
        path="/community/post/:id"
        element={
          <SEORoute seoConfig={seoConfigs.communityPost}>
            <Suspense fallback={<LoadingSpinner />}>
              <FikaBoardPost />
            </Suspense>
          </SEORoute>
        }
      />

    


                <Route
                  path="/monthly-fika"
                  element={
                    <SEORoute seoConfig={{
                      title: "Monthly Fika - Swedish Coffee Tradition in Tokyo | Connect & Co",
                      description: "Join Tokyo's beloved monthly Swedish Fika tradition. Open to everyone, Sunday of each month. Experience authentic community over exceptional coffee in curated third wave cafés.",
                      keywords: "monthly fika, swedish coffee tradition, tokyo community events, sunday morning coffee, third wave coffee tokyo, swedish tradition japan, community gathering",
                      url: "https://connectco.app/monthly-fika",
                      canonical: "https://connectco.app/monthly-fika",
                      openGraph: {
                        title: "Monthly Fika - Swedish Coffee Tradition in Tokyo",
                        description: "Join Tokyo's beloved monthly Swedish Fika tradition. Open to everyone, authentic community over exceptional coffee.",
                        url: "https://connectco.app/monthly-fika",
                        image: "https://connectco.app/monthly-fika-og.jpg",
                        type: "website"
                      },
                      twitter: {
                        card: "summary_large_image", 
                        title: "Monthly Fika - Swedish Coffee Tradition in Tokyo",
                        description: "Join Tokyo's beloved monthly Swedish Fika tradition. Open to everyone, authentic community over exceptional coffee.",
                        image: "https://connectco.app/monthly-fika-og.jpg"
                      }
                    }}>
                      <Suspense fallback={<LoadingSpinner />}>
                        <MonthlyFikaPage />
                      </Suspense>
                    </SEORoute>
                  }
                />

                <Route
                  path="/monthly-fika-ad"
                  element={
                    <SEORoute seoConfig={{
                      title: "Hanami Fika Tokyo 2026 — Swedish Coffee Under the Cherry Blossoms | Connect & Co",
                      description: "Join us March 28 for a special Hanami Fika in Tokyo. Experience Swedish fika tradition beneath the cherry blossoms — intimate coffee, genuine conversation, 450+ members. First-timers welcome free.",
                      keywords: "hanami fika tokyo, cherry blossom coffee event tokyo, hanami 2026, swedish fika tokyo, fika hanami, tokyo spring events 2026, cherry blossom events tokyo march 2026, third wave coffee tokyo, tokyo community events, expat events tokyo spring",
                      url: "https://connectco.app/monthly-fika-ad",
                      canonical: "https://connectco.app/monthly-fika-ad",
                      openGraph: {
                        title: "Hanami Fika Tokyo — Coffee & Cherry Blossoms, March 28",
                        description: "Swedish fika meets Tokyo's cherry blossom season. Join 450+ members for intentional connection over exceptional coffee. First-timers join free.",
                        url: "https://connectco.app/monthly-fika-ad",
                        image: "https://connectco.app/monthly-fika-og.jpg",
                        type: "website"
                      },
                      twitter: {
                        card: "summary_large_image",
                        title: "Hanami Fika Tokyo — Coffee & Cherry Blossoms, March 28",
                        description: "Swedish fika meets Tokyo's cherry blossom season. Join 450+ members for intentional connection over exceptional coffee. First-timers join free.",
                        image: "https://connectco.app/monthly-fika-og.jpg"
                      }
                    }}>
                      <Suspense fallback={<LoadingSpinner />}>
                        <MonthlyFikaPageAd />
                      </Suspense>
                    </SEORoute>
                  }
                />

              

                <Route
                  path="/signature-fika"
                  element={
                    <SEORoute seoConfig={signatureFikaSEO}>
                      <Suspense fallback={<LoadingSpinner />}>
                        <SignatureFikaPage />
                      </Suspense>
                    </SEORoute>
                  }
                />

                <Route
                  path="/what-is-fika"
                  element={
                    <SEORoute seoConfig={{
                      title: "What is Fika? Understanding Sweden's Art of Slow Connection | Connect & Co",
                      description: "Discover the Swedish tradition of fika - more than just coffee, it's a practice of slowing down, connecting authentically, and building meaningful relationships.",
                      keywords: "what is fika, swedish fika tradition, fika meaning, swedish coffee culture, slow living, authentic connection",
                      url: "https://connectco.app/what-is-fika",
                      canonical: "https://connectco.app/what-is-fika"
                    }}>
                      <Suspense fallback={<LoadingSpinner />}>
                        <WhatIsFikaPage />
                      </Suspense>
                    </SEORoute>
                  }
                />

                <Route
                  path="/fika-at-33"
                  element={
                    <SEORoute seoConfig={{
                      title: "Fika at 33⅓ — A Vinyl Listening & Coffee Social | Connect & Co Tokyo",
                      description: "Where Swedish fika meets Tokyo's vinyl listening bar culture. An intimate afternoon of hand-drip coffee, analog sound, and unhurried conversation in Nakameguro.",
                      keywords: "vinyl listening bar tokyo, fika tokyo, nakameguro cafe, analog social, record cafe tokyo, specialty coffee social tokyo",
                      url: "https://connectco.app/fika-at-33",
                      canonical: "https://connectco.app/fika-at-33"
                    }}>
                      <Suspense fallback={<LoadingSpinner />}>
                        <FikaAt33Page />
                      </Suspense>
                    </SEORoute>
                  }
                />

                    <Route
                      path="/tokyo-expat-guide"
                      element={
                        <SEORoute seoConfig={seoConfigs.tokyoExpatGuide}>
                          <Suspense fallback={<LoadingSpinner />}>
                            <TokyoExpatGuide />
                          </Suspense>
                        </SEORoute>
                      }
                    />    
                    
                    {/* 🆕 NEW: Tokyo Survival Guide Route */}
                   

                     <Route
                        path="/tokyo-survival-guide"
                        element={
                          <SEORoute seoConfig={{
                            title: "Free Tokyo Survival Guide - Complete Expat Handbook 2024 | Connect & Co",
                            description: "Download the complete Tokyo survival guide for expats. 6 pages covering banking, housing, transportation, healthcare, and cultural integration. Written by expats, for expats.",
                            keywords: "tokyo survival guide, expat guide tokyo, tokyo living guide, tokyo expat handbook, free tokyo guide, tokyo moving guide, expat resources japan",
                            url: "https://connectco.app/tokyo-survival-guide",
                            canonical: "https://connectco.app/tokyo-survival-guide",
                            openGraph: {
                              title: "Free Tokyo Survival Guide - Complete Expat Handbook 2024",
                              description: "Download the complete Tokyo survival guide for expats. 6 pages covering banking, housing, transportation, healthcare, and cultural integration. Written by expats, for expats.",
                              url: "https://connectco.app/tokyo-survival-guide",
                              image: "https://connectco.app/tokyo-survival-guide-og.jpg",
                              type: "website"
                            },
                            twitter: {
                              card: "summary_large_image",
                              title: "Free Tokyo Survival Guide - Complete Expat Handbook 2024",
                              description: "Complete 6-page expat handbook covering banking, housing, transportation, healthcare, and cultural integration in Tokyo.",
                              image: "https://connectco.app/tokyo-survival-guide-og.jpg"
                            }
                          }}>
                            <Suspense fallback={<LoadingSpinner />}>
                              <TokyoSurvivalGuide />
                            </Suspense>
                          </SEORoute>
                        }
                      />   

                      <Route
                        path="/curated-fika"
                        element={
                          <SEORoute seoConfig={{
                            title: "Curated Fika - Ultra-Premium Intimate Swedish Coffee Experience | Connect & Co",
                            description: "Discover Curated Fika: Tokyo's most exclusive Swedish coffee tradition. 2-4 personally matched participants in premium venues. The pinnacle of authentic connection through intimate, thoughtfully curated gatherings.",
                            keywords: "curated fika, ultra premium fika, intimate networking tokyo, exclusive swedish coffee, personal matching service, premium small group meetup, authentic connection, high-end networking, boutique community events, selective networking, tokyo premium experiences, intimate conversation groups, exclusive coffee gatherings, luxury networking tokyo, personalized matchmaking, premium community curation, intimate swedish fika, high-quality connections, community building through dialogue, exclusive social experiences, premium venue experiences",
                            url: "https://connectco.app/curated-fika",
                            canonical: "https://connectco.app/curated-fika",
                            robots: "index, follow",
                            openGraph: {
                              title: "Curated Fika - Ultra-Premium Intimate Swedish Coffee Experience",
                              description: "Tokyo's most exclusive Swedish coffee tradition. 2-4 personally matched participants in premium venues for authentic, meaningful connections.",
                              url: "https://connectco.app/curated-fika",
                              image: "https://connectco.app/curated-fika-og.jpg",
                              type: "website",
                              site_name: "Connect & Co",
                              locale: "en_US"
                            },
                            twitter: {
                              card: "summary_large_image",
                              site: "@connectandco",
                              title: "Curated Fika - Ultra-Premium Intimate Swedish Coffee Experience",
                              description: "Tokyo's most exclusive Swedish coffee tradition. 2-4 personally matched participants in premium venues.",
                              image: "https://connectco.app/curated-fika-og.jpg"
                            },
                            additionalMeta: [
                              { name: "author", content: "Connect & Co" },
                              { name: "publisher", content: "Connect & Co" },
                              { property: "article:author", content: "Connect & Co" },
                              { property: "article:publisher", content: "https://connectco.app" },
                              { property: "og:image:width", content: "1200" },
                              { property: "og:image:height", content: "630" },
                              { property: "og:image:alt", content: "Curated Fika - Ultra-premium intimate Swedish coffee experience with personal matching" }
                            ]
                          }}>
                            <Suspense fallback={<LoadingSpinner />}>
                              <CuratedFikaPage />
                            </Suspense>
                          </SEORoute>
                        }
                      />

<Route
  path="/curated-fika/feedback"
  element={
    <SEORoute seoConfig={{
      title: "Curated Fika Feedback - Share Your Experience | Connect & Co",
      description: "Share your feedback about your Curated Fika experience. Help us improve our premium curated intimate Swedish coffee gatherings.",
      keywords: "curated fika feedback, session feedback, experience review, curated fika review",
      url: "https://connectco.app/curated-fika/feedback",
      canonical: "https://connectco.app/curated-fika/feedback",
      robots: "noindex, nofollow", // Keep feedback pages private
      openGraph: {
        title: "Curated Fika Feedback - Share Your Experience",
        description: "Share your feedback about your premium intimate Swedish coffee experience.",
        url: "https://connectco.app/curated-fika/feedback",
        image: "https://connectco.app/curated-fika-feedback-og.jpg",
        type: "website"
      }
    }}>
      <Suspense fallback={<LoadingSpinner />}>
        <CuratedFikaFeedback />
      </Suspense>
    </SEORoute>
  }
/>



<Route
  path="/curated-fika-entrepreneurs/feedback"
  element={
    <SEORoute seoConfig={{
      title: "Founders 1-on-1 Feedback - Share Your Experience | Connect & Co",
      description: "Share your feedback about your Founders 1-on-1 peer connection experience. Help us improve intimate founder conversations and peer matching.",
      keywords: "founders 1-on-1 feedback, founder peer feedback, startup founder connections, entrepreneur peer review",
      url: "https://connectco.app/curated-fika-entrepreneurs/feedback",
      canonical: "https://connectco.app/curated-fika-entrepreneurs/feedback",
      robots: "noindex, nofollow", // Keep feedback pages private
      openGraph: {
        title: "Founders 1-on-1 Feedback - Share Your Experience",
        description: "Share your feedback about your intimate founder peer connection experience.",
        url: "https://connectco.app/curated-fika-entrepreneurs/feedback",
        image: "https://connectco.app/founders-1on1-feedback-og.jpg",
        type: "website"
      }
    }}>
      <Suspense fallback={<LoadingSpinner />}>
        <CuratedFikaEntrepreneursFeedback />
      </Suspense>
    </SEORoute>
  }
/>

                      <Route
                        path="/curated-fika-signup"
                        element={
                          <SEORoute seoConfig={{
                            title: "Curated Fika Application - Premium Intimate Swedish Coffee Experience | Connect & Co",
                            description: "Apply for Curated Fika - exclusive 2-4 person Swedish coffee gatherings in Tokyo. Premium intimate networking with personally matched professionals. Premium members only.",
                            keywords: "curated fika application, premium fika tokyo, intimate networking, authentic friendships, swedish coffee experience, small group meetup, premium membership, tokyo networking, professional matching, authentic connections, swedish fika tradition, exclusive events, intimate gatherings",
                            url: "https://connectco.app/curated-fika-signup",
                            canonical: "https://connectco.app/curated-fika-signup",
                            robots: "index, follow",
                            openGraph: {
                              title: "Curated Fika Application - Premium Intimate Swedish Coffee Experience",
                              description: "Apply for exclusive 2-4 person Swedish coffee gatherings in Tokyo. Premium intimate networking with personally matched professionals.",
                              url: "https://connectco.app/curated-fika-signup",
                              image: "https://connectco.app/curated-fika-signup-og.jpg",
                              type: "website",
                              site_name: "Connect & Co",
                              locale: "en_US"
                            },
                            twitter: {
                              card: "summary_large_image",
                              site: "@connectandco",
                              title: "Curated Fika Application - Premium Intimate Swedish Coffee Experience",
                              description: "Apply for exclusive 2-4 person Swedish coffee gatherings in Tokyo. authentic friendsihps, Premium networking with personally matched professionals.",
                              image: "https://connectco.app/curated-fika-signup-og.jpg"
                            }
                          }}>
                            <Suspense fallback={<LoadingSpinner />}>
                              <CuratedFikaSignup />
                            </Suspense>
                          </SEORoute>
                        }
                      />

<Route
  path="/curated-fika/founders"
  element={
    <SEORoute
      seoConfig={{
        title: "Curated Fika for Founders & Startups | Private 1-on-1 Matches | Connect & Co",
        description:
          "Apply for the Founders & Startups track of Curated Fika — confidential, AI-assisted 1-on-1 matchmaking for founders, entrepreneurs, professionals, operators, and tech builders in Japan.",
        keywords:
          "founder fika tokyo, 1-on-1 founder matchmaking, private founder coffee, startup networking japan, confidential founder meetings, curated fika founders, tech entrepreneurs tokyo, high trust networking",
        url: "https://connectco.app/curated-fika/founders",
        canonical: "https://connectco.app/curated-fika/founders",
        robots: "index, follow",
        openGraph: {
          title: "Curated Fika for Founders & Startups | Private 1-on-1 Matches",
          description:
            "Exclusive, high-trust 1-on-1 fika sessions for founders, professionals, entreprenuers, & operators — AI-assisted matching, confidentiality first.",
          url: "https://connectco.app/curated-fika/founders",
          image: "https://connectco.app/curated-fika-founders-og.jpg",
          type: "website",
          site_name: "Connect & Co",
          locale: "en_US",
        },
        twitter: {
          card: "summary_large_image",
          site: "@connectandco",
          title: "Curated Fika for Founders & Startups | Private 1-on-1 Matches",
          description:
            "High-trust, confidential 1-on-1 fika for founders, entrepreneurs, and professionals in Japan. AI-assisted matching.",
          image: "https://connectco.app/curated-fika-founders-og.jpg",
        },
      }}
    >
      <Suspense fallback={<LoadingSpinner />}>
        <CuratedFikaEntrepreneursPage />
      </Suspense>
    </SEORoute>
  }
/>

<Route
  path="/curated-fika/founders-1on1/apply"
  element={
    <SEORoute seoConfig={{
      title: "Apply for Founders 1-on-1 | Curated Fika | Connect & Co",
      description: "Apply for exclusive 1-on-1 Curated Fika sessions with fellow founders and operators in Tokyo. Confidential, high-trust matchmaking for entrepreneurs building in Japan.",
      keywords: "founder application, 1-on-1 application, entrepreneur matching, startup networking tokyo, founder fika application, confidential founder meetings",
      url: "https://connectco.app/curated-fika/founders-1on1/apply",
      canonical: "https://connectco.app/curated-fika/founders-1on1/apply",
      robots: "index, follow",
      openGraph: {
        title: "Apply for Founders 1-on-1 | Curated Fika",
        description: "Apply for exclusive 1-on-1 sessions with fellow founders and operators. Confidential, high-trust matchmaking.",
        url: "https://connectco.app/curated-fika/founders-1on1/apply",
        image: "https://connectco.app/curated-fika-founders-apply-og.jpg",
        type: "website",
        site_name: "Connect & Co"
      },
      twitter: {
        card: "summary_large_image",
        title: "Apply for Founders 1-on-1 | Curated Fika",
        description: "Exclusive 1-on-1 sessions with fellow founders. Confidential, high-trust matchmaking.",
        image: "https://connectco.app/curated-fika-founders-apply-og.jpg"
      }
    }}>
      <Suspense fallback={<LoadingSpinner />}>
        <CuratedFikaEntrepreneursSignup />
      </Suspense>
    </SEORoute>
  }
/>


<Route
  path="/subscription/pay-per-event-success"
  element={
    <SEORoute seoConfig={{
      title: "Pass Purchase Successful - You're All Set! | Connect & Co",
      description: "Your event pass is ready to use! Choose from any ConnectCo event in Tokyo - Weekly Signature Fika, Curated Fika, Speed Fika, Happy Hours, and more. Valid for 90 days.",
      keywords: "event pass purchase, tokyo events, single event access, connect co pass, fika tokyo, community events tokyo, pay per event, event registration success",
      url: "https://connectco.app/subscription/pay-per-event-success",
      canonical: "https://connectco.app/subscription/pay-per-event-success",
      robots: "noindex, nofollow", // Success pages shouldn't be indexed
      openGraph: {
        title: "Pass Purchase Successful - You're All Set!",
        description: "Your event pass is ready! Choose from Weekly Signature Fika, Curated Fika, Speed Fika, and more. Valid for 90 days.",
        url: "https://connectco.app/subscription/pay-per-event-success",
        image: "https://connectco.app/event-pass-success-og.jpg",
        type: "website",
        site_name: "Connect & Co"
      },
      twitter: {
        card: "summary_large_image",
        title: "Pass Purchase Successful - You're All Set!",
        description: "Your event pass is ready! Choose from any ConnectCo event in Tokyo. Valid for 90 days.",
        image: "https://connectco.app/event-pass-success-og.jpg"
      }
    }}>
      <Suspense fallback={<LoadingSpinner />}>
        <PayPerEventSuccess />
      </Suspense>
    </SEORoute>
  }
/>

<Route
  path="/founders-fika"
  element={
    <SEORoute seoConfig={{
      title: "Founders Fika - Entrepreneur Peer Support Community | Connect & Co",
      description: "Join 6-8 entrepreneurs for structured peer support sessions. Monthly gatherings focused on real business challenges: fundraising, hiring, scaling, and mental health. Anti-performative, authentic support for founders building in Tokyo.",
      keywords: "founders fika, entrepreneur support tokyo, startup peer group, founder community, business mastermind, entrepreneur networking, startup support group, founder mental health, peer accountability entrepreneurs",
      url: "https://connectco.app/founders-fika",
      canonical: "https://connectco.app/founders-fika",
      robots: "index, follow",
      openGraph: {
        title: "Founders Fika - Entrepreneur Peer Support Community",
        description: "Join 6-8 entrepreneurs for structured peer support. Real business challenges, authentic community, monthly gatherings in Tokyo.",
        url: "https://connectco.app/founders-fika",
        image: "https://connectco.app/founders-fika-og.jpg",
        type: "website",
        site_name: "Connect & Co"
      },
      twitter: {
        card: "summary_large_image",
        title: "Founders Fika - Entrepreneur Peer Support Community",
        description: "6-8 entrepreneurs tackling real challenges together. Anti-performative, authentic support for builders.",
        image: "https://connectco.app/founders-fika-og.jpg"
      }
    }}>
      <Suspense fallback={<LoadingSpinner />}>
        <FoundersFikaPage />
      </Suspense>
    </SEORoute>
  }
/>





<Route
  path="/curated-fika/confirm"
  element={
    <SEORoute seoConfig={{
      title: "Confirm Your Curated Fika Attendance | Connect & Co",
      description: "Confirm your attendance for an exclusive Curated Fika session. Premium intimate Swedish coffee experience with personally matched professionals.",
      keywords: "curated fika confirmation, confirm attendance, premium fika session, swedish coffee experience, intimate networking",
      url: "https://connectco.app/curated-fika/confirm",
      canonical: "https://connectco.app/curated-fika/confirm",
      robots: "noindex, nofollow", // Keep confirmation pages private
      openGraph: {
        title: "Confirm Your Curated Fika Attendance",
        description: "Confirm your attendance for an exclusive Curated Fika session with personally matched professionals.",
        url: "https://connectco.app/curated-fika/confirm",
        image: "https://connectco.app/curated-fika-confirm-og.jpg",
        type: "website"
      },
      twitter: {
        card: "summary_large_image",
        title: "Confirm Your Curated Fika Attendance",
        description: "Exclusive intimate Swedish coffee experience with personally matched professionals.",
        image: "https://connectco.app/curated-fika-confirm-og.jpg"
      }
    }}>
      <Suspense fallback={<LoadingSpinner />}>
        <CuratedFikaConfirm />
      </Suspense>
    </SEORoute>
  }
/>

<Route
  path="/curated-fika-entrepreneurs/confirm"
  element={
    <SEORoute seoConfig={{
      title: "Confirm Your Founders 1-on-1 Session | Connect & Co",
      description: "Confirm your attendance for an exclusive Founders 1-on-1 session. Intimate founder-to-founder conversation with carefully matched entrepreneur.",
      keywords: "founders 1-on-1 confirmation, entrepreneur meeting, founder matching, startup networking, peer support",
      url: "https://connectco.app/curated-fika-entrepreneurs/confirm",
      canonical: "https://connectco.app/curated-fika-entrepreneurs/confirm",
      robots: "noindex, nofollow", // Keep confirmation pages private
      openGraph: {
        title: "Confirm Your Founders 1-on-1 Session",
        description: "Confirm your attendance for an exclusive 1-on-1 session with a carefully matched founder.",
        url: "https://connectco.app/curated-fika-entrepreneurs/confirm",
        image: "https://connectco.app/founders-1on1-confirm-og.jpg",
        type: "website"
      },
      twitter: {
        card: "summary_large_image",
        title: "Confirm Your Founders 1-on-1 Session",
        description: "Exclusive founder-to-founder conversation with carefully matched entrepreneur.",
        image: "https://connectco.app/founders-1on1-confirm-og.jpg"
      }
    }}>
      <Suspense fallback={<LoadingSpinner />}>
        <CuratedFikaEntrepreneursConfirm />
      </Suspense>
    </SEORoute>
  }
/>

<Route
  path="/join-instagram"
  element={
    <SEORoute seoConfig={{
      title: "Join Tokyo's Friendliest Community | ConnectCo",
      description: "Three ways to connect authentically in Tokyo: Monthly Fika (40+ people), Weekly Signature Fika, and Curated Fika (3-4 AI-matched). One membership, ¥2,100/month.",
      keywords: "tokyo community, fika tokyo, make friends tokyo, authentic connection, curated fika, weekly events tokyo, tokyo friendships, expat tokyo, digital nomad tokyo",
      url: "https://connectco.app/join-instagram",
      canonical: "https://connectco.app/join-instagram",
      openGraph: {
        title: "Join Tokyo's Friendliest Community | ConnectCo",
        description: "Three ways to connect authentically in Tokyo: Monthly Fika (40+ people), Weekly Signature Fika, and Curated Fika (3-4 AI-matched people). ¥2,100/month.",
        url: "https://connectco.app/join-instagram",
        image: "https://connectco.app/og-image.jpg",
        type: "website"
      },
      twitter: {
        card: "summary_large_image",
        title: "Join Tokyo's Friendliest Community | ConnectCo",
        description: "Three ways to connect authentically in Tokyo: Monthly Fika, Weekly Signature Fika, and Curated Fika. ¥2,100/month.",
        image: "https://connectco.app/og-image.jpg"
      }
    }}>
      <Suspense fallback={<LoadingSpinner />}>
        <JoinInstagramPage />
      </Suspense>
    </SEORoute>
  }
/>

<Route
  path="/meet-people-tokyo"
  element={
    <SEORoute seoConfig={seoConfigs.meetPeopleTokyo}>
      <Suspense fallback={<LoadingSpinner />}>
        <MeetPeopleTokyoPage />
      </Suspense>
    </SEORoute>
  }
/>

<Route
  path="/bangkok"
  element={
    <SEORoute seoConfig={bangkokSeoConfig}>
      <Suspense fallback={<LoadingSpinner />}>
        <BangkokPage />
      </Suspense>
    </SEORoute>
  }
/>
<Route
  path="/bangkok/success"
  element={
    <Suspense fallback={<LoadingSpinner />}>
      <BangkokSuccess />
    </Suspense>
  }
/>

<Route
  path="/deep-fika"
  element={
    <SEORoute seoConfig={deepFikaSeoConfig}>
      <Suspense fallback={<LoadingSpinner />}>
        <DeepFikaPage />
      </Suspense>
    </SEORoute>
  }
/>

<Route
  path="/deep-fika/apply"
  element={
    <SEORoute seoConfig={deepFikaSignupSeoConfig}>
      <Suspense fallback={<LoadingSpinner />}>
        <DeepFikaSignup />
      </Suspense>
    </SEORoute>
  }
/>

<Route
  path="/deep-fika/respond"
  element={<DeepFikaRespond />}
/>

<Route
  path="/deep-fika/feedback"
  element={<DeepFikaFeedback />}
/>

                    <Route path="/what-is-speed-fika" element={
                      <SEORoute seoConfig={seoConfigs.whatIsSpeedFika}>
                        <Suspense fallback={<LoadingSpinner />}>
                          <WhatIsSpeedFikaPage />
                        </Suspense>
                      </SEORoute>
                      } 
                    />


<Route
  path="/admin/email-blocking"
  element={
    <SEORoute seoConfig={{
      title: "Email Blocking Management | Connect & Co",
      description: "Admin dashboard for managing blocked emails and domains",
      robots: "noindex, nofollow"
    }}>
      <Suspense fallback={<LoadingSpinner />}>
        <EmailBlockingManagement />
      </Suspense>
    </SEORoute>
  }
/>

                  <Route
                    path="/goal-accountability-partners"
                    element={
                      <SEORoute seoConfig={{
                        title: "Goal Accountability Partners - Find Your Accountability Tribe | Transform Goals Into Success | Connect & Co",
                        description: "Join 6-8 like-minded people pursuing the same goal through authentic Swedish fika tradition. Turn solo struggles into shared success with weekly check-ins, expert matching, and proven accountability systems. Free 6-week program with global community support.",
                        keywords: "goal accountability partners, accountability tribe, goal achievement, personal development community support, fika tradition accountability, authentic accountability groups, goal achievement support system, accountability partner matching, habit formation support, goal tracking community, peer accountability, accountability buddy system, goal success coaching, monthly goal tracking, weekly accountability check-ins, accountability partner program, goal achievement strategy, habit accountability, personal growth accountability, professional development goals, fitness goal accountability, language learning accountability, reading challenge accountability, creative project accountability, entrepreneurial goal support, skill mastery accountability, academic study support, goal setting community, achievement tracking system, accountability group dynamics, goal completion support, sustainable habit formation, goal motivation system",
                        url: "https://connectco.app/goal-accountability-partners",
                        canonical: "https://connectco.app/goal-accountability-partners",
                        robots: "index, follow",
                        openGraph: {
                          title: "Goal Accountability Partners - Find Your Accountability Tribe | Transform Goals Into Success",
                          description: "Join 6-8 like-minded people pursuing the same goal through authentic Swedish fika tradition. Turn solo struggles into shared success with weekly check-ins and expert matching.",
                          url: "https://connectco.app/goal-accountability-partners",
                          image: "https://connectco.app/goal-accountability-partners-og.jpg",
                          type: "website",
                          site_name: "Connect & Co",
                          locale: "en_US"
                        },
                        twitter: {
                          card: "summary_large_image",
                          site: "@connectandco",
                          title: "Goal Accountability Partners - Find Your Accountability Tribe",
                          description: "Join authentic accountability groups in the Swedish fika tradition. Transform your goals into shared success with 6-8 like-minded people.",
                          image: "https://connectco.app/goal-accountability-partners-og.jpg"
                        },
                        additionalMeta: [
                          { name: "author", content: "Connect & Co" },
                          { name: "publisher", content: "Connect & Co" },
                          { name: "application-name", content: "Connect & Co Goal Accountability Partners" },
                          { name: "apple-mobile-web-app-title", content: "Goal Accountability Partners" },
                          { name: "theme-color", content: "#8b5cf6" },
                          { name: "format-detection", content: "telephone=no" },
                          { property: "article:author", content: "Connect & Co" },
                          { property: "article:publisher", content: "https://connectco.app" },
                          { property: "og:image:width", content: "1200" },
                          { property: "og:image:height", content: "630" },
                          { property: "og:image:alt", content: "Goal Accountability Partners - Swedish fika tradition meets goal achievement" }
                        ]
                      }}>
                        <Suspense fallback={<LoadingSpinner />}>
                          <GoalAccountabilityForm />
                        </Suspense>
                      </SEORoute>
                    }
                  />


<Route
  path="/about"
  element={
    <SEORoute seoConfig={{
      title: "About Us - Meet the Team Behind Connect & Co",
      description: "Learn about Connect & Co's mission to build meaningful connections worldwide. Meet our founder Mark Jerrick and discover our story of creating authentic community.",
      keywords: "about connect co, mark jerrick, community building, global network, founder story, team, mission, values",
      url: "https://connectco.app/about",
      canonical: "https://connectco.app/about",
      openGraph: {
        title: "About Us - Meet the Team Behind Connect & Co",
        description: "Learn about Connect & Co's mission to build meaningful connections worldwide. Meet our founder Mark Jerrick and discover our story.",
        url: "https://connectco.app/about",
        image: "https://connectco.app/about-og.jpg",
        type: "website"
      },
      twitter: {
        card: "summary_large_image",
        title: "About Us - Meet the Team Behind Connect & Co",
        description: "Meet Mark Jerrick and learn about our mission to build meaningful connections worldwide through authentic community.",
        image: "https://connectco.app/about-og.jpg"
      }
    }}>
      <Suspense fallback={<LoadingSpinner />}>
        <AboutPage />
      </Suspense>
    </SEORoute>
  }
/>


<Route
  path="/goal-accountability-faq"
  element={
    <SEORoute seoConfig={{
      title: "Goal Accountability Partners FAQ - Your Questions Answered | Connect & Co",
      description: "Get answers to all your questions about Goal Accountability Partners. Learn about our Swedish fika tradition, matching process, success rates, and how authentic accountability works.",
      keywords: "goal accountability FAQ, accountability partners questions, how accountability works, fika accountability, goal achievement questions, accountability group questions, goal matching process, accountability success rates, swedish fika tradition FAQ",
      url: "https://connectco.app/goal-accountability-faq",
      canonical: "https://connectco.app/goal-accountability-faq",
      robots: "index, follow",
      openGraph: {
        title: "Goal Accountability Partners FAQ - Your Questions Answered",
        description: "Get answers to all your questions about Goal Accountability Partners, Swedish fika tradition, matching process, and authentic accountability.",
        url: "https://connectco.app/goal-accountability-faq",
        image: "https://connectco.app/goal-accountability-faq-og.jpg",
        type: "website",
        site_name: "Connect & Co",
        locale: "en_US"
      },
      twitter: {
        card: "summary_large_image",
        site: "@connectandco",
        title: "Goal Accountability Partners FAQ - Your Questions Answered",
        description: "Everything you need to know about achieving goals through authentic accountability partnerships and Swedish fika tradition.",
        image: "https://connectco.app/goal-accountability-faq-og.jpg"
      }
    }}>
      <Suspense fallback={<LoadingSpinner />}>
        <GoalAccountabilityFAQ />
      </Suspense>
    </SEORoute>
  }
/>


                      {/*
                     <Route
                      path="/admin/goal-accountability"
                      element={
                        <SEORoute seoConfig={{
                          title: "Goal Accountability Admin | Connect & Co",
                          description: "Admin dashboard for managing goal accountability applications and groups",
                          robots: "noindex, nofollow" // Keep admin pages private
                        }}>
                          <Suspense fallback={<LoadingSpinner />}>
                            <GoalAccountabilityAdmin />
                          </Suspense>
                        </SEORoute>
                      }
                    />
                    */}

                    <Route
                      path="/admin/goal-accountability"
                      element={
                        <ProtectedAdminRoute>
                          <SEORoute seoConfig={{
                            title: "Goal Accountability Admin | Connect & Co",
                            description: "Admin dashboard for managing goal accountability applications and groups",
                            robots: "noindex, nofollow" // Keep admin pages private
                          }}>
                            <Suspense fallback={<LoadingSpinner />}>
                              <GoalAccountabilityAdmin />
                            </Suspense>
                          </SEORoute>
                        </ProtectedAdminRoute>
                      }
                    />    

                    <Route
                      path="/member-dashboard"
                      element={
                        <SEORoute seoConfig={{
                          title: "Member Dashboard | Goal Accountability Partners",
                          description: "Track your goal progress and connect with your accountability group",
                          robots: "noindex, nofollow", // Keep member dashboards private
                          canonical: "https://connectco.app/member-dashboard"
                        }}>
                          <Suspense fallback={<LoadingSpinner />}>
                            <MemberDashboard />
                          </Suspense>
                        </SEORoute>
                      }
                    />

                   
                    <Route
                      path="/member-login"
                      element={
                        <SEORoute seoConfig={{
                          title: "Member Login | Goal Accountability Partners",
                          description: "Access your goal accountability dashboard with secure email verification",
                          keywords: "member login, goal accountability, progress tracking, member dashboard",
                          canonical: "https://connectco.app/member-login"
                        }}>
                          <Suspense fallback={<LoadingSpinner />}>
                            <MemberLogin />
                          </Suspense>
                        </SEORoute>
                      }
                    />  

                      {/*
                    <Route
                      path="/admin/curated-fika"
                      element={
                        <SEORoute seoConfig={{
                          title: "Curated Fika Admin | Connect & Co",
                          description: "Admin dashboard for managing curated fika applications and premium intimate sessions",
                          robots: "noindex, nofollow" // Keep admin pages private
                        }}>
                          <Suspense fallback={<LoadingSpinner />}>
                            <CuratedFikaAdmin />
                          </Suspense>
                        </SEORoute>
                      }
                    />
                    */}
                    

                    <Route
                      path="/admin/curated-fika"
                      element={
                        <ProtectedAdminRoute>
                          <SEORoute seoConfig={{
                            title: "Curated Fika Admin | Connect & Co",
                            description: "Admin dashboard for managing curated fika applications and premium intimate sessions",
                            robots: "noindex, nofollow" // Keep admin pages private
                          }}>
                            <Suspense fallback={<LoadingSpinner />}>
                              <CuratedFikaAdmin />
                            </Suspense>
                          </SEORoute>
                        </ProtectedAdminRoute>
                      }
                    />

                    
                    
                    

                      {/*
                    <Route
                      path="/admin/curated-fika-entrepreneurs"
                      element={
                        <SEORoute seoConfig={{
                          title: "Founders 1-on-1 Admin | Connect & Co",
                          description: "Admin dashboard for managing entrepreneur 1-on-1 matching and founder sessions",
                          robots: "noindex, nofollow" // Keep admin pages private
                        }}>
                          <Suspense fallback={<LoadingSpinner />}>
                            <CuratedFikaEntrepreneursAdmin />
                          </Suspense>
                        </SEORoute>
                      }
                    />
                    */}

                    <Route
                      path="/admin/curated-fika-entrepreneurs"
                      element={
                        <ProtectedAdminRoute>
                          <SEORoute seoConfig={{
                            title: "Founders 1-on-1 Admin | Connect & Co",
                            description: "Admin dashboard for managing entrepreneur 1-on-1 matching and founder sessions",
                            robots: "noindex, nofollow"
                          }}>
                            <Suspense fallback={<LoadingSpinner />}>
                              <CuratedFikaEntrepreneursAdmin />
                            </Suspense>
                          </SEORoute>
                        </ProtectedAdminRoute>
                      }
                    />

                    <Route
                      path="/admin/deep-fika"
                      element={
                        <ProtectedAdminRoute>
                          <SEORoute seoConfig={{
                            title: "Deep Fika Admin | Connect & Co",
                            description: "Admin dashboard for managing Deep Fika 1-on-1 matched sessions",
                            robots: "noindex, nofollow"
                          }}>
                            <Suspense fallback={<LoadingSpinner />}>
                              <DeepFikaAdmin />
                            </Suspense>
                          </SEORoute>
                        </ProtectedAdminRoute>
                      }
                    />

                   
  <Route
    path="/merchandise"
    element={
      <SEORoute seoConfig={{
        title: "Premium Community Merchandise - Fika Culture & Authentic Connection | Connect & Co",
        description: "Discover premium merchandise celebrating Swedish fika culture and authentic community connections. Member discounts, free event delivery, and limited edition items for Connect & Co community.",
        keywords: "connect co merchandise, fika merchandise, community apparel, swedish coffee culture, premium merchandise, member discounts, authentic connection, community gear",
        url: "https://connectco.app/merchandise",
        canonical: "https://connectco.app/merchandise",
        openGraph: {
          title: "Premium Community Merchandise - Fika Culture & Authentic Connection",
          description: "Premium merchandise celebrating Swedish fika culture and authentic community connections. Member discounts and exclusive items.",
          url: "https://connectco.app/merchandise",
          image: "https://connectco.app/merchandise-og.jpg",
          type: "website"
        },
        twitter: {
          card: "summary_large_image",
          title: "Premium Community Merchandise - Fika Culture & Authentic Connection",
          description: "Premium merchandise celebrating Swedish fika culture and authentic community connections.",
          image: "https://connectco.app/merchandise-og.jpg"
        }
      }}>
        <Suspense fallback={<LoadingSpinner />}>
          <MerchandisePage />
        </Suspense>
      </SEORoute>
    }
  />


  <Route
    path="/checkin"
    element={
      <SEORoute seoConfig={{
        title: "Event Check-In - Connect & Co",
        description: "Check in to your Connect & Co event using QR code. Get instant access to event photos and community features.",
        keywords: "event checkin, qr code, connect co events, event attendance",
        url: "https://connectco.app/checkin",
        canonical: "https://connectco.app/checkin",
        openGraph: {
          title: "Event Check-In - Connect & Co",
          description: "Check in to your Connect & Co event using QR code",
          url: "https://connectco.app/checkin",
          image: "https://connectco.app/checkin-og.jpg",
          type: "website"
        },
        twitter: {
          card: "summary_large_image",
          title: "Event Check-In - Connect & Co",
          description: "Check in to your Connect & Co event using QR code",
          image: "https://connectco.app/checkin-og.jpg"
        }
      }}>
        <Suspense fallback={<LoadingSpinner />}>
          <QRCheckIn />
        </Suspense>
      </SEORoute>
    }
  />

<Route
  path="/happiness"
  element={
    <SEORoute seoConfig={{
      title: "How to Be Happy in Tokyo: Authentic Friendships & Fika | Connect & Co",
      description: "Discover how authentic friendships and curated community events create happiness. Join Connect & Co for Fika, curated connections, and growth circles in Tokyo.",
      keywords: "happiness tokyo, how to be happy, friendship happiness, authentic connection, fika tradition, community events tokyo, meaningful relationships",
      url: "https://connectco.app/happiness",
      canonical: "https://connectco.app/happiness",
      openGraph: {
        title: "How to Be Happy in Tokyo: Authentic Friendships & Fika",
        description: "Discover how authentic friendships and curated community events create happiness through Swedish Fika tradition and meaningful connections.",
        url: "https://connectco.app/happiness",
        image: "https://connectco.app/happiness-og.jpg",
        type: "website"
      },
      twitter: {
        card: "summary_large_image",
        title: "How to Be Happy in Tokyo: Authentic Friendships & Fika",
        description: "Discover how authentic friendships create happiness through Swedish Fika and meaningful connections.",
        image: "https://connectco.app/happiness-og.jpg"
      }
    }}>
      <Suspense fallback={<LoadingSpinner />}>
        <HappinessLandingPage />
      </Suspense>
    </SEORoute>
  }
/>

{/*
<Route
  path="/why-connectco"
  element={
    <SEORoute seoConfig={{
      title: "Why Connect & Co: Tokyo's Curated Friendship Community | Built Different",
      description: "Connect & Co is Tokyo's membership-based fika community for locals, expats, and digital nomads. See how we compare to Meetup, Eventbrite, Bumble BFF, and InterNations — and why curated intimacy wins.",
      keywords: "connect and co tokyo, fika community tokyo, meetup alternative tokyo, curated friendship events, expat community tokyo, small group social events, authentic connection tokyo, swedish fika tokyo",
      url: "https://connectco.app/why-connectco",
      canonical: "https://connectco.app/why-connectco",
      openGraph: {
        title: "Why Connect & Co: Tokyo's Curated Friendship Community",
        description: "No networking. No agenda. No strangers — just people who haven't met yet. See how Connect & Co compares to every other social platform in Tokyo.",
        url: "https://connectco.app/why-connectco",
        image: "https://connectco.app/why-connectco-og.jpg",
        type: "website"
      },
      twitter: {
        card: "summary_large_image",
        title: "Why Connect & Co: Tokyo's Curated Friendship Community",
        description: "No networking. No agenda. No strangers — just people who haven't met yet.",
        image: "https://connectco.app/why-connectco-og.jpg"
      }
    }}>
      <Suspense fallback={<LoadingSpinner />}>
        <PositioningPage />
      </Suspense>
    </SEORoute>
  }
/>
*/}

<Route
  path="/why-connectco"
  element={
    <SEORoute seoConfig={seoConfigs.whyConnectCo}>
      <Suspense fallback={<LoadingSpinner />}>
        <PositioningPage />
      </Suspense>
    </SEORoute>
  }
/>

<Route
  path="/partner-with-us"
  element={
    <SEORoute seoConfig={seoConfigs.partnerWithUs}>
      <Suspense fallback={<LoadingSpinner />}>
        <PartnerPage />
      </Suspense>
    </SEORoute>
  }
/>

<Route
  path="/nomad-concierge"
  element={
    <SEORoute seoConfig={{
      title: "Nomad Concierge - Live Like a Local, Connect Like a Member | Connect & Co",
      description: "Community-sourced café, bar, restaurant picks, co-working spots, cultural tips, and local favorites in Tokyo & Osaka. Not just reviews—places where real friendships begin.",
      keywords: "nomad concierge, tokyo cafes, osaka venues, digital nomad guide, expat guide japan, tokyo co-working, cultural tips japan, community recommendations",
      url: "https://connectco.app/nomad-concierge",
      canonical: "https://connectco.app/nomad-concierge",
      openGraph: {
        title: "Nomad Concierge - Live Like a Local, Connect Like a Member",
        description: "Community-sourced café picks, co-working spots, cultural tips, and local favorites in Tokyo & Osaka for building authentic friendships.",
        url: "https://connectco.app/nomad-concierge",
        image: "https://connectco.app/nomad-concierge-og.jpg",
        type: "website"
      },
      twitter: {
        card: "summary_large_image",
        title: "Nomad Concierge - Live Like a Local, Connect Like a Member",
        description: "Community-sourced local intelligence for building authentic friendships in Tokyo & Osaka.",
        image: "https://connectco.app/nomad-concierge-og.jpg"
      }
    }}>
      <Suspense fallback={<LoadingSpinner />}>
        <NomadConcierge />
      </Suspense>
    </SEORoute>
  }
/>

<Route
  path="/submit-discovery"
  element={
    <SEORoute seoConfig={{
      title: "Submit a Discovery - Share Hidden Gems | Connect & Co",
      description: "Found an amazing café, bar, restaurant, or hidden spot in Tokyo or Osaka? Share your discovery with our community and get featured in our Weekly Digest.",
      keywords: "submit discovery, tokyo hidden gems, osaka recommendations, community contributions, local spots tokyo, share recommendations, tokyo cafe recommendations, expat discoveries japan, nomad community tokyo",
      url: "https://connectco.app/submit-discovery",
      canonical: "https://connectco.app/submit-discovery",
      openGraph: {
        title: "Submit a Discovery - Share Your Hidden Gems",
        description: "Found something amazing in Tokyo or Osaka? Share it with our community and get featured in our Weekly Digest.",
        url: "https://connectco.app/submit-discovery",
        image: "https://connectco.app/submit-discovery-og.jpg",
        type: "website"
      },
      twitter: {
        card: "summary_large_image",
        title: "Submit a Discovery - Share Your Hidden Gems",
        description: "Found something amazing in Tokyo or Osaka? Share it with our community and get featured in our Weekly Digest.",
        image: "https://connectco.app/submit-discovery-og.jpg"
      }
    }}>
      <Suspense fallback={<LoadingSpinner />}>
        <SubmitDiscovery />
      </Suspense>
    </SEORoute>
  }
/>


<Route
    path="/curated-fika/member-portal"
    element={
      <SEORoute seoConfig={{
        title: "Member Portal | Curated Fika | Connect & Co",
        description: "Access your personalized Curated Fika member portal. View upcoming sessions matched to your interests and register for exclusive intimate Swedish coffee experiences.",
        keywords: "curated fika member portal, premium fika access, personalized sessions, matched networking, swedish fika members",
        url: "https://connectco.app/curated-fika/member-portal",
        canonical: "https://connectco.app/curated-fika/member-portal",
        robots: "noindex, follow",
        openGraph: {
          title: "Curated Fika Member Portal | Connect & Co",
          description: "Access your personalized member portal to view upcoming Curated Fika sessions matched to your interests and preferences.",
          url: "https://connectco.app/curated-fika/member-portal",
          image: "https://connectco.app/curated-fika-member-portal-og.jpg",
          type: "website"
        },
        twitter: {
          card: "summary_large_image",
          title: "Curated Fika Member Portal",
          description: "View personalized sessions and register for exclusive intimate Swedish coffee experiences.",
          image: "https://connectco.app/curated-fika-member-portal-og.jpg"
        }
      }}>
        <Suspense fallback={<LoadingSpinner />}>
          <CuratedFikaMemberPortal />
        </Suspense>
      </SEORoute>
    }
  />

<Route
  path="/curated-fika/my-sessions"
  element={
    <SEORoute seoConfig={{
      title: "My Curated Fika Sessions | Connect & Co",
      description: "View and manage your intimate Curated Fika coffee experiences in Tokyo and Osaka.",
      keywords: "my sessions, curated fika, manage bookings, tokyo coffee, osaka coffee meetups",
      url: "https://connectco.app/curated-fika/my-sessions",
      canonical: "https://connectco.app/curated-fika/my-sessions",
      openGraph: {
        title: "My Curated Fika Sessions",
        description: "View and manage your intimate coffee experiences",
        url: "https://connectco.app/curated-fika/my-sessions",
        type: "website"
      },
      twitter: {
        card: "summary",
        title: "My Curated Fika Sessions",
        description: "View and manage your intimate coffee experiences"
      }
    }}>
      <Suspense fallback={<LoadingSpinner />}>
        <MySessions />
      </Suspense>
    </SEORoute>
  }
/>


<Route
  path="/unsubscribe"
  element={
    <SEORoute seoConfig={{
      title: "Unsubscribe - Connect & Co",
      robots: "noindex, nofollow"
    }}>
      <Suspense fallback={<LoadingSpinner />}>
        <UnsubscribePage />
      </Suspense>
    </SEORoute>
  }
/>

                   {/*
                  <Route
                    path="/tokyo-expat-calculator"
                    element={
                      <SEORoute seoConfig={{
                        title: "Free Tokyo Expat Integration Calculator - Cost, Time & Loneliness Analysis | Connect & Co",
                        description: "Calculate how much time, money and loneliness you can save choosing authentic community over traditional networking. Free tool used by 2,800+ Tokyo expats.",
                        keywords: "tokyo expat calculator, expat integration cost, tokyo networking analysis, expat friendship timeline, tokyo social budget calculator",
                        url: "https://connectco.app/tokyo-expat-calculator",
                        canonical: "https://connectco.app/tokyo-expat-calculator"
                      }}>
                        <Suspense fallback={<LoadingSpinner />}>
                          <TokyoExpatCalculator />
                        </Suspense>
                      </SEORoute>
                    }
                  />
                  */}


                  <Route
                    path="/tokyo-expat-calculator"
                    element={
                      <SEORoute seoConfig={seoConfigs.tokyoExpatCalculator}>
                        <Suspense fallback={<LoadingSpinner />}>
                          <TokyoExpatCalculator />
                        </Suspense>
                      </SEORoute>
                    }
                  />
              
                  {/* Keep existing event details route */}
                  <Route
                    path="/events/:slug"
                    element={
                      <SEORoute>
                        <Suspense fallback={<LoadingSpinner />}>
                          <EventDetailsPage />
                        </Suspense>
                      </SEORoute>
                    }
                  />

                    {/* 🆕 NEW: Member Passport Route - ZERO impact on existing routes */}
                <Route
                  path="/member-passport"
                  element={
                    <SEORoute seoConfig={{
                      title: "My Member Passport - Track Your Community Journey | Connect & Co",
                      description: "View your event attendance history, tier progress, and achievements. Track your stamps and unlock new community benefits.",
                      keywords: "member passport, event history, community achievements, tier progress, event stamps",
                      url: "https://connectco.app/member-passport",
                      canonical: "https://connectco.app/member-passport",
                      robots: "noindex, nofollow", // Private member page
                      openGraph: {
                        title: "My Member Passport - Track Your Community Journey",
                        description: "View your event attendance history, tier progress, and achievements in the Connect & Co community.",
                        url: "https://connectco.app/member-passport",
                        image: "https://connectco.app/member-passport-og.jpg",
                        type: "website"
                      }
                    }}>
                      <Suspense fallback={<LoadingSpinner />}>
                        <MemberPassportPage />
                      </Suspense>
                    </SEORoute>
                  }
                />

                   {/* 
                <Route
                  path="/tokyo-cafes"
                  element={
                    <SEORoute seoConfig={{
                      title: "Tokyo's Best Third Wave Coffee Shops | Connect & Co",
                      description: "Discover 150+ exceptional specialty coffee shops in Tokyo curated by the Connect & Co community. Find third wave cafes by neighborhood with ratings, reviews, and locations.",
                      keywords: "tokyo coffee, third wave coffee tokyo, specialty coffee tokyo, best cafes tokyo, tokyo coffee shops, coffee roasters tokyo, specialty coffee guide, tokyo fika",
                      url: "https://connectco.app/tokyo-cafes",
                      canonical: "https://connectco.app/tokyo-cafes",
                      openGraph: {
                        title: "Tokyo's Best Third Wave Coffee Shops - Community Curated",
                        description: "Discover 150+ exceptional specialty coffee shops in Tokyo curated by the Connect & Co community.",
                        url: "https://connectco.app/tokyo-cafes",
                        image: "https://connectco.app/tokyo-cafes-og.jpg",
                        type: "website"
                      },
                      twitter: {
                        card: "summary_large_image",
                        title: "Tokyo's Best Third Wave Coffee Shops",
                        description: "150+ specialty coffee shops curated by the community",
                        image: "https://connectco.app/tokyo-cafes-og.jpg"
                      }
                    }}>
                      <Suspense fallback={<LoadingSpinner />}>
                        <TokyoCafesPage />
                      </Suspense>
                    </SEORoute>
                  }
                />
                */}

                {/* Tokyo - City-specific route */}
  <Route
    path="/coffee-guide/tokyo"
    element={
      <SEORoute seoConfig={{
        title: "Tokyo's Best Third Wave Coffee Shops | Specialty Coffee Guide",
        description: "Discover 150+ exceptional specialty coffee shops in Tokyo curated by the Connect & Co community. Find third wave cafes in Shibuya, Harajuku, Omotesando and beyond.",
        keywords: "tokyo coffee, third wave coffee tokyo, specialty coffee tokyo, best cafes tokyo, tokyo coffee shops, coffee roasters tokyo, shibuya coffee, harajuku coffee",
        url: "https://connectco.app/coffee-guide/tokyo",
        canonical: "https://connectco.app/coffee-guide/tokyo",
        openGraph: {
          title: "Tokyo's Best Third Wave Coffee Shops - 150+ Curated Cafes",
          description: "Discover Tokyo's exceptional specialty coffee scene curated by the Connect & Co community.",
          url: "https://connectco.app/coffee-guide/tokyo",
          image: "https://connectco.app/tokyo-coffee-og.jpg",
          type: "website"
        },
        twitter: {
          card: "summary_large_image",
          title: "Tokyo's Best Third Wave Coffee Shops",
          description: "150+ specialty coffee shops curated by the community",
          image: "https://connectco.app/tokyo-coffee-og.jpg"
        }
      }}>
        <Suspense fallback={<LoadingSpinner />}>
          <TokyoCafesPage defaultCity="tokyo" />
        </Suspense>
      </SEORoute>
    }
  />



  {/* Osaka - City-specific route */}
  <Route
    path="/coffee-guide/osaka"
    element={
      <SEORoute seoConfig={{
        title: "Osaka's Best Specialty Coffee Shops | Artisan Coffee Guide",
        description: "Discover exceptional specialty coffee shops in Osaka curated by the Connect & Co community. Find artisan cafes in Namba, Kitahama, and throughout the city.",
        keywords: "osaka coffee, specialty coffee osaka, best cafes osaka, osaka coffee shops, coffee roasters osaka, namba coffee, kitahama coffee",
        url: "https://connectco.app/coffee-guide/osaka",
        canonical: "https://connectco.app/coffee-guide/osaka",
        openGraph: {
          title: "Osaka's Best Specialty Coffee Shops - Curated Guide",
          description: "Discover Osaka's vibrant specialty coffee culture curated by the Connect & Co community.",
          url: "https://connectco.app/coffee-guide/osaka",
          image: "https://connectco.app/osaka-coffee-og.jpg",
          type: "website"
        },
        twitter: {
          card: "summary_large_image",
          title: "Osaka's Best Specialty Coffee Shops",
          description: "Curated artisan coffee experiences in Osaka",
          image: "https://connectco.app/osaka-coffee-og.jpg"
        }
      }}>
        <Suspense fallback={<LoadingSpinner />}>
          <TokyoCafesPage defaultCity="osaka" />
        </Suspense>
      </SEORoute>
    }
  />

  {/* Taipei - City-specific route */}
<Route
  path="/coffee-guide/taipei"
  element={
    <SEORoute seoConfig={{
      title: "Taipei's Best Third Wave Coffee & Traditional Tea Houses | Specialty Coffee Guide",
      description: "Discover 36+ exceptional specialty coffee shops and traditional tea houses in Taipei curated by the Connect & Co community. Visit world champion cafes, authentic gongfu cha tea ceremonies, and Taiwanese coffee roasters.",
      keywords: "taipei coffee, third wave coffee taipei, specialty coffee taipei, best cafes taipei, taipei coffee shops, coffee roasters taipei, taipei tea houses, gongfu cha taipei, taiwan coffee, da'an coffee, xinyi coffee, dihua street tea, traditional tea ceremony taipei, berg wu simple kaffa, world barista champion taipei, taiwanese coffee, taipei 101 coffee",
      url: "https://connectco.app/coffee-guide/taipei",
      canonical: "https://connectco.app/coffee-guide/taipei",
      openGraph: {
        title: "Taipei's Best Coffee & Tea - 36+ World Champion Cafes & Traditional Tea Houses",
        description: "Explore Taipei's exceptional specialty coffee scene and traditional tea culture. Home to world barista champions and authentic gongfu cha ceremonies.",
        url: "https://connectco.app/coffee-guide/taipei",
        image: "https://connectco.app/taipei-coffee-tea-og.jpg",
        type: "website"
      },
      twitter: {
        card: "summary_large_image",
        title: "Taipei's Best Coffee & Traditional Tea Houses",
        description: "36+ specialty cafes & tea houses | World champions | Authentic gongfu cha",
        image: "https://connectco.app/taipei-coffee-tea-og.jpg"
      }
    }}>
      <Suspense fallback={<LoadingSpinner />}>
        <TokyoCafesPage  defaultCity="taipei" />
      </Suspense>
    </SEORoute>
  }
/>

          {/* Manila - City-specific route */}
                <Route
                  path="/coffee-guide/manila"
                  element={
                    <SEORoute seoConfig={{
                      title: "Manila's Best Specialty Coffee Shops | Third Wave Coffee Guide",
                      description: "Discover 35+ world-class specialty coffee shops in Metro Manila curated by the Connect & Co community. Find award-winning cafes in BGC, Makati, Rockwell, and beyond.",
                      keywords: "manila coffee, specialty coffee manila, third wave coffee philippines, best cafes manila, bgc coffee shops, makati cafes, yardstick coffee, single origin manila, coffee roasters manila, bonifacio global city coffee",
                      url: "https://connectco.app/coffee-guide/manila",
                      canonical: "https://connectco.app/coffee-guide/manila",
                      openGraph: {
                        title: "Manila's Best Specialty Coffee Shops - 35+ Award-Winning Cafes",
                        description: "Discover Manila's world-class specialty coffee scene curated by the Connect & Co community. Featuring cafes ranked in World's 100 Best.",
                        url: "https://connectco.app/coffee-guide/manila",
                        image: "https://connectco.app/manila-coffee-og.jpg",
                        type: "website"
                      },
                      twitter: {
                        card: "summary_large_image",
                        title: "Manila's Best Specialty Coffee Shops",
                        description: "35+ world-class specialty coffee shops including award-winning cafes",
                        image: "https://connectco.app/manila-coffee-og.jpg"
                      }
                    }}>
                      <Suspense fallback={<LoadingSpinner />}>
                        <TokyoCafesPage defaultCity="manila" />
                      </Suspense>
                    </SEORoute>
                  }
                />

                {/* Bangkok - City-specific route ✨ NEW */}
                <Route
                  path="/coffee-guide/bangkok"
                  element={
                    <SEORoute seoConfig={{
                      title: "Bangkok's Best Coffee & Matcha Cafes | Specialty Coffee Guide",
                      description: "Discover 41+ exceptional third-wave coffee shops and ceremonial matcha cafes in Bangkok curated by the Connect & Co community. Find specialty cafes in Sukhumvit, Ari, Thonglor, and beyond.",
                      keywords: "bangkok coffee, specialty coffee bangkok, matcha bangkok, third wave coffee thailand, best cafes bangkok, sukhumvit coffee, ari cafes, thonglor coffee, rocket coffeebar, factory coffee bangkok, mtch matcha, peace oriental teahouse",
                      url: "https://connectco.app/coffee-guide/bangkok",
                      canonical: "https://connectco.app/coffee-guide/bangkok",
                      openGraph: {
                        title: "Bangkok's Best Coffee & Matcha Culture - 41+ Curated Cafes",
                        description: "Discover Bangkok's exceptional third-wave coffee and ceremonial matcha scene curated by the Connect & Co community.",
                        url: "https://connectco.app/coffee-guide/bangkok",
                        image: "https://connectco.app/bangkok-coffee-og.jpg",
                        type: "website"
                      },
                      twitter: {
                        card: "summary_large_image",
                        title: "Bangkok's Best Coffee & Matcha Cafes",
                        description: "41+ third-wave coffee shops and ceremonial matcha excellence",
                        image: "https://connectco.app/bangkok-coffee-og.jpg"
                      }
                    }}>
                      <Suspense fallback={<LoadingSpinner />}>
                        <TokyoCafesPage defaultCity="bangkok" />
                      </Suspense>
                    </SEORoute>
                  }
                />

                {/* New York City - City-specific route ✨ NEW */}
                <Route
                  path="/coffee-guide/nyc"
                  element={
                    <SEORoute seoConfig={{
                      title: "NYC's Best Specialty Coffee Shops | Third Wave Coffee Guide",
                      description: "Discover pioneering specialty coffee shops in New York City curated by the Connect & Co community. From Brooklyn's roasteries to Manhattan's third-wave pioneers.",
                      keywords: "nyc coffee, specialty coffee nyc, third wave coffee new york, best cafes manhattan brooklyn, sey coffee, la cabra nyc, blue bottle coffee, devocion williamsburg, abraco east village, third rail coffee, cafe grumpy greenpoint",
                      url: "https://connectco.app/coffee-guide/nyc",
                      canonical: "https://connectco.app/coffee-guide/nyc",
                      openGraph: {
                        title: "NYC's Best Specialty Coffee - 48+ Third-Wave Cafes",
                        description: "Discover New York City's pioneering third-wave coffee scene curated by the Connect & Co community.",
                        url: "https://connectco.app/coffee-guide/nyc",
                        image: "https://connectco.app/nyc-coffee-og.jpg",
                        type: "website"
                      },
                      twitter: {
                        card: "summary_large_image",
                        title: "NYC's Best Specialty Coffee Shops",
                        description: "48+ third-wave coffee pioneers across Manhattan & Brooklyn",
                        image: "https://connectco.app/nyc-coffee-og.jpg"
                      }
                    }}>
                      <Suspense fallback={<LoadingSpinner />}>
                        <TokyoCafesPage defaultCity="nyc" />
                      </Suspense>
                    </SEORoute>
                  }
                />

                {/* Melbourne - City-specific route ✨ NEW */}
                <Route
                  path="/coffee-guide/melbourne"
                  element={
                    <SEORoute seoConfig={{
                      title: "Melbourne's Best Specialty Coffee Shops | Third Wave Coffee Guide",
                      description: "Discover 43+ world-renowned specialty coffee shops in Melbourne curated by the Connect & Co community. From Collingwood's roasteries to CBD laneway gems and globally ranked cafes.",
                      keywords: "melbourne coffee, specialty coffee melbourne, third wave coffee melbourne, best cafes fitzroy collingwood, proud mary coffee, seven seeds, industry beans, st ali, patricia coffee brewers, market lane coffee, vacation coffee, ona coffee melbourne",
                      url: "https://connectco.app/coffee-guide/melbourne",
                      canonical: "https://connectco.app/coffee-guide/melbourne",
                      openGraph: {
                        title: "Melbourne's Best Specialty Coffee - 43+ Third-Wave Cafes",
                        description: "Discover Melbourne's world-renowned third-wave coffee scene curated by the Connect & Co community.",
                        url: "https://connectco.app/coffee-guide/melbourne",
                        image: "https://connectco.app/melbourne-coffee-og.jpg",
                        type: "website"
                      },
                      twitter: {
                        card: "summary_large_image",
                        title: "Melbourne's Best Specialty Coffee Shops",
                        description: "43+ world-class specialty roasters and laneway gems across Melbourne",
                        image: "https://connectco.app/melbourne-coffee-og.jpg"
                      }
                    }}>
                      <Suspense fallback={<LoadingSpinner />}>
                        <TokyoCafesPage defaultCity="melbourne" />
                      </Suspense>
                    </SEORoute>
                  }
                />

  {/* Lisbon - City-specific route */}
  <Route
    path="/coffee-guide/lisbon"
    element={
      <SEORoute seoConfig={{
        title: "Lisbon's Best Specialty Coffee Shops | Coffee Culture Guide",
        description: "Discover exceptional specialty coffee shops in Lisbon curated by the Connect & Co community. Find charming cafes in Baixa, Chiado, Santos and beyond.",
        keywords: "lisbon coffee, specialty coffee lisbon, best cafes lisbon, lisbon coffee shops, coffee roasters lisbon, baixa coffee, chiado coffee",
        url: "https://connectco.app/coffee-guide/lisbon",
        canonical: "https://connectco.app/coffee-guide/lisbon",
        openGraph: {
          title: "Lisbon's Best Specialty Coffee Shops - Curated Guide",
          description: "Discover Lisbon's charming specialty coffee scene curated by the Connect & Co community.",
          url: "https://connectco.app/coffee-guide/lisbon",
          image: "https://connectco.app/lisbon-coffee-og.jpg",
          type: "website"
        },
        twitter: {
          card: "summary_large_image",
          title: "Lisbon's Best Specialty Coffee Shops",
          description: "Curated coffee experiences in Lisbon",
          image: "https://connectco.app/lisbon-coffee-og.jpg"
        }
      }}>
        <Suspense fallback={<LoadingSpinner />}>
          <TokyoCafesPage defaultCity="lisbon" />
        </Suspense>
      </SEORoute>
    }
  />


   {/* Porto - City-specific route */}
  <Route
    path="/coffee-guide/porto"
    element={
      <SEORoute seoConfig={{
        title: "Porto's Best Specialty Coffee Shops | Coffee Culture Guide",
        description: "Discover exceptional specialty coffee shops in Porto curated by the Connect & Co community. Find charming cafes in Baixa, Chiado, Santos and beyond.",
        keywords: "Porto coffee, fika, specialty coffee lisbon, best cafes Porto, Porto coffee shops, coffee roasters lisbon, baixa coffee, chiado coffee",
        url: "https://connectco.app/coffee-guide/porto",
        canonical: "https://connectco.app/coffee-guide/porto",
        openGraph: {
          title: "Porto's Best Specialty Coffee Shops - Curated Guide",
          description: "Discover Porto's charming specialty coffee scene curated by the Connect & Co community.",
          url: "https://connectco.app/coffee-guide/porto",
          image: "https://connectco.app/porto-coffee-og.jpg",
          type: "website"
        },
        twitter: {
          card: "summary_large_image",
          title: "Porto's Best Specialty Coffee Shops",
          description: "Curated coffee experiences in Porto",
          image: "https://connectco.app/porto-coffee-og.jpg"
        }
      }}>
        <Suspense fallback={<LoadingSpinner />}>
          <TokyoCafesPage defaultCity="porto" />
        </Suspense>
      </SEORoute>
    }
  />

  {/* Main coffee guide page - defaults to Tokyo or shows city selector */}
  <Route
    path="/coffee-guide"
    element={
      <SEORoute seoConfig={{
        title: "Specialty Coffee Guide | Tokyo, Osaka, Lisbon, Porto | Connect & Co",
        description: "Discover 150+ exceptional specialty coffee shops across Tokyo, Osaka, Lisbon, and Porto curated by the Connect & Co community. Your guide to third wave coffee culture.",
        keywords: "specialty coffee guide, global coffee guide, coffee guide, third wave coffee, best cafes, coffee culture, tokyo coffee, osaka coffee, lisbon coffee, coffee roasters",
        url: "https://connectco.app/coffee-guide",
        canonical: "https://connectco.app/coffee-guide",
        openGraph: {
          title: "Specialty Coffee Guide - Tokyo, Osaka, Lisbon | Connect & Co",
          description: "Explore exceptional specialty coffee shops curated by our global community.",
          url: "https://connectco.app/coffee-guide",
          image: "https://connectco.app/coffee-guide-og.jpg",
          type: "website"
        },
        twitter: {
          card: "summary_large_image",
          title: "Specialty Coffee Guide | Connect & Co",
          description: "Curated coffee experiences worldwide",
          image: "https://connectco.app/coffee-guide-og.jpg"
        }
      }}>
        <Suspense fallback={<LoadingSpinner />}>
          <TokyoCafesPage />
        </Suspense>
      </SEORoute>
    }
  />

  
<Route
  path="/admin/moderation"
  element={
    <ProtectedAdminRoute>
      <SEORoute seoConfig={{
        title: "Response Moderation | Admin | Connect & Co",
        robots: "noindex, nofollow"
      }}>
        <Suspense fallback={<LoadingSpinner />}>
          <ResponseModerationDashboard />
        </Suspense>
      </SEORoute>
    </ProtectedAdminRoute>
  }
/>




  {/* Legacy redirect - keeps old bookmarks/links working */}
  <Route
    path="/tokyo-cafes"
    element={<Navigate to="/coffee-guide/tokyo" replace />}
  />

  <Route
  path="/photos"
  element={
    <SEORoute seoConfig={{
      title: "Community Photo Gallery - Authentic Moments & Connections | Connect & Co",
      description: "Browse 247+ photos from our community events. Real moments from Monthly Fika, Signature Fika, Curated Fika, and cultural experiences across Tokyo.",
      keywords: "connect co photos, community photos, tokyo events photos, fika photos, event gallery, community memories, tokyo community, authentic connections",
      url: "https://connectco.app/photos",
      canonical: "https://connectco.app/photos",
      robots: "index, follow",
      openGraph: {
        title: "Community Photo Gallery - Authentic Moments & Connections",
        description: "Browse 247+ photos from our community events capturing real moments and authentic connections.",
        url: "https://connectco.app/photos",
        image: "https://connectco.app/photos-og.jpg",
        type: "website"
      },
      twitter: {
        card: "summary_large_image",
        title: "Community Photo Gallery - Connect & Co",
        description: "Real moments from our community events across Tokyo",
        image: "https://connectco.app/photos-og.jpg"
      }
    }}>
      <Suspense fallback={<LoadingSpinner />}>
        <PhotosPage />
      </Suspense>
    </SEORoute>
  }
/>

<Route
  path="/stories/why-were-charging"
  element={
    <SEORoute seoConfig={{
      title: "Why We're Charging | Connect & Co",
      description: "January 2026 marks a new chapter for Connect & Co. Here's the honest story behind our transition to a membership-based community.",
      keywords: "Connect & Co, membership, fika, community, Tokyo, subscription"
    }}>
      <Suspense fallback={<LoadingSpinner />}>
        <WhyWereChargingPage />
      </Suspense>
    </SEORoute>
  }
/>

    {/* ── Fika Practice ─────────────────────────────────── */}
      <Route
        path="/fika-practice/onboarding"
        element={
          <SEORoute seoConfig={{
            title: "Start Your Fika Practice | Connect & Co",
            description: "A 5-minute assessment that builds your personal friendship skill path. Inspired by Fika philosophy. Works anywhere in the world.",
            keywords: "fika practice, friendship skills, daily habit, connection practice, swedish fika, friendship building",
            url: "https://connectco.app/fika-practice/onboarding",
            canonical: "https://connectco.app/fika-practice/onboarding",
            robots: "index, follow",
            openGraph: {
              title: "Start Your Fika Practice | Connect & Co",
              description: "5 minutes a day. One small lesson. Real friendships — built anywhere in the world.",
              url: "https://connectco.app/fika-practice/onboarding",
              image: "https://connectco.app/fika-practice-og.jpg",
              type: "website"
            }
          }}>
            <Suspense fallback={<LoadingSpinner />}>
              <FikaPracticeOnboarding />
            </Suspense>
          </SEORoute>
        }
      />

    <Route
      path="/fika-practice/today"
      element={
        <SEORoute seoConfig={{
          title: "Today's Fika Practice | Connect & Co",
          description: "Your daily 5-minute friendship skill lesson. Build your streak and deepen real connections — one small practice at a time.",
          keywords: "daily fika practice, friendship lesson, connection habit, streak, daily habit",
          url: "https://connectco.app/fika-practice/today",
          canonical: "https://connectco.app/fika-practice/today",
          robots: "noindex, follow"
        }}>
          <Suspense fallback={<LoadingSpinner />}>
            <FikaPracticeToday />
          </Suspense>
        </SEORoute>
      }
    />


    <Route
      path="/fika-practice/progress"
      element={
        <SEORoute seoConfig={{
          title: "Your Fika Practice Progress | Connect & Co",
          description: "Track your streak, completed lessons, and earned stamps. Your friendship skill journey at a glance.",
          keywords: "fika progress, streak, lessons completed, practice stamps, achievement",
          url: "https://connectco.app/fika-practice/progress",
          canonical: "https://connectco.app/fika-practice/progress",
          robots: "noindex, follow"
        }}>
          <Suspense fallback={<LoadingSpinner />}>
            <FikaPracticeProgress />
          </Suspense>
        </SEORoute>
      }
    />

    <Route
      path="/fika-practice/courses"
      element={
        <SEORoute seoConfig={{
          title: "Fika Practice Course Library | Connect & Co",
          description: "Explore all friendship skill courses — from Fika Philosophy to Showing Up in a New City. Your complete learning path.",
          keywords: "fika courses, friendship skills courses, course library, learning path, fika philosophy",
          url: "https://connectco.app/fika-practice/courses",
          canonical: "https://connectco.app/fika-practice/courses",
          robots: "index, follow",
          openGraph: {
            title: "Fika Practice Course Library | Connect & Co",
            description: "Friendship skill courses for anywhere in the world. From Fika Philosophy to making friends in a new city.",
            url: "https://connectco.app/fika-practice/courses",
            image: "https://connectco.app/fika-practice-courses-og.jpg",
            type: "website"
          }
        }}>
          <Suspense fallback={<LoadingSpinner />}>
            <FikaPracticeCourses />
          </Suspense>
        </SEORoute>
      }
    />


                    {/* 🚨 NEW: Catch-all 404 route - MUST BE LAST */}
                    <Route
                    path="*"
                    element={
                      <SEORoute seoConfig={{
                        title: "404 - Page Not Found | Connect & Co",
                        description: "The page you're looking for doesn't exist. Explore our community events, stories, and resources instead.",
                        keywords: "404, page not found, connect co",
                        url: `https://connectco.app${window.location.pathname}`,
                        canonical: "https://connectco.app",
                        robots: "noindex, nofollow", // Don't index 404 pages
                        openGraph: {
                          title: "404 - Page Not Found | Connect & Co",
                          description: "The page you're looking for doesn't exist. Explore our community instead.",
                          url: "https://connectco.app",
                          image: "https://connectco.app/og-image.jpg",
                          type: "website"
                        }
                      }}>
                        <NotFound />
                      </SEORoute>
                    }
                  />



                </Routes>
          
               
              </AnalyticsWrapper>
            </ScrollToTop>
            
            <Footer onNavigate={handleNavigation} onBlogNavigate={handleBlogNavigation} />
          </div>
          
          {/* 🆕 ADD: PWA components at the end, inside Router but outside main div */}
          {/* 
          <InstallButton />
          <NotificationPrompt />
          */}

      {/*  PWA components at the end, inside Router but outside main div */}
       {/*  Daily Prompt questions */}
         {/* 
        <NotificationPrompt />

        {promptModalOpen && currentPromptId && (
          <DailyPromptModal
            promptId={currentPromptId}
            onClose={() => {
              setPromptModalOpen(false);
              setCurrentPromptId(null);
            }}
          />
        )}
         */}
        
                 

        </Router>
  
    </ErrorBoundary>
  );
}

export default App;